import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from "react";
import RetencionPreview from "../components/RetencionPreview";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { useSSE } from "../hooks/useSSE";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, ArrowLeftRight, Settings, Plus, Trash2, Edit2,
  Check, X, Save, Eye, EyeOff, Users, ChevronRight, ChevronLeft, Filter,
  Calendar, Search, RefreshCcw, AlertTriangle, Info,
  Building2, CreditCard, TrendingUp, TrendingDown, Repeat2,
  History, Shield, CircleDot, Printer, FileText, Star,
  DollarSign, RefreshCw, PenLine, ChevronDown, ChevronUp,
  CheckCircle, Clock, AlertCircle, Wallet, Banknote, Receipt, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import MoneyInput from "../components/ui/MoneyInput";
import { AuthContext } from "../App";
import { toast } from "sonner";
import ChequesPage from "./ChequesPage";
import GarantiasPage from "./GarantiasPage";
import ChequeEgresoForm from "../components/ChequeEgresoForm";
import ProcesadorasCobroPanel from "../components/ProcesadorasCobroPanel";
import { AuditoriaRegistroBtn } from "../components/AuditoriaModal";
import { svgDocumentHeaderLogoHtml } from "../lib/marcaLogoSvg";
import { LogoMarcaArandu, LogoMarcaJar, LogoMarcaAranduJar } from "../components/MarcaLogos";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtNum = (n, moneda = "PYG") => {
  if (n === undefined || n === null) return "—";
  const num = Number(n);
  if (moneda === "USD") return `USD ${num.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (moneda === "BRL") return `R$ ${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `₲ ${Math.round(num).toLocaleString("es-PY")}`;
};

const onlyDigits = (value) => String(value || "").replace(/\D/g, "");
const fmtInputPYG = (value) => {
  const digits = onlyDigits(value);
  return digits ? Number(digits).toLocaleString("es-PY") : "";
};
const parseInputPYG = (value) => onlyDigits(value);

const hoy = () => new Date().toISOString().slice(0, 10);

// Helper chip-search (copiado de EgresosPage para uso en tab Pagos Proveedores)
function ChipSearch({ chips, setChips, inputVal, setInputVal, placeholder, accentColor = "orange", actionButton = null }) {
  const addChip = () => {
    const term = inputVal.trim().replace(/,$/, "");
    if (term && !chips.includes(term)) setChips(prev => [...prev, term]);
    setInputVal("");
  };
  return (
    <div className="space-y-2 flex-1">
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          {chips.map((chip, idx) => (
            <span key={idx} className={`flex items-center gap-1.5 bg-${accentColor}-500/20 text-${accentColor}-300 border border-${accentColor}-500/40 rounded-full px-3 py-1 text-xs`}>
              <Search className="w-3 h-3" />
              {chip}
              <button onClick={() => setChips(prev => prev.filter((_, i) => i !== idx))} className="ml-1 hover:text-white transition-colors">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button onClick={() => setChips([])} className="text-xs text-slate-500 hover:text-slate-300 px-2 py-1 rounded-full border border-white/10 hover:border-white/20 transition-all">
            Limpiar
          </button>
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            onKeyDown={e => {
              if ((e.key === "Enter" || e.key === ",") && inputVal.trim()) { e.preventDefault(); addChip(); }
              if (e.key === "Backspace" && inputVal === "" && chips.length > 0) setChips(prev => prev.slice(0, -1));
            }}
            placeholder={placeholder}
            className={`w-full bg-arandu-dark-light border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-white text-sm focus:outline-none focus:border-${accentColor}-500/60 placeholder-slate-500`}
          />
        </div>
        {actionButton}
      </div>
    </div>
  );
}
// ─── Buscador de entidades para préstamos (sin creación inline) ───────────────
function EntidadSearchSelect({ entidades, value, onChange, placeholder = "Buscar..." }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return entidades.slice(0, 12);
    const q = query.toLowerCase();
    return entidades.filter(e =>
      (e.nombre || "").toLowerCase().includes(q) ||
      (e.apellido || "").toLowerCase().includes(q) ||
      (e.ruc || "").toLowerCase().includes(q)
    ).slice(0, 12);
  }, [entidades, query]);

  const getLabel = (e) => e.nombre + (e.apellido ? " " + e.apellido : "");

  const handleSelect = (entidad) => {
    onChange({ id: entidad.id, nombre: getLabel(entidad), ruc: entidad.ruc || "" });
    setQuery(""); setOpen(false);
  };

  if (value?.id) {
    return (
      <div className="flex items-center gap-2 bg-arandu-dark border border-arandu-blue/30 rounded-lg px-3 py-2 min-h-[38px]">
        <div className="flex-1 min-w-0">
          <span className="text-white text-sm font-medium truncate block">{value.nombre}</span>
          {value.ruc && <span className="text-slate-400 text-xs">RUC: {value.ruc}</span>}
        </div>
        <button type="button" onClick={() => onChange(null)} className="text-slate-500 hover:text-white shrink-0 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full bg-arandu-dark border border-white/10 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue/50 placeholder:text-slate-500"
        />
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-arandu-dark-light border border-white/10 rounded-lg shadow-xl overflow-hidden">
          {filtered.length > 0 ? (
            <ul className="max-h-48 overflow-y-auto divide-y divide-white/5">
              {filtered.map(e => (
                <li key={e.id} onMouseDown={() => handleSelect(e)}
                  className="px-3 py-2.5 hover:bg-white/5 cursor-pointer flex items-center justify-between">
                  <span className="text-white text-sm">{getLabel(e)}</span>
                  {e.ruc && <span className="text-slate-500 text-xs ml-2 shrink-0">RUC: {e.ruc}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-3 py-2.5 text-slate-500 text-xs">
              {query.trim() ? `Sin resultados para "${query.trim()}"` : "Sin entidades disponibles"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function matchChips(chips, inputVal, texto) {
  const allChips = [...chips, ...(inputVal.trim() ? [inputVal.trim()] : [])];
  if (allChips.length === 0) return true;
  const t = texto.toLowerCase();
  return allChips.every(chip => t.includes(chip.toLowerCase()));
}
function mesLabel(m) {
  if (!m) return "";
  const [y, mo] = m.split("-");
  const meses = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  return `${meses[parseInt(mo,10)-1] || mo} ${y}`;
}

const hoyFin = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const prevDiaFin = (d) => { const dt = new Date(d + "T12:00:00"); dt.setDate(dt.getDate() - 1); return dt.toISOString().slice(0, 10); };
const nextDiaFin = (d) => { const dt = new Date(d + "T12:00:00"); dt.setDate(dt.getDate() + 1); return dt.toISOString().slice(0, 10); };
const diaLabelFin = (d) => { if (!d) return ""; return new Date(d + "T12:00:00").toLocaleDateString("es-PY", { weekday: "short", day: "numeric", month: "short" }); };

// ── Semáforo de saldo ─────────────────────────────────────────────────────────
function Semaforo({ saldo, cuenta, showSaldo }) {
  const umbralRojo     = cuenta?.umbral_rojo     ?? 0;
  const umbralAmarillo = cuenta?.umbral_amarillo ?? 0;
  let color, pulse, label;
  if (saldo <= umbralRojo) {
    color = "bg-red-500";    pulse = "shadow-red-500/60";    label = "Saldo bajo";
  } else if (umbralAmarillo > 0 && saldo <= umbralAmarillo) {
    color = "bg-amber-400";  pulse = "shadow-amber-400/60";  label = "Saldo medio";
  } else {
    color = "bg-emerald-400"; pulse = "shadow-emerald-400/60"; label = "Saldo OK";
  }
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-2.5 h-2.5 rounded-full ${color} shadow-[0_0_6px_2px] ${pulse}`}
        title={label}
      />
      {showSaldo && (
        <span className={`text-sm font-mono font-semibold ${
          saldo <= umbralRojo ? "text-red-400" :
          umbralAmarillo > 0 && saldo <= umbralAmarillo ? "text-amber-400" : "text-emerald-400"
        }`}>
          {fmtNum(saldo, cuenta?.moneda)}
        </span>
      )}
    </div>
  );
}

// ── Cuenta Card ───────────────────────────────────────────────────────────────
function CuentaCard({ cuenta, saldo_actual, canVerSaldo, onClick }) {
  const saldo = saldo_actual ?? 0;
  return (
    <div
      className="border border-white/10 rounded-xl p-4 bg-white/3 cursor-pointer hover:bg-white/5 transition-all group"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-white text-sm font-semibold truncate max-w-[140px]">{cuenta.nombre}</p>
          <p className="text-slate-500 text-xs">{cuenta.banco || "—"}</p>
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold border ${
          cuenta.moneda === "USD"
            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
            : "border-arandu-blue/30 bg-arandu-blue/10 text-arandu-blue"
        }`}>
          {cuenta.moneda || "PYG"}
        </span>
      </div>
      <Semaforo saldo={saldo} cuenta={cuenta} showSaldo={canVerSaldo} />
      {!canVerSaldo && (
        <p className="text-slate-600 text-[10px] mt-1 flex items-center gap-1">
          <EyeOff className="w-3 h-3" /> Saldo restringido
        </p>
      )}
    </div>
  );
}

// ── Modal de transferencia ────────────────────────────────────────────────────
function TransferenciaModal({ cuentas, saldos, onClose, onSave, editingDoc, canVerSaldoFn, cotHoy = {}, canEditarTC = true }) {
  const [step, setStep]             = useState(1); // 1=form 2=confirm
  const [deCuentaId, setDeCuentaId] = useState(editingDoc?.de_cuenta_id || "");
  const [aCuentaId, setACuentaId]   = useState(editingDoc?.a_cuenta_id  || "");
  const [monto, setMonto]           = useState(editingDoc?.monto        || "");
  const [tipoCambio, setTipoCambio] = useState(editingDoc?.tipo_cambio  || "");
  const [montoDestino, setMontoDestino] = useState(editingDoc?.monto_destino || "");
  const [descripcion, setDescripcion]   = useState(editingDoc?.descripcion   || "");
  const [fecha, setFecha]               = useState(editingDoc?.fecha         || hoy());
  const [loading, setLoading]           = useState(false);

  const deCuenta = cuentas.find(c => c.id === deCuentaId);
  const aCuenta  = cuentas.find(c => c.id === aCuentaId);
  const saldoDe  = saldos[deCuentaId] ?? 0;

  const esCrossCurrency = deCuenta && aCuenta && deCuenta.moneda !== aCuenta.moneda;

  // Determinar la moneda extranjera involucrada (USD o BRL)
  const monedaExtranjera = deCuenta && aCuenta
    ? (deCuenta.moneda !== "PYG" ? deCuenta.moneda : aCuenta.moneda !== "PYG" ? aCuenta.moneda : null)
    : null;

  // Label del tipo de cambio
  const tcLabel = deCuenta && aCuenta
    ? `${deCuenta.moneda}→${aCuenta.moneda === "PYG" ? "₲" : aCuenta.moneda}`
    : "Tipo de cambio";

  // Auto-fill TC desde cotHoy cuando cambia el par de cuentas
  useEffect(() => {
    if (!esCrossCurrency || !monedaExtranjera) return;
    if (editingDoc?.tipo_cambio) return; // en modo edición no sobreescribir
    const cotMoneda = cotHoy[monedaExtranjera] || cotHoy[monedaExtranjera?.toLowerCase()];
    if (cotMoneda) {
      const tc = cotMoneda.tc_empresa || cotMoneda.venta || cotMoneda.compra;
      if (tc && tc > 0) setTipoCambio(String(tc));
    }
  }, [deCuentaId, aCuentaId, esCrossCurrency, monedaExtranjera]); // eslint-disable-line

  // Auto-calcular monto destino
  useEffect(() => {
    if (!esCrossCurrency || !monto || !tipoCambio) return;
    const m = parseFloat(monto), tc = parseFloat(tipoCambio);
    if (isNaN(m) || isNaN(tc) || tc <= 0) return;
    let dest;
    const deMon = deCuenta?.moneda;
    const aMon  = aCuenta?.moneda;
    if (deMon !== "PYG" && aMon === "PYG") {
      // USD/BRL → PYG: multiplicar
      dest = m * tc;
    } else if (deMon === "PYG" && aMon !== "PYG") {
      // PYG → USD/BRL: dividir
      dest = m / tc;
    } else {
      // Cruce entre extranjeras (ej. USD↔BRL): dividir como aproximación
      dest = m / tc;
    }
    setMontoDestino(dest.toFixed(aMon === "PYG" ? 0 : 2));
  }, [monto, tipoCambio, esCrossCurrency, deCuenta, aCuenta]);

  const montoNum  = parseFloat(monto)  || 0;
  const saldoPost = saldoDe - montoNum;
  const permiteSaldoNegativo = !!deCuenta?.permite_saldo_negativo;
  const quedariaNegativo = montoNum > 0 && saldoPost < 0;
  // Solo se bloquea si la cuenta origen NO tiene habilitado el saldo negativo.
  const saldoInsuf = quedariaNegativo && !permiteSaldoNegativo;
  const saldoNegativoPermitido = quedariaNegativo && permiteSaldoNegativo;

  function handleContinue() {
    if (!deCuentaId || !aCuentaId) { toast.error("Seleccioná ambas cuentas"); return; }
    if (deCuentaId === aCuentaId)  { toast.error("Las cuentas deben ser distintas"); return; }
    if (montoNum <= 0)             { toast.error("El monto debe ser mayor a cero"); return; }
    if (saldoInsuf)                { toast.error("Saldo insuficiente en la cuenta de origen"); return; }
    if (esCrossCurrency && (!tipoCambio || parseFloat(tipoCambio) <= 0)) {
      toast.error("Ingresá el tipo de cambio"); return;
    }
    if (!fecha) { toast.error("La fecha es obligatoria"); return; }
    setStep(2);
  }

  async function handleConfirm() {
    setLoading(true);
    try {
      await onSave({
        de_cuenta_id: deCuentaId,
        a_cuenta_id:  aCuentaId,
        monto:         montoNum,
        monto_destino: esCrossCurrency ? parseFloat(montoDestino) : montoNum,
        tipo_cambio:   esCrossCurrency ? parseFloat(tipoCambio) : null,
        descripcion,
        fecha,
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-arandu-dark border border-white/10 rounded-2xl w-full max-w-md shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/8">
          <div className="flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-arandu-blue" />
            <h2 className="text-white font-semibold">
              {step === 1 ? "Nueva Transferencia" : "Confirmar Transferencia"}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {step === 1 ? (
            <>
              {/* De cuenta */}
              <div>
                <label className="text-slate-400 text-xs font-medium mb-1.5 block">Cuenta origen</label>
                <select
                  value={deCuentaId}
                  onChange={e => { setDeCuentaId(e.target.value); setACuentaId(""); }}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-arandu-blue/50"
                >
                  <option value="">— Seleccioná cuenta —</option>
                  {cuentas.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.nombre} ({c.moneda}){canVerSaldoFn && canVerSaldoFn(c.id) ? ` — Saldo: ${fmtNum(saldos[c.id] ?? 0, c.moneda)}` : ""}
                    </option>
                  ))}
                </select>
                {deCuenta && canVerSaldoFn && canVerSaldoFn(deCuentaId) && (
                  <p className="text-xs mt-1 text-slate-500">
                    Saldo disponible: <span className={saldoInsuf ? "text-red-400 font-semibold" : saldoNegativoPermitido ? "text-amber-400 font-semibold" : "text-emerald-400"}>
                      {fmtNum(saldoDe, deCuenta.moneda)}
                    </span>
                    {permiteSaldoNegativo && (
                      <span className="ml-1.5 text-[10px] text-amber-500/80">(saldo negativo permitido)</span>
                    )}
                  </p>
                )}
              </div>

              {/* A cuenta */}
              <div>
                <label className="text-slate-400 text-xs font-medium mb-1.5 block">Cuenta destino</label>
                <select
                  value={aCuentaId}
                  onChange={e => setACuentaId(e.target.value)}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-arandu-blue/50"
                  disabled={!deCuentaId}
                >
                  <option value="">— Seleccioná cuenta —</option>
                  {cuentas.filter(c => c.id !== deCuentaId).map(c => (
                    <option key={c.id} value={c.id}>
                      {c.nombre} ({c.moneda})
                    </option>
                  ))}
                </select>
              </div>

              {/* Monto */}
              <div>
                <label className="text-slate-400 text-xs font-medium mb-1.5 block">
                  Monto {deCuenta ? `(${deCuenta.moneda})` : ""}
                </label>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={monto}
                  onChange={e => {
                    const v = e.target.value;
                    if (v !== "" && parseFloat(v) < 0) setMonto("0");
                    else setMonto(v);
                  }}
                  placeholder="0"
                  className={`bg-arandu-dark border-white/10 text-white ${saldoInsuf ? "border-red-500/50" : saldoNegativoPermitido ? "border-amber-500/50" : ""}`}
                />
                {saldoInsuf && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Saldo insuficiente
                  </p>
                )}
                {saldoNegativoPermitido && (
                  <p className="text-amber-400 text-xs mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Esta cuenta quedará en saldo negativo (permitido)
                  </p>
                )}
              </div>

              {/* Tipo de cambio (solo cross-currency) */}
              {esCrossCurrency && (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 space-y-3">
                  <p className="text-amber-400 text-xs font-medium flex items-center gap-1">
                    <Info className="w-3.5 h-3.5" />
                    Cuentas en monedas distintas — {deCuenta.moneda} → {aCuenta.moneda}
                  </p>
                  <div>
                    <label className="text-slate-400 text-xs font-medium mb-1.5 block">
                      Tipo de cambio ({tcLabel})
                      {!canEditarTC && <span className="ml-2 text-slate-500 text-[10px]">— de cotizaciones</span>}
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      value={tipoCambio}
                      onChange={e => canEditarTC && setTipoCambio(e.target.value)}
                      readOnly={!canEditarTC}
                      placeholder="ej: 7800"
                      className={`bg-arandu-dark border-white/10 text-white ${!canEditarTC ? "opacity-70 cursor-not-allowed" : ""}`}
                    />
                  </div>
                  {montoDestino && parseFloat(montoDestino) > 0 && (
                    <p className="text-emerald-400 text-xs">
                      Monto a acreditar: <strong>{fmtNum(parseFloat(montoDestino), aCuenta.moneda)}</strong>
                    </p>
                  )}
                </div>
              )}

              {/* Descripción */}
              <div>
                <label className="text-slate-400 text-xs font-medium mb-1.5 block">Descripción / concepto</label>
                <Input
                  value={descripcion}
                  onChange={e => setDescripcion(e.target.value)}
                  placeholder="Ej: Fondeo para operaciones, pago anticipado..."
                  className="bg-arandu-dark border-white/10 text-white"
                />
              </div>

              {/* Fecha */}
              <div>
                <label className="text-slate-400 text-xs font-medium mb-1.5 block">Fecha</label>
                <DateInput
                  value={fecha}
                  onChange={e => setFecha(e.target.value)}
                  className="bg-arandu-dark border-white/10 text-white"
                />
              </div>
            </>
          ) : (
            /* Paso 2: Confirmación */
            <div className="space-y-4">
              <div className="bg-white/3 border border-white/10 rounded-xl p-4 space-y-3">
                {/* De → A */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 text-center">
                    <p className="text-slate-400 text-[10px] uppercase tracking-wide mb-1">Origen</p>
                    <p className="text-white font-semibold text-sm">{deCuenta?.nombre}</p>
                    <p className="text-slate-500 text-xs">{deCuenta?.moneda}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-arandu-blue flex-shrink-0" />
                  <div className="flex-1 text-center">
                    <p className="text-slate-400 text-[10px] uppercase tracking-wide mb-1">Destino</p>
                    <p className="text-white font-semibold text-sm">{aCuenta?.nombre}</p>
                    <p className="text-slate-500 text-xs">{aCuenta?.moneda}</p>
                  </div>
                </div>

                <div className="border-t border-white/8 pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Monto a debitar</span>
                    <span className="text-white font-semibold">{fmtNum(montoNum, deCuenta?.moneda)}</span>
                  </div>
                  {esCrossCurrency && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Monto a acreditar</span>
                      <span className="text-emerald-400 font-semibold">{fmtNum(parseFloat(montoDestino), aCuenta?.moneda)}</span>
                    </div>
                  )}
                  {esCrossCurrency && (
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Tipo de cambio</span>
                      <span>{parseFloat(tipoCambio).toLocaleString("es-PY")}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm border-t border-white/8 pt-2">
                    <span className="text-slate-400">Saldo {deCuenta?.nombre} después</span>
                    <span className={saldoPost < 0 ? (permiteSaldoNegativo ? "text-amber-400 font-bold" : "text-red-400 font-bold") : "text-white font-semibold"}>
                      {fmtNum(saldoPost, deCuenta?.moneda)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>Fecha</span>
                    <span>{fecha}</span>
                  </div>
                  {descripcion && (
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Descripción</span>
                      <span className="text-right max-w-[180px] truncate">{descripcion}</span>
                    </div>
                  )}
                </div>
              </div>
              {saldoPost < 0 && (
                permiteSaldoNegativo ? (
                  <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <p className="text-amber-300 text-xs">El saldo de la cuenta origen quedará negativo. Esta cuenta tiene habilitado el saldo negativo, por lo que la transferencia puede continuar.</p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    <p className="text-red-300 text-xs">El saldo de la cuenta origen quedaría negativo. La transferencia podría ser rechazada.</p>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t border-white/8">
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={onClose} className="flex-1 border-white/10 text-slate-400">
                Cancelar
              </Button>
              <Button onClick={handleContinue} className="flex-1 bg-arandu-blue hover:bg-arandu-blue-dark text-white">
                Continuar <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 border-white/10 text-slate-400">
                <ArrowLeft className="w-4 h-4 mr-1" /> Volver
              </Button>
              <Button onClick={handleConfirm} disabled={loading} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">
                {loading ? "Procesando..." : <><Check className="w-4 h-4 mr-1" /> Confirmar</>}
              </Button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ── Modal de edición (solo descripción y fecha) ───────────────────────────────
function EditarTransferenciaModal({ doc, onClose, onSave }) {
  const [descripcion, setDescripcion] = useState(doc.descripcion || "");
  const [fecha, setFecha]             = useState(doc.fecha        || hoy());
  const [loading, setLoading]         = useState(false);

  async function handleSave() {
    setLoading(true);
    try {
      await onSave({ descripcion, fecha });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-arandu-dark border border-white/10 rounded-2xl w-full max-w-sm shadow-2xl"
      >
        <div className="flex items-center justify-between p-5 border-b border-white/8">
          <div className="flex items-center gap-2">
            <Edit2 className="w-4 h-4 text-arandu-blue" />
            <h2 className="text-white font-semibold text-sm">Editar {doc.numero}</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-slate-400 text-xs font-medium mb-1.5 block">Descripción</label>
            <Input
              value={descripcion}
              onChange={e => setDescripcion(e.target.value)}
              className="bg-arandu-dark border-white/10 text-white"
            />
          </div>
          <div>
            <label className="text-slate-400 text-xs font-medium mb-1.5 block">Fecha</label>
            <DateInput
              value={fecha}
              onChange={e => setFecha(e.target.value)}
              className="bg-arandu-dark border-white/10 text-white"
            />
          </div>
        </div>
        <div className="flex gap-3 p-5 border-t border-white/8">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/10 text-slate-400">Cancelar</Button>
          <Button onClick={handleSave} disabled={loading} className="flex-1 bg-arandu-blue hover:bg-arandu-blue-dark text-white">
            <Save className="w-4 h-4 mr-1" /> {loading ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Modal de gestión de acceso a cuentas ─────────────────────────────────────
function AccesoModal({ cuentas, onClose, token, logoFiltro }) {
  const [usuarios, setUsuarios]         = useState([]);
  const [userSelId, setUserSelId]       = useState(null);
  const [accesoMap, setAccesoMap]       = useState({}); // { user_id: { cuenta_id: { ver, ver_saldo } } }
  const [umbrales, setUmbrales]         = useState({}); // { cuenta_id: { umbral_rojo, umbral_amarillo } }
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [saving, setSaving]             = useState(false);
  const [tab, setTab]                   = useState("usuarios"); // "usuarios" | "umbrales"

  useEffect(() => {
    loadUsuarios();
    // Init umbrales from cuentas
    const u = {};
    cuentas.forEach(c => {
      u[c.id] = {
        umbral_rojo: c.umbral_rojo ?? 0,
        umbral_amarillo: c.umbral_amarillo ?? 0,
        permite_saldo_negativo: c.permite_saldo_negativo ?? false,
      };
    });
    setUmbrales(u);
  }, []); // eslint-disable-line

  async function loadUsuarios() {
    setLoadingUsers(true);
    try {
      const params = logoFiltro ? `?logo_tipo=${logoFiltro}` : "";
      const res = await fetch(`${API}/admin/transferencias/config/usuarios${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUsuarios(data);
        // Init acceso map
        const map = {};
        data.forEach(u => {
          map[u.id] = u.cuentas_acceso || {};
        });
        setAccesoMap(map);
        if (data.length > 0 && !userSelId) setUserSelId(data[0].id);
      }
    } finally {
      setLoadingUsers(false);
    }
  }

  function toggleVer(userId, cuentaId) {
    setAccesoMap(prev => {
      const cur = (prev[userId]?.[cuentaId]) || {};
      const newVer = !cur.ver;
      return {
        ...prev,
        [userId]: {
          ...prev[userId],
          [cuentaId]: { ver: newVer, ver_saldo: newVer ? (cur.ver_saldo ?? false) : false },
        },
      };
    });
  }

  function toggleVerSaldo(userId, cuentaId) {
    setAccesoMap(prev => {
      const cur = (prev[userId]?.[cuentaId]) || {};
      return {
        ...prev,
        [userId]: {
          ...prev[userId],
          [cuentaId]: { ...cur, ver_saldo: !cur.ver_saldo },
        },
      };
    });
  }

  async function guardarAcceso() {
    if (!userSelId) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/usuarios/${userSelId}/cuentas-acceso`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ cuentas_acceso: accesoMap[userSelId] || {} }),
      });
      if (res.ok) {
        toast.success("Acceso guardado");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error al guardar");
      }
    } finally {
      setSaving(false);
    }
  }

  async function guardarUmbrales(cuentaId) {
    const u = umbrales[cuentaId];
    if (!u) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/cuentas-bancarias/${cuentaId}/umbrales`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          umbral_rojo: parseFloat(u.umbral_rojo) || 0,
          umbral_amarillo: parseFloat(u.umbral_amarillo) || 0,
          permite_saldo_negativo: u.permite_saldo_negativo ?? false,
        }),
      });
      if (res.ok) {
        toast.success("Umbrales guardados");
      } else {
        toast.error("Error al guardar umbrales");
      }
    } finally {
      setSaving(false);
    }
  }

  const userSel = usuarios.find(u => u.id === userSelId);
  const accesoSel = accesoMap[userSelId] || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-arandu-dark border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/8 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-violet-400" />
            <h2 className="text-white font-semibold">Gestión de Acceso a Cuentas</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/8 flex-shrink-0">
          {[
            { key: "usuarios", label: "Acceso por usuario", icon: <Users className="w-3.5 h-3.5" /> },
            { key: "umbrales", label: "Umbrales semáforo",  icon: <CircleDot className="w-3.5 h-3.5" /> },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-5 py-3 text-xs font-medium border-b-2 transition-all ${
                tab === t.key
                  ? "border-arandu-blue text-arandu-blue"
                  : "border-transparent text-slate-400 hover:text-white"
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-auto p-5">
          {tab === "usuarios" ? (
            <div className="flex gap-4 h-full min-h-[340px]">
              {/* Lista de usuarios */}
              <div className="w-44 flex-shrink-0 space-y-1">
                <p className="text-slate-500 text-[10px] uppercase tracking-wide mb-2">Usuarios</p>
                {loadingUsers ? (
                  <p className="text-slate-500 text-xs animate-pulse">Cargando...</p>
                ) : usuarios.length === 0 ? (
                  <p className="text-slate-500 text-xs italic">Sin usuarios</p>
                ) : usuarios.map(u => (
                  <button
                    key={u.id}
                    onClick={() => setUserSelId(u.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all ${
                      userSelId === u.id
                        ? "bg-arandu-blue/15 border border-arandu-blue/40 text-white"
                        : "text-slate-400 hover:bg-white/5 hover:text-white border border-transparent"
                    }`}
                  >
                    <p className="font-medium truncate">{u.name || u.email}</p>
                    <p className="text-[10px] text-slate-500 truncate">{u.email}</p>
                  </button>
                ))}
              </div>

              {/* Config del usuario seleccionado */}
              <div className="flex-1 space-y-2">
                {userSel ? (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-white text-sm font-semibold">{userSel.name || userSel.email}</p>
                      <Button onClick={guardarAcceso} disabled={saving} className="h-7 text-xs bg-arandu-blue hover:bg-arandu-blue-dark text-white px-3">
                        <Save className="w-3 h-3 mr-1" /> {saving ? "..." : "Guardar"}
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      {cuentas.map(c => {
                        const cfg = accesoSel[c.id] || {};
                        return (
                          <div
                            key={c.id}
                            className={`border rounded-lg p-3 transition-all ${
                              cfg.ver
                                ? "border-arandu-blue/30 bg-arandu-blue/5"
                                : "border-white/8 bg-white/2"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-white text-xs font-medium">{c.nombre}</p>
                                <p className="text-slate-500 text-[10px]">{c.banco} · {c.moneda}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                {/* Toggle ver */}
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                  <span className="text-slate-400 text-[10px]">Ver cuenta</span>
                                  <button
                                    type="button"
                                    onClick={() => toggleVer(userSelId, c.id)}
                                    className={`w-8 h-4.5 rounded-full transition-colors relative flex items-center ${
                                      cfg.ver ? "bg-arandu-blue" : "bg-white/10"
                                    }`}
                                    style={{ height: "18px", width: "32px" }}
                                  >
                                    <span
                                      className={`w-3 h-3 rounded-full bg-white absolute transition-all ${
                                        cfg.ver ? "left-[16px]" : "left-[2px]"
                                      }`}
                                    />
                                  </button>
                                </label>
                                {/* Toggle ver saldo */}
                                {cfg.ver && (
                                  <label className="flex items-center gap-1.5 cursor-pointer">
                                    <span className="text-slate-400 text-[10px]">Ver saldo</span>
                                    <button
                                      type="button"
                                      onClick={() => toggleVerSaldo(userSelId, c.id)}
                                      className={`rounded-full transition-colors relative flex items-center ${
                                        cfg.ver_saldo ? "bg-emerald-600" : "bg-white/10"
                                      }`}
                                      style={{ height: "18px", width: "32px" }}
                                    >
                                      <span
                                        className={`w-3 h-3 rounded-full bg-white absolute transition-all ${
                                          cfg.ver_saldo ? "left-[16px]" : "left-[2px]"
                                        }`}
                                      />
                                    </button>
                                  </label>
                                )}
                              </div>
                            </div>
                            {cfg.ver && !cfg.ver_saldo && (
                              <p className="text-slate-500 text-[10px] mt-1.5 flex items-center gap-1">
                                <CircleDot className="w-2.5 h-2.5" /> Solo ve el semáforo de estado (sin monto)
                              </p>
                            )}
                            {cfg.ver && cfg.ver_saldo && (
                              <p className="text-emerald-400/70 text-[10px] mt-1.5 flex items-center gap-1">
                                <Eye className="w-2.5 h-2.5" /> Ve el saldo real + semáforo
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p className="text-slate-500 text-sm">Seleccioná un usuario</p>
                )}
              </div>
            </div>
          ) : (
            /* Tab umbrales */
            <div className="space-y-3">
              <p className="text-slate-400 text-xs mb-4">
                Configurá los umbrales para el semáforo de saldo de cada cuenta.
                Verde: saldo &gt; Umbral amarillo. Amarillo: entre umbrales. Rojo: saldo ≤ Umbral rojo.
              </p>
              {cuentas.map(c => {
                const u = umbrales[c.id] || {};
                return (
                  <div key={c.id} className="border border-white/10 rounded-xl p-4 bg-white/2">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-white text-sm font-semibold">{c.nombre}</p>
                        <p className="text-slate-500 text-xs">{c.banco} · {c.moneda}</p>
                      </div>
                      <Button
                        onClick={() => guardarUmbrales(c.id)}
                        disabled={saving}
                        className="h-7 text-xs bg-white/8 hover:bg-white/12 text-slate-300 px-3 border border-white/10"
                      >
                        <Save className="w-3 h-3 mr-1" /> Guardar
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] text-red-400 font-medium mb-1 flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-red-500" /> Umbral rojo (saldo mínimo)
                        </label>
                        <Input
                          type="number"
                          min="0"
                          value={u.umbral_rojo ?? 0}
                          onChange={e => setUmbrales(prev => ({ ...prev, [c.id]: { ...prev[c.id], umbral_rojo: e.target.value } }))}
                          className="bg-arandu-dark border-red-500/20 text-white text-xs h-8"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-amber-400 font-medium mb-1 flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-amber-400" /> Umbral amarillo
                        </label>
                        <Input
                          type="number"
                          min="0"
                          value={u.umbral_amarillo ?? 0}
                          onChange={e => setUmbrales(prev => ({ ...prev, [c.id]: { ...prev[c.id], umbral_amarillo: e.target.value } }))}
                          className="bg-arandu-dark border-amber-500/20 text-white text-xs h-8"
                        />
                      </div>
                    </div>
                    {/* Permite saldo negativo */}
                    <div className="mt-3 flex items-center justify-between border-t border-white/5 pt-3">
                      <div>
                        <p className="text-white text-xs font-medium">Permite saldo negativo</p>
                        <p className="text-slate-500 text-[10px] mt-0.5">Si está activo, las transferencias pueden dejar esta cuenta en negativo</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setUmbrales(prev => ({
                          ...prev,
                          [c.id]: { ...prev[c.id], permite_saldo_negativo: !(u.permite_saldo_negativo ?? false) }
                        }))}
                        className={`rounded-full transition-colors relative flex items-center flex-shrink-0 ml-4 ${
                          u.permite_saldo_negativo ? "bg-amber-500" : "bg-white/10"
                        }`}
                        style={{ height: "20px", width: "36px" }}
                      >
                        <span
                          className={`w-3.5 h-3.5 rounded-full bg-white absolute transition-all ${
                            u.permite_saldo_negativo ? "left-[18px]" : "left-[2px]"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}


// ── Modal de impresión individual ─────────────────────────────────────────────
function PrintTransferenciaModal({ transfer, empresa, onClose }) {
  const [modo, setModo] = useState("a4"); // "a4" | "ticket"

  const esCross = transfer.tipo_cambio && transfer.monto_destino && transfer.de_moneda !== transfer.a_moneda;
  const empresaNombre = empresa?.nombre || empresa?.razon_social || "Empresa";
  const ahora = new Date().toLocaleString("es-PY");

  function handlePrint() {
    const el = document.getElementById("print-area-transferencia");
    const win = window.open("", "_blank");
    win.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8"/>
      <title>Transferencia ${transfer.numero}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: Arial, sans-serif; font-size: ${modo === "ticket" ? "10px" : "12px"}; color: #111; background: #fff; }
        ${modo === "a4" ? `
          .doc { width: 210mm; min-height: 297mm; margin: 0 auto; padding: 20mm 18mm; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1a2e4a; padding-bottom: 12px; margin-bottom: 18px; }
          .empresa-nombre { font-size: 20px; font-weight: 700; color: #1a2e4a; }
          .doc-title { text-align: right; }
          .doc-num { font-size: 22px; font-weight: 700; color: #1a2e4a; }
          .badge { display: inline-block; background: #e8f0fe; color: #1a2e4a; border: 1px solid #1a2e4a; border-radius: 4px; padding: 2px 8px; font-size: 11px; font-weight: 600; margin-top: 4px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 24px; }
          .info-box { background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 6px; padding: 12px; }
          .info-label { font-size: 10px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 4px; }
          .info-value { font-size: 13px; font-weight: 600; color: #111; }
          .arrow-row { text-align: center; margin: 16px 0; font-size: 18px; color: #1a2e4a; }
          .monto-box { background: #1a2e4a; color: #fff; border-radius: 8px; padding: 16px; text-align: center; margin: 20px 0; }
          .monto-label { font-size: 11px; opacity: 0.7; margin-bottom: 4px; }
          .monto-valor { font-size: 26px; font-weight: 700; }
          .monto-destino { font-size: 14px; opacity: 0.8; margin-top: 4px; }
          .desc-box { border: 1px solid #dee2e6; border-radius: 6px; padding: 12px; margin-bottom: 24px; }
          .firma-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 40px; }
          .firma-linea { border-top: 1px solid #aaa; padding-top: 6px; text-align: center; font-size: 10px; color: #666; }
          .footer { border-top: 1px solid #dee2e6; margin-top: 30px; padding-top: 8px; font-size: 9px; color: #999; text-align: center; }
        ` : `
          .doc { width: 80mm; margin: 0 auto; padding: 6mm 4mm; }
          .empresa-nombre { font-size: 13px; font-weight: 700; text-align: center; }
          .doc-num { font-size: 14px; font-weight: 700; text-align: center; margin: 4px 0; }
          .separador { border-top: 1px dashed #999; margin: 6px 0; }
          .row { display: flex; justify-content: space-between; margin: 3px 0; }
          .lbl { font-size: 10px; color: #555; }
          .val { font-size: 10px; font-weight: 600; text-align: right; max-width: 55mm; word-break: break-word; }
          .monto-ticket { text-align: center; font-size: 16px; font-weight: 700; margin: 8px 0; border: 1px solid #000; padding: 4px; }
          .center { text-align: center; font-size: 9px; color: #666; }
        `}
      </style>
    </head><body>
      ${el.innerHTML}
    </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-arandu-dark border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/8">
          <div className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-arandu-blue" />
            <h3 className="text-white font-semibold">Imprimir transferencia {transfer.numero}</h3>
          </div>
          <div className="flex items-center gap-2">
            {/* Toggle A4 / Ticket */}
            <div className="flex bg-white/5 rounded-lg p-0.5 border border-white/10">
              {["a4", "ticket"].map(m => (
                <button
                  key={m}
                  onClick={() => setModo(m)}
                  className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${
                    modo === m ? "bg-arandu-blue text-white" : "text-slate-400 hover:text-white"
                  }`}
                >
                  {m === "a4" ? "A4" : "🧾 Ticket"}
                </button>
              ))}
            </div>
            <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors ml-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Preview */}
        <div className="overflow-y-auto flex-1 p-4 bg-slate-900/40">
          <div
            id="print-area-transferencia"
            className={`bg-white rounded-lg mx-auto shadow-xl ${modo === "a4" ? "w-full max-w-2xl p-10" : "max-w-xs p-4"}`}
            style={{ color: "#111" }}
          >
            {modo === "a4" ? (
              /* ── A4 Layout ── */
              <div className="doc">
                <div className="header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #1a2e4a", paddingBottom: "12px", marginBottom: "18px" }}>
                  <div>
                    <div className="empresa-nombre" style={{ fontSize: "20px", fontWeight: 700, color: "#1a2e4a" }}>{empresaNombre}</div>
                    <div style={{ fontSize: "11px", color: "#6c757d", marginTop: "4px" }}>Módulo Financiero</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "11px", color: "#6c757d" }}>COMPROBANTE DE TRANSFERENCIA</div>
                    <div className="doc-num" style={{ fontSize: "22px", fontWeight: 700, color: "#1a2e4a" }}>N° {transfer.numero}</div>
                    <div style={{ display: "inline-block", background: "#e8f0fe", color: "#1a2e4a", border: "1px solid #1a2e4a", borderRadius: "4px", padding: "2px 8px", fontSize: "11px", fontWeight: 600, marginTop: "4px" }}>
                      {transfer.fecha}
                    </div>
                  </div>
                </div>

                {/* Cuentas */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: "12px", alignItems: "center", marginBottom: "20px" }}>
                  <div style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "6px", padding: "12px" }}>
                    <div style={{ fontSize: "10px", color: "#6c757d", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>CUENTA ORIGEN</div>
                    <div style={{ fontSize: "14px", fontWeight: 700, color: "#111" }}>{transfer.de_cuenta_nombre}</div>
                    <div style={{ fontSize: "11px", color: "#6c757d" }}>{transfer.de_moneda}</div>
                  </div>
                  <div style={{ textAlign: "center", fontSize: "20px", color: "#1a2e4a" }}>→</div>
                  <div style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "6px", padding: "12px" }}>
                    <div style={{ fontSize: "10px", color: "#6c757d", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>CUENTA DESTINO</div>
                    <div style={{ fontSize: "14px", fontWeight: 700, color: "#111" }}>{transfer.a_cuenta_nombre}</div>
                    <div style={{ fontSize: "11px", color: "#6c757d" }}>{transfer.a_moneda}</div>
                  </div>
                </div>

                {/* Monto */}
                <div style={{ background: "#1a2e4a", color: "#fff", borderRadius: "8px", padding: "16px", textAlign: "center", marginBottom: "20px" }}>
                  <div style={{ fontSize: "11px", opacity: 0.7, marginBottom: "4px" }}>MONTO TRANSFERIDO</div>
                  <div style={{ fontSize: "28px", fontWeight: 700 }}>{fmtNum(transfer.monto, transfer.de_moneda)}</div>
                  {esCross && (
                    <div style={{ fontSize: "14px", opacity: 0.8, marginTop: "4px" }}>
                      → {fmtNum(transfer.monto_destino, transfer.a_moneda)} · TC: {transfer.tipo_cambio?.toLocaleString("es-PY")}
                    </div>
                  )}
                </div>

                {/* Info adicional */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "20px" }}>
                  <div style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "6px", padding: "12px" }}>
                    <div style={{ fontSize: "10px", color: "#6c757d", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>REGISTRADO POR</div>
                    <div style={{ fontSize: "13px", fontWeight: 600 }}>{transfer.creado_por_nombre || "—"}</div>
                  </div>
                  <div style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "6px", padding: "12px" }}>
                    <div style={{ fontSize: "10px", color: "#6c757d", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>DESCRIPCIÓN</div>
                    <div style={{ fontSize: "13px", fontWeight: 600 }}>{transfer.descripcion || "Sin descripción"}</div>
                  </div>
                </div>

                {/* Firmas */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "60px", marginTop: "50px" }}>
                  <div style={{ borderTop: "1px solid #aaa", paddingTop: "6px", textAlign: "center", fontSize: "10px", color: "#666" }}>Firma y aclaración</div>
                  <div style={{ borderTop: "1px solid #aaa", paddingTop: "6px", textAlign: "center", fontSize: "10px", color: "#666" }}>Firma y aclaración</div>
                </div>

                <div style={{ borderTop: "1px solid #dee2e6", marginTop: "30px", paddingTop: "8px", fontSize: "9px", color: "#999", textAlign: "center" }}>
                  Impreso el {ahora} · {empresaNombre} · Sistema Arandu
                </div>
              </div>
            ) : (
              /* ── Ticket Layout ── */
              <div style={{ fontFamily: "'Courier New', monospace", fontSize: "11px", color: "#111" }}>
                <div style={{ textAlign: "center", fontWeight: 700, fontSize: "13px", marginBottom: "2px" }}>{empresaNombre}</div>
                <div style={{ textAlign: "center", fontSize: "10px", color: "#555", marginBottom: "6px" }}>Módulo Financiero</div>
                <div style={{ borderTop: "1px dashed #999", margin: "6px 0" }} />
                <div style={{ textAlign: "center", fontWeight: 700, fontSize: "14px" }}>TRANSFERENCIA N° {transfer.numero}</div>
                <div style={{ textAlign: "center", fontSize: "10px", marginBottom: "6px" }}>{transfer.fecha}</div>
                <div style={{ borderTop: "1px dashed #999", margin: "6px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", margin: "3px 0" }}>
                  <span style={{ color: "#555", fontSize: "10px" }}>ORIGEN:</span>
                  <span style={{ fontWeight: 600, fontSize: "10px", maxWidth: "55mm", textAlign: "right" }}>{transfer.de_cuenta_nombre} ({transfer.de_moneda})</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", margin: "3px 0" }}>
                  <span style={{ color: "#555", fontSize: "10px" }}>DESTINO:</span>
                  <span style={{ fontWeight: 600, fontSize: "10px", maxWidth: "55mm", textAlign: "right" }}>{transfer.a_cuenta_nombre} ({transfer.a_moneda})</span>
                </div>
                <div style={{ borderTop: "1px dashed #999", margin: "6px 0" }} />
                <div style={{ textAlign: "center", fontWeight: 700, fontSize: "16px", border: "1px solid #000", padding: "4px", margin: "6px 0" }}>
                  {fmtNum(transfer.monto, transfer.de_moneda)}
                </div>
                {esCross && (
                  <div style={{ textAlign: "center", fontSize: "10px", color: "#555" }}>
                    → {fmtNum(transfer.monto_destino, transfer.a_moneda)} · TC: {transfer.tipo_cambio?.toLocaleString("es-PY")}
                  </div>
                )}
                <div style={{ borderTop: "1px dashed #999", margin: "6px 0" }} />
                {transfer.descripcion && (
                  <div style={{ display: "flex", justifyContent: "space-between", margin: "3px 0" }}>
                    <span style={{ color: "#555", fontSize: "10px" }}>DESC:</span>
                    <span style={{ fontSize: "10px", maxWidth: "55mm", textAlign: "right" }}>{transfer.descripcion}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", margin: "3px 0" }}>
                  <span style={{ color: "#555", fontSize: "10px" }}>POR:</span>
                  <span style={{ fontSize: "10px" }}>{transfer.creado_por_nombre || "—"}</span>
                </div>
                <div style={{ borderTop: "1px dashed #999", margin: "6px 0" }} />
                <div style={{ textAlign: "center", fontSize: "9px", color: "#999" }}>
                  {ahora}
                </div>
                <div style={{ textAlign: "center", fontSize: "9px", color: "#999" }}>
                  Sistema Arandu
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-white/8">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-slate-400 h-9">
            Cancelar
          </Button>
          <Button
            onClick={handlePrint}
            className="bg-arandu-blue hover:bg-arandu-blue-dark text-white h-9 gap-2"
          >
            <Printer className="w-4 h-4" />
            Imprimir {modo === "a4" ? "A4" : "Ticket"}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ── Modal de reporte histórico imprimible ─────────────────────────────────────
function PrintReporteModal({ transfers, empresa, filtros, onClose }) {
  const empresaNombre = empresa?.nombre || empresa?.razon_social || "Empresa";
  const ahora = new Date().toLocaleString("es-PY");
  const totalMonto = transfers.reduce((s, t) => s + (t.monto || 0), 0);

  // Intentar detectar moneda dominante para mostrar total
  const monedas = [...new Set(transfers.map(t => t.de_moneda || "PYG"))];
  const monedaUnica = monedas.length === 1 ? monedas[0] : null;

  function handlePrint() {
    const el = document.getElementById("print-area-reporte");
    const win = window.open("", "_blank");
    win.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8"/>
      <title>Reporte de Transferencias</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: Arial, sans-serif; font-size: 11px; color: #111; background: #fff; }
        .doc { padding: 16mm 14mm; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1a2e4a; padding-bottom: 10px; margin-bottom: 16px; }
        .empresa { font-size: 18px; font-weight: 700; color: #1a2e4a; }
        .doc-title { font-size: 16px; font-weight: 700; color: #1a2e4a; text-align: right; }
        .meta { font-size: 10px; color: #666; text-align: right; margin-top: 3px; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th { background: #1a2e4a; color: #fff; padding: 6px 8px; text-align: left; font-size: 10px; }
        td { padding: 5px 8px; border-bottom: 1px solid #eee; font-size: 10px; vertical-align: top; }
        tr:nth-child(even) td { background: #f8f9fa; }
        .text-right { text-align: right; }
        .totales { margin-top: 12px; border-top: 2px solid #1a2e4a; padding-top: 8px; display: flex; justify-content: flex-end; gap: 24px; }
        .totales-label { font-size: 11px; font-weight: 600; }
        .totales-valor { font-size: 13px; font-weight: 700; color: #1a2e4a; }
        .footer { margin-top: 20px; border-top: 1px solid #dee2e6; padding-top: 6px; font-size: 9px; color: #999; text-align: center; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style>
    </head><body>
      ${el.innerHTML}
    </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-arandu-dark border border-white/10 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/8">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-arandu-blue" />
            <h3 className="text-white font-semibold">Reporte de transferencias ({transfers.length})</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preview */}
        <div className="overflow-y-auto flex-1 p-4 bg-slate-900/40">
          <div id="print-area-reporte" className="bg-white rounded-lg p-8 mx-auto shadow-xl" style={{ color: "#111" }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #1a2e4a", paddingBottom: "10px", marginBottom: "16px" }}>
              <div>
                <div style={{ fontSize: "18px", fontWeight: 700, color: "#1a2e4a" }}>{empresaNombre}</div>
                <div style={{ fontSize: "10px", color: "#666", marginTop: "3px" }}>Módulo Financiero — Histórico de Transferencias</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "16px", fontWeight: 700, color: "#1a2e4a" }}>REPORTE DE TRANSFERENCIAS</div>
                <div style={{ fontSize: "10px", color: "#666", marginTop: "3px" }}>
                  {filtros.desde && filtros.hasta ? `Período: ${filtros.desde} al ${filtros.hasta}` :
                   filtros.desde ? `Desde: ${filtros.desde}` :
                   filtros.hasta ? `Hasta: ${filtros.hasta}` : "Todos los períodos"}
                </div>
                {filtros.cuenta && <div style={{ fontSize: "10px", color: "#666" }}>Cuenta: {filtros.cuenta}</div>}
                <div style={{ fontSize: "10px", color: "#999", marginTop: "3px" }}>Generado: {ahora}</div>
              </div>
            </div>

            {/* Tabla */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "12px" }}>
              <thead>
                <tr>
                  {["Nro.", "Fecha", "Origen", "Destino", "Monto", "Descripción", "Registrado por"].map(h => (
                    <th key={h} style={{ background: "#1a2e4a", color: "#fff", padding: "6px 8px", textAlign: "left", fontSize: "10px" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transfers.map((t, i) => {
                  const rowBg = i % 2 === 1 ? "#f0f4f8" : "#ffffff";
                  const tdBase = { padding: "5px 8px", borderBottom: "1px solid #dde", background: rowBg, fontSize: "10px", color: "#111" };
                  return (
                    <tr key={t.id}>
                      <td style={{ ...tdBase, fontWeight: 700, color: "#1a2e4a" }}>{t.numero}</td>
                      <td style={{ ...tdBase }}>{t.fecha}</td>
                      <td style={{ ...tdBase }}>
                        <span style={{ fontWeight: 600 }}>{t.de_cuenta_nombre}</span><br/>
                        <span style={{ color: "#555", fontSize: "9px" }}>{t.de_moneda}</span>
                      </td>
                      <td style={{ ...tdBase }}>
                        <span style={{ fontWeight: 600 }}>{t.a_cuenta_nombre}</span><br/>
                        <span style={{ color: "#555", fontSize: "9px" }}>{t.a_moneda}</span>
                      </td>
                      <td style={{ ...tdBase, textAlign: "right", fontWeight: 700 }}>
                        {fmtNum(t.monto, t.de_moneda)}
                        {t.tipo_cambio && <div style={{ fontSize: "9px", color: "#444", fontWeight: "normal" }}>→ {fmtNum(t.monto_destino, t.a_moneda)}</div>}
                      </td>
                      <td style={{ ...tdBase, color: "#333" }}>{t.descripcion || "—"}</td>
                      <td style={{ ...tdBase, color: "#333" }}>{t.creado_por_nombre || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Totales */}
            <div style={{ marginTop: "12px", borderTop: "2px solid #1a2e4a", paddingTop: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: "11px", color: "#666" }}>{transfers.length} transferencia{transfers.length !== 1 ? "s" : ""}</div>
              {monedaUnica && (
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "10px", color: "#666" }}>TOTAL TRANSFERIDO</div>
                  <div style={{ fontSize: "16px", fontWeight: 700, color: "#1a2e4a" }}>{fmtNum(totalMonto, monedaUnica)}</div>
                </div>
              )}
            </div>

            <div style={{ marginTop: "16px", borderTop: "1px solid #dee2e6", paddingTop: "6px", fontSize: "9px", color: "#999", textAlign: "center" }}>
              Impreso el {ahora} · {empresaNombre} · Sistema Arandu
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-white/8">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-slate-400 h-9">
            Cancelar
          </Button>
          <Button
            onClick={handlePrint}
            className="bg-arandu-blue hover:bg-arandu-blue-dark text-white h-9 gap-2"
          >
            <Printer className="w-4 h-4" />
            Imprimir reporte
          </Button>
        </div>
      </div>
    </div>
  );
}


// ── Modal acceso bancos (sistema + reportes) ──────────────────────────────────
function AccesoBancosModal({ cuentas, onClose, token, logoFiltro }) {
  const [usuarios, setUsuarios]         = useState([]);
  const [userSelId, setUserSelId]       = useState(null);
  const [config, setConfig]             = useState({});   // { user_id: { modo, cuentas_ids, reporte_ids, sistema_ids } }
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);

  useEffect(() => { loadUsuarios(); }, []); // eslint-disable-line

  async function loadUsuarios() {
    setLoading(true);
    try {
      const params = logoFiltro ? `?logo_tipo=${logoFiltro}` : "";
      const res = await fetch(`${API}/admin/bancos/config/usuarios${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const lista = data.usuarios || [];
        setUsuarios(lista);
        const map = {};
        lista.forEach(u => {
          map[u.id] = u.acceso_cuentas || null;
        });
        setConfig(map);
        if (lista.length > 0) setUserSelId(lista[0].id);
      }
    } finally { setLoading(false); }
  }

  const userSel = usuarios.find(u => u.id === userSelId);
  const userConfig = config[userSelId] || null;

  function setUserModo(modo) {
    setConfig(prev => ({ ...prev, [userSelId]: { ...(prev[userSelId] || {}), modo, cuentas_ids: [], reporte_ids: [], sistema_ids: [] } }));
  }

  function toggleCuenta(field, cid) {
    const cur = config[userSelId] || {};
    const arr = cur[field] || [];
    const next = arr.includes(cid) ? arr.filter(x => x !== cid) : [...arr, cid];
    setConfig(prev => ({ ...prev, [userSelId]: { ...(prev[userSelId] || {}), [field]: next } }));
  }

  function clearConfig() {
    setConfig(prev => ({ ...prev, [userSelId]: null }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await Promise.all(
        usuarios.map(async u => {
          const acc = config[u.id];
          const body = acc === null
            ? { borrar: true }
            : { modo: acc.modo || "unificado", cuentas_ids: acc.cuentas_ids || [], reporte_ids: acc.reporte_ids || [], sistema_ids: acc.sistema_ids || [] };
          const res = await fetch(`${API}/admin/usuarios/${u.id}/acceso-cuentas`, {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error"); }
        })
      );
      toast.success("Acceso a bancos actualizado");
      onClose();
    } catch (e) { toast.error(e.message || "Error al guardar"); }
    finally { setSaving(false); }
  }

  const modo = userConfig?.modo || "unificado";

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-arandu-dark border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <h2 className="font-heading text-white text-lg flex items-center gap-2">
              <Building2 className="w-4 h-4 text-cyan-400" /> Acceso a cuentas en Bancos
            </h2>
            <p className="text-slate-500 text-xs mt-0.5">Configurá qué cuentas puede ver cada usuario en el sistema y en reportes.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Lista usuarios */}
          <div className="w-52 border-r border-white/8 flex-shrink-0 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-slate-500 text-sm animate-pulse">Cargando...</div>
            ) : usuarios.length === 0 ? (
              <div className="p-4 text-slate-500 text-xs">Sin usuarios en esta empresa.</div>
            ) : (
              usuarios.map(u => (
                <button
                  key={u.id}
                  onClick={() => setUserSelId(u.id)}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 transition-all ${userSelId === u.id ? "bg-arandu-blue/15 border-l-2 border-l-arandu-blue text-white" : "text-slate-400 hover:bg-white/3"}`}
                >
                  <p className="text-sm font-medium truncate">{u.name}</p>
                  <p className="text-xs text-slate-500 truncate">{u.email}</p>
                  {config[u.id] !== null && config[u.id]?.modo && (
                    <span className="text-[10px] text-cyan-400">● {config[u.id].modo}</span>
                  )}
                  {config[u.id] === null && (
                    <span className="text-[10px] text-slate-600">sin restricción</span>
                  )}
                </button>
              ))
            )}
          </div>

          {/* Config del usuario seleccionado */}
          <div className="flex-1 p-5 overflow-y-auto space-y-4">
            {!userSel ? (
              <p className="text-slate-500 text-sm">Seleccioná un usuario.</p>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-white font-semibold text-sm">{userSel.name}</p>
                  {userConfig && (
                    <button onClick={clearConfig} className="text-xs text-slate-500 hover:text-red-400 border border-white/10 rounded px-2 py-1">
                      Quitar restricción
                    </button>
                  )}
                </div>

                {!userConfig && (
                  <div className="bg-emerald-500/8 border border-emerald-500/20 rounded-lg p-3 text-xs text-emerald-300">
                    Sin restricción — el usuario ve todas las cuentas.
                  </div>
                )}

                {/* Modo selector */}
                <div>
                  <p className="text-slate-400 text-xs mb-2">Modo de acceso</p>
                  <div className="flex gap-2">
                    {[["unificado", "Unificado", "Misma lista para sistema y reportes"], ["separado", "Separado", "Listas distintas para sistema y reportes"]].map(([val, label, desc]) => (
                      <button
                        key={val}
                        onClick={() => setUserModo(val)}
                        className={`flex-1 text-left px-3 py-2.5 rounded-lg border text-xs transition-all ${(userConfig?.modo || "unificado") === val ? "border-arandu-blue bg-arandu-blue/15 text-arandu-blue" : "border-white/10 text-slate-400 hover:border-white/20"}`}
                      >
                        <p className="font-semibold">{label}</p>
                        <p className="text-slate-500 mt-0.5 text-[10px]">{desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Selector de cuentas */}
                {modo === "unificado" && (
                  <div>
                    <p className="text-slate-400 text-xs mb-2">Cuentas permitidas (sistema y reportes)</p>
                    <div className="space-y-1.5 max-h-52 overflow-y-auto">
                      {cuentas.map(c => {
                        const on = (userConfig?.cuentas_ids || []).includes(c.id);
                        return (
                          <button key={c.id} onClick={() => toggleCuenta("cuentas_ids", c.id)} className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-all flex items-center justify-between ${on ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-200" : "border-white/8 text-slate-400 hover:border-white/20"}`}>
                            <span>{c.nombre} <span className="text-slate-500">({c.moneda})</span></span>
                            {on && <Check className="w-3.5 h-3.5" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {modo === "separado" && (
                  <div className="grid grid-cols-2 gap-3">
                    {[["reporte_ids", "Cuentas para reportes"], ["sistema_ids", "Cuentas para sistema"]].map(([field, label]) => (
                      <div key={field}>
                        <p className="text-slate-400 text-xs mb-2">{label}</p>
                        <div className="space-y-1.5 max-h-48 overflow-y-auto">
                          {cuentas.map(c => {
                            const on = (userConfig?.[field] || []).includes(c.id);
                            return (
                              <button key={c.id} onClick={() => toggleCuenta(field, c.id)} className={`w-full text-left px-2 py-1.5 rounded-lg border text-[11px] transition-all flex items-center justify-between ${on ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-200" : "border-white/8 text-slate-400 hover:border-white/20"}`}>
                                <span className="truncate">{c.nombre}</span>
                                {on && <Check className="w-3 h-3 flex-shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-white/8">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-slate-400 h-9">Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white h-9 gap-2">
            <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}


// ── Restricción de fechas en movimientos ───────────────────────────────────
// Bloquea (opcionalmente) registrar facturas/compras/pagos/cheques/etc. con
// fecha futura o retroactiva. Modo "libre" = sin restricción (default,
// comportamiento histórico). Modo "restringido" con alcance general (un solo
// permitir_pasado/permitir_futuro para todos) o por usuario (cada uno con su
// propio override; si no tiene uno guardado, se le aplica el general).
// admin/gerente/super_admin quedan siempre exentos (resuelto en el backend).
function FechasMovimientosModal({ onClose, token, logoFiltro }) {
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [cfg, setCfg] = useState({ modo: "libre", personalizado_por_usuario: false, permitir_pasado: false, permitir_futuro: false });
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingUserId, setSavingUserId] = useState(null);

  useEffect(() => { cargarConfig(); }, []); // eslint-disable-line

  async function cargarConfig() {
    setLoading(true);
    try {
      const params = logoFiltro ? `?logo_tipo=${logoFiltro}` : "";
      const res = await fetch(`${API}/admin/configuracion-fechas-movimientos${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setCfg(data);
        if (data.personalizado_por_usuario) cargarUsuarios();
      }
    } finally {
      setLoading(false);
    }
  }

  async function cargarUsuarios() {
    setLoadingUsuarios(true);
    try {
      const params = logoFiltro ? `?logo_tipo=${logoFiltro}` : "";
      const res = await fetch(`${API}/admin/configuracion-fechas-movimientos/usuarios${params}`, { headers });
      if (res.ok) setUsuarios(await res.json());
    } finally {
      setLoadingUsuarios(false);
    }
  }

  async function guardarConfig(patch) {
    const next = { ...cfg, ...patch };
    setCfg(next);
    setSaving(true);
    try {
      const body = { ...next, logo_tipo: logoFiltro || undefined };
      const res = await fetch(`${API}/admin/configuracion-fechas-movimientos`, { method: "PUT", headers, body: JSON.stringify(body) });
      if (res.ok) {
        toast.success("Configuración guardada");
        if (next.personalizado_por_usuario) cargarUsuarios();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo guardar");
      }
    } finally {
      setSaving(false);
    }
  }

  async function guardarUsuario(userId, override) {
    setSavingUserId(userId);
    try {
      const body = override
        ? { ...override, logo_tipo: logoFiltro || undefined }
        : { reset: true, logo_tipo: logoFiltro || undefined };
      const res = await fetch(`${API}/admin/configuracion-fechas-movimientos/usuarios/${userId}`, {
        method: "PUT", headers, body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        setUsuarios(prev => prev.map(u => u.user_id === userId ? { ...u, override: data.override } : u));
      } else {
        toast.error("No se pudo guardar la preferencia del usuario");
      }
    } finally {
      setSavingUserId(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-arandu-dark border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/8 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-amber-400" />
            <h2 className="text-white font-semibold">Restricción de fechas en movimientos</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-5">
          {loading ? (
            <p className="text-slate-500 text-xs animate-pulse">Cargando…</p>
          ) : (
            <>
              <p className="text-slate-400 text-xs">
                Controla si se pueden registrar facturas, compras, pagos, cheques y otros movimientos con una
                fecha distinta a la de hoy. <span className="text-white font-medium">Hoy siempre está permitido</span> —
                esto solo restringe fechas pasadas y/o futuras.
              </p>

              {/* Modo */}
              <div className="flex bg-white/5 rounded-lg p-1 border border-white/10 w-fit">
                {[
                  { key: "libre", label: "Libre" },
                  { key: "restringido", label: "Restringido" },
                ].map(m => (
                  <button
                    key={m.key}
                    onClick={() => guardarConfig({ modo: m.key })}
                    disabled={saving}
                    className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${
                      cfg.modo === m.key ? "bg-amber-500 text-black" : "text-slate-400 hover:text-white"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {cfg.modo === "restringido" && (
                <>
                  {/* Alcance */}
                  <div className="flex items-center justify-between border border-white/10 rounded-xl p-4 bg-white/3">
                    <div>
                      <p className="text-white text-sm font-medium">Personalizar por usuario</p>
                      <p className="text-slate-400 text-xs mt-0.5">
                        Si está apagado, la configuración general de abajo aplica a todos por igual.
                      </p>
                    </div>
                    <button
                      onClick={() => guardarConfig({ personalizado_por_usuario: !cfg.personalizado_por_usuario })}
                      disabled={saving}
                      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${cfg.personalizado_por_usuario ? "bg-amber-500" : "bg-white/15"}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${cfg.personalizado_por_usuario ? "translate-x-5" : ""}`} />
                    </button>
                  </div>

                  {/* General */}
                  <div className="border border-white/10 rounded-xl p-4 bg-white/3 space-y-3">
                    <p className="text-white text-sm font-medium">
                      {cfg.personalizado_por_usuario ? "General (default para usuarios sin configuración propia)" : "General (aplica a todos)"}
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="flex items-center gap-2 border border-white/10 rounded-lg px-3 py-2 opacity-60">
                        <input type="checkbox" checked readOnly className="accent-amber-500" />
                        <span className="text-xs text-slate-300">Presente</span>
                      </div>
                      <label className="flex items-center gap-2 border border-white/10 rounded-lg px-3 py-2 cursor-pointer hover:border-white/25">
                        <input
                          type="checkbox"
                          checked={!!cfg.permitir_pasado}
                          onChange={e => guardarConfig({ permitir_pasado: e.target.checked })}
                          disabled={saving}
                          className="accent-amber-500"
                        />
                        <span className="text-xs text-slate-300">Pasado</span>
                      </label>
                      <label className="flex items-center gap-2 border border-white/10 rounded-lg px-3 py-2 cursor-pointer hover:border-white/25">
                        <input
                          type="checkbox"
                          checked={!!cfg.permitir_futuro}
                          onChange={e => guardarConfig({ permitir_futuro: e.target.checked })}
                          disabled={saving}
                          className="accent-amber-500"
                        />
                        <span className="text-xs text-slate-300">Futuro</span>
                      </label>
                    </div>
                  </div>

                  {/* Por usuario */}
                  {cfg.personalizado_por_usuario && (
                    <div className="border border-white/10 rounded-xl p-4 bg-white/3">
                      <p className="text-white text-sm font-medium mb-3">Por usuario</p>
                      {loadingUsuarios ? (
                        <p className="text-slate-500 text-xs animate-pulse">Cargando usuarios…</p>
                      ) : usuarios.length === 0 ? (
                        <p className="text-slate-500 text-xs italic">Sin usuarios</p>
                      ) : (
                        <div className="space-y-1.5 max-h-64 overflow-auto">
                          {usuarios.map(u => {
                            const ov = u.override;
                            return (
                              <div key={u.user_id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-white/8">
                                <div className="min-w-0">
                                  <p className="text-xs text-white truncate">{u.nombre}</p>
                                  <p className="text-[10px] text-slate-500 truncate">{ov ? "Personalizado" : "Usa el general"}</p>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  <label className="flex items-center gap-1 text-[11px] text-slate-300 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={!!ov?.permitir_pasado}
                                      disabled={savingUserId === u.user_id}
                                      onChange={e => guardarUsuario(u.user_id, { permitir_pasado: e.target.checked, permitir_futuro: !!ov?.permitir_futuro })}
                                      className="accent-amber-500"
                                    />
                                    Pasado
                                  </label>
                                  <label className="flex items-center gap-1 text-[11px] text-slate-300 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={!!ov?.permitir_futuro}
                                      disabled={savingUserId === u.user_id}
                                      onChange={e => guardarUsuario(u.user_id, { permitir_pasado: !!ov?.permitir_pasado, permitir_futuro: e.target.checked })}
                                      className="accent-amber-500"
                                    />
                                    Futuro
                                  </label>
                                  {ov && (
                                    <button
                                      onClick={() => guardarUsuario(u.user_id, null)}
                                      disabled={savingUserId === u.user_id}
                                      className="text-[10px] text-slate-500 hover:text-red-400 underline"
                                    >
                                      Restaurar
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-white/8">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-slate-400 h-9">Cerrar</Button>
        </div>
      </motion.div>
    </div>
  );
}


// Historial de traspasos de saldo realizados (para verificar que se aplicaron)
function HistorialTraspasos({ headers, logoFiltro }) {
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [q, setQ] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const cargar = useCallback(async () => {
    if (!logoFiltro) return;
    setCargando(true);
    try {
      const qs = new URLSearchParams({ logo_tipo: logoFiltro });
      if (q.trim()) qs.append("q", q.trim());
      if (desde) qs.append("desde", desde);
      if (hasta) qs.append("hasta", hasta);
      const r = await fetch(`${API}/admin/plan-cuentas/traspaso/historial?${qs.toString()}`, { headers });
      if (r.ok) setFilas(await r.json());
      else toast.error("No se pudo cargar el historial");
    } finally { setCargando(false); }
  }, [logoFiltro, q, desde, hasta]); // eslint-disable-line

  useEffect(() => { cargar(); }, [logoFiltro]); // eslint-disable-line

  const fmtFecha = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d) ? iso.slice(0, 16) : d.toLocaleString("es-PY", { dateStyle: "short", timeStyle: "short" });
  };

  return (
    <div className="space-y-4">
      <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-slate-400 font-semibold uppercase">Buscar</label>
          <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === "Enter") cargar(); }}
            placeholder="N° o cliente/proveedor..." className="mt-1 bg-slate-950 border-white/10 text-white" />
        </div>
        <div>
          <label className="text-xs text-slate-400 font-semibold uppercase">Desde</label>
          <DateInput value={desde} onChange={e => setDesde(e.target.value)} style={{ colorScheme: "dark" }}
            className="mt-1 block bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
        </div>
        <div>
          <label className="text-xs text-slate-400 font-semibold uppercase">Hasta</label>
          <DateInput value={hasta} onChange={e => setHasta(e.target.value)} style={{ colorScheme: "dark" }}
            className="mt-1 block bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
        </div>
        <Button onClick={cargar} disabled={cargando} className="bg-arandu-blue hover:bg-arandu-blue/80 gap-2">
          <Search className="w-4 h-4" /> {cargando ? "Cargando..." : "Buscar"}
        </Button>
      </div>

      <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-slate-400 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Fecha y hora</th>
              <th className="text-left px-4 py-3">Documento</th>
              <th className="text-left px-4 py-3">Entidad</th>
              <th className="text-left px-4 py-3">Cuenta anterior → nueva</th>
              <th className="text-right px-4 py-3">Saldo</th>
              <th className="text-left px-4 py-3">Vencimiento</th>
              <th className="text-left px-4 py-3">Usuario</th>
              <th className="text-left px-4 py-3">Estado actual</th>
            </tr>
          </thead>
          <tbody>
            {filas.length === 0 && !cargando && (
              <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-500">No hay traspasos registrados con esos filtros.</td></tr>
            )}
            {filas.map(f => (
              <tr key={f.id} className="border-t border-white/5 hover:bg-white/[0.02] align-top">
                <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtFecha(f.fecha)}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border mr-2 ${f.tipo_doc === "factura" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-amber-500/10 border-amber-500/30 text-amber-300"}`}>
                    {f.tipo_doc === "factura" ? "Factura" : "Compra"}
                  </span>
                  <span className="text-white font-mono text-xs">{f.numero || "—"}</span>
                </td>
                <td className="px-4 py-3 text-white">{f.entidad_nombre || "—"}</td>
                <td className="px-4 py-3 text-slate-300">
                  {f.cuenta_anterior || "Sin cuenta"} <span className="text-slate-500">→</span> <span className="text-white font-medium">{f.cuenta_nueva}</span>
                  {f.notas && <p className="text-slate-500 text-xs mt-0.5">{f.notas}</p>}
                </td>
                <td className="px-4 py-3 text-right text-white">{fmtNum(f.saldo_al_traspasar, f.moneda)}</td>
                <td className="px-4 py-3 text-slate-300 text-xs whitespace-nowrap">
                  {f.vencimiento_nuevo ? `${f.vencimiento_anterior || "—"} → ${f.vencimiento_nuevo}` : "sin cambio"}
                </td>
                <td className="px-4 py-3 text-slate-300">{f.usuario || "—"}</td>
                <td className="px-4 py-3 text-xs">
                  {f.documento_eliminado ? (
                    <span className="text-red-300">Documento eliminado</span>
                  ) : f.vigente ? (
                    <span className="text-emerald-300">✔ En {f.cuenta_actual} · saldo {fmtNum(f.saldo_actual, f.moneda)}</span>
                  ) : (
                    <span className="text-slate-400">Hoy en {f.cuenta_actual || "Sin cuenta"} (movido luego)</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── Traspaso de Saldos ───────────────────────────────────────────────────────
// Distinto de "Transferencias" (que mueve dinero entre cuentas bancarias):
// esto reclasifica a qué cuenta del Plan de Cuentas pertenece el saldo
// pendiente de una factura o compra completa — ej. de "Venta contado" a
// "Venta cuotas", o de una cuenta de cobrar a "Saldo a favor cliente" (y
// viceversa). Gated por permiso plan_cuentas.traspasar_saldos.
function TraspasoSaldosPanel({ token, logoFiltro }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [planCuentas, setPlanCuentas] = useState([]);
  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [buscoAlMenosUnaVez, setBuscoAlMenosUnaVez] = useState(false);
  const [seleccionado, setSeleccionado] = useState(null); // documento elegido para traspasar
  const [destinoId, setDestinoId] = useState("");
  const [notas, setNotas] = useState("");
  const [fechaVenc, setFechaVenc] = useState("");
  const [numCuotas, setNumCuotas] = useState(1);
  const [cuotasEdit, setCuotasEdit] = useState(null);       // cuotas impagas editables (null = documento sin cuotas)
  const [cuotasOrig, setCuotasOrig] = useState("");         // JSON original, para saber si se modificaron
  const [guardando, setGuardando] = useState(false);
  const [vista, setVista] = useState("nuevo");          // "nuevo" | "historial"
  const [ultimoResultado, setUltimoResultado] = useState(null); // confirmación del último traspaso

  const fetchPlanCuentas = useCallback(async () => {
    if (!logoFiltro) return;
    const r = await fetch(`${API}/admin/plan-cuentas?logo_tipo=${logoFiltro}`, { headers });
    if (r.ok) setPlanCuentas(await r.json());
  }, [logoFiltro, token]); // eslint-disable-line

  useEffect(() => { fetchPlanCuentas(); }, [fetchPlanCuentas]);

  const buscar = useCallback(async () => {
    if (!logoFiltro) return;
    setBuscando(true);
    try {
      const qs = new URLSearchParams({ logo_tipo: logoFiltro });
      if (query.trim()) qs.append("q", query.trim());
      const r = await fetch(`${API}/admin/plan-cuentas/traspaso/buscar?${qs.toString()}`, { headers });
      if (r.ok) setResultados(await r.json());
      else toast.error("No se pudo buscar documentos");
    } finally {
      setBuscando(false);
      setBuscoAlMenosUnaVez(true);
    }
  }, [logoFiltro, query, token]); // eslint-disable-line

  useEffect(() => { buscar(); }, [logoFiltro]); // eslint-disable-line

  const abrirTraspaso = (doc) => {
    setSeleccionado(doc);
    setDestinoId("");
    setNotas("");
    setFechaVenc((doc.fecha_vencimiento || "").slice(0, 10));
    setNumCuotas(1);
    setCuotasEdit(null);
    setCuotasOrig("");
    // Si el documento ya está en cuotas, se cargan para poder tipear monto y vencimiento de cada una
    fetch(`${API}/admin/plan-cuentas/traspaso/cuotas?tipo_doc=${doc.tipo_doc}&doc_id=${doc.doc_id}`, { headers })
      .then(r => (r.ok ? r.json() : []))
      .then(rows => {
        if (Array.isArray(rows) && rows.length > 0) {
          const e = rows.map(c => ({ id: c.id, numero: c.numero, monto: c.monto, fecha_vencimiento: (c.fecha_vencimiento || "").slice(0, 10) }));
          setCuotasEdit(e);
          setCuotasOrig(JSON.stringify(e));
        }
      })
      .catch(() => {});
  };

  // Se permite la misma cuenta de origen: sirve para postergar el vencimiento del saldo
  const cuentasDestino = planCuentas.filter(c => c.activa !== false);
  const destinoCuenta = planCuentas.find(c => c.id === destinoId);
  const destinoEsCuotas = !!destinoCuenta?.es_cuotas;
  const maxCuotasDestino = Math.max(1, Number(destinoCuenta?.mi_max_cuotas ?? destinoCuenta?.max_cuotas ?? 6));
  const mismaCuenta = !!seleccionado && destinoId === seleccionado.plan_cuenta_id;
  const planPrevio = (() => {
    if (!destinoEsCuotas || !seleccionado) return [];
    const n = Math.max(1, Math.min(maxCuotasDestino, Number(numCuotas) || 1));
    const total = Number(seleccionado.saldo_pendiente) || 0;
    const base = Math.round((total / n) * 100) / 100;
    const resto = Math.round((total - base * n) * 100) / 100;
    const d0 = fechaVenc ? new Date(`${fechaVenc}T00:00:00`) : new Date(Date.now() + 30 * 86400000);
    return Array.from({ length: n }, (_, i) => {
      const y = d0.getFullYear() + Math.floor((d0.getMonth() + i) / 12);
      const m = (d0.getMonth() + i) % 12;
      const dia = Math.min(d0.getDate(), new Date(y, m + 1, 0).getDate());
      const f = `${String(dia).padStart(2, "0")}/${String(m + 1).padStart(2, "0")}/${y}`;
      return { n: i + 1, f, monto: base + (i === n - 1 ? resto : 0) };
    });
  })();
  const cuotasModificadas = !!cuotasEdit && JSON.stringify(cuotasEdit) !== cuotasOrig;
  const cuotasValidas = !cuotasEdit || (cuotasEdit.length > 0 && cuotasEdit.every(c => Number(c.monto) > 0 && /^\d{4}-\d{2}-\d{2}$/.test(c.fecha_vencimiento || "")));
  const puedeConfirmar = cuotasEdit
    ? (!!destinoId && !guardando && cuotasValidas && (!mismaCuenta || cuotasModificadas))
    : (!!destinoId && !guardando && (!mismaCuenta || !!fechaVenc || destinoEsCuotas) && (!destinoEsCuotas || (Number(numCuotas) >= 1 && Number(numCuotas) <= maxCuotasDestino)));

  const confirmarTraspaso = async () => {
    if (!seleccionado || !destinoId) return;
    setGuardando(true);
    try {
      const r = await fetch(`${API}/admin/plan-cuentas/traspaso`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          tipo_doc: seleccionado.tipo_doc,
          doc_id: seleccionado.doc_id,
          plan_cuenta_destino_id: destinoId,
          notas: notas.trim() || null,
          fecha_vencimiento: cuotasEdit ? null : (fechaVenc || null),
          num_cuotas: !cuotasEdit && destinoEsCuotas ? Number(numCuotas) : null,
          cuotas: cuotasEdit && (cuotasModificadas || destinoEsCuotas)
            ? cuotasEdit.map(c => ({ id: c.id || null, monto: Number(c.monto) || 0, fecha_vencimiento: c.fecha_vencimiento }))
            : null,
        }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || "No se pudo traspasar"); }
      const docActualizado = await r.json().catch(() => ({}));
      const cuentaNueva = planCuentas.find(c => c.id === destinoId);
      toast.success("Saldo traspasado correctamente");
      setUltimoResultado({
        tipo_doc: seleccionado.tipo_doc,
        numero: seleccionado.numero,
        entidad: seleccionado.entidad_nombre,
        de: seleccionado.plan_cuenta_nombre || "Sin cuenta",
        a: cuentaNueva?.nombre || docActualizado?.plan_cuenta_id,
        // confirmación leída del documento ya guardado, no de lo que se envió
        aplicado: docActualizado?.plan_cuenta_id === destinoId,
        saldo: seleccionado.saldo_pendiente,
        moneda: seleccionado.moneda,
        vencimiento: fechaVenc || null,
      });
      setSeleccionado(null);
      buscar();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex gap-2">
        {[["nuevo", "Traspasar"], ["historial", "Traspasos realizados"]].map(([k, l]) => (
          <button key={k} onClick={() => setVista(k)}
            className={`px-4 py-2 rounded-lg text-sm border ${vista === k ? "bg-arandu-blue text-white border-arandu-blue" : "border-white/10 text-slate-300 hover:text-white"}`}>{l}</button>
        ))}
      </div>

      {vista === "historial" && <HistorialTraspasos headers={headers} logoFiltro={logoFiltro} />}

      {vista === "nuevo" && ultimoResultado && (
        <div className={`rounded-xl border p-4 flex items-start justify-between gap-3 ${ultimoResultado.aplicado ? "bg-emerald-500/10 border-emerald-500/30" : "bg-red-500/10 border-red-500/30"}`}>
          <div className="text-sm">
            <p className={`font-semibold ${ultimoResultado.aplicado ? "text-emerald-300" : "text-red-300"}`}>
              {ultimoResultado.aplicado ? "✔ Traspaso realizado y verificado" : "⚠ El traspaso no quedó aplicado, revisá el historial"}
            </p>
            <p className="text-slate-200 mt-1">
              {ultimoResultado.tipo_doc === "factura" ? "Factura" : "Compra"} {ultimoResultado.numero} · {ultimoResultado.entidad}: <b>{ultimoResultado.de}</b> → <b>{ultimoResultado.a}</b>
              {" "}(saldo {fmtNum(ultimoResultado.saldo, ultimoResultado.moneda)}{ultimoResultado.vencimiento ? `, vence ${ultimoResultado.vencimiento}` : ""})
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" onClick={() => { setUltimoResultado(null); setVista("historial"); }} className="bg-white/10 hover:bg-white/20 text-white">Ver historial</Button>
            <button onClick={() => setUltimoResultado(null)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {vista === "nuevo" && (<>
      <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
        <p className="text-slate-400 text-sm mb-3">
          Buscá una factura o compra con saldo pendiente y reclasificala a otra cuenta del plan
          (ej: de contado a cuotas, o hacia/desde saldo a favor). El saldo no se divide: se traspasa el documento completo.
        </p>
        <div className="flex gap-2">
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") buscar(); }}
            placeholder="Buscar por número o cliente/proveedor..."
            className="flex-1 bg-slate-950 border-white/10 text-white"
          />
          <Button onClick={buscar} disabled={buscando} className="bg-arandu-blue hover:bg-arandu-blue/80 gap-2">
            <Search className="w-4 h-4" /> Buscar
          </Button>
        </div>
      </div>

      <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-slate-400 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Tipo</th>
              <th className="text-left px-4 py-3">N°</th>
              <th className="text-left px-4 py-3">Entidad</th>
              <th className="text-left px-4 py-3">Cuenta actual</th>
              <th className="text-right px-4 py-3">Saldo pendiente</th>
              <th className="text-right px-4 py-3">Acción</th>
            </tr>
          </thead>
          <tbody>
            {resultados.length === 0 && buscoAlMenosUnaVez && !buscando && (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">Sin documentos con saldo pendiente para esos criterios.</td></tr>
            )}
            {resultados.map(r => (
              <tr key={`${r.tipo_doc}-${r.doc_id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                    r.tipo_doc === "factura" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-amber-500/10 border-amber-500/30 text-amber-300"
                  }`}>
                    {r.tipo_doc === "factura" ? "Factura (cobrar)" : "Compra (pagar)"}
                  </span>
                </td>
                <td className="px-4 py-3 text-white font-mono text-xs">{r.numero || "—"}</td>
                <td className="px-4 py-3 text-white">{r.entidad_nombre || "—"}</td>
                <td className="px-4 py-3 text-slate-300">
                  {r.plan_cuenta_nombre || "Sin cuenta"}
                  {r.cant_traspasos > 0 && (
                    <span title={`Último traspaso: ${r.ultimo_traspaso?.plan_cuenta_anterior_nombre || "sin cuenta"} → ${r.ultimo_traspaso?.plan_cuenta_nuevo_nombre || ""} (${(r.ultimo_traspaso?.fecha || "").slice(0,10)})`}
                      className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30">traspasado{r.cant_traspasos > 1 ? ` ×${r.cant_traspasos}` : ""}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right text-white font-semibold">{fmtNum(r.saldo_pendiente, r.moneda)}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" onClick={() => abrirTraspaso(r)} className="bg-white/10 hover:bg-white/20 text-white gap-1.5">
                    <ArrowRight className="w-3.5 h-3.5" /> Traspasar
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      </>)}

      {/* Modal de confirmación */}
      {vista === "nuevo" && seleccionado && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`bg-arandu-dark-light border border-white/10 rounded-xl w-full max-h-[90vh] overflow-y-auto ${cuotasEdit ? "max-w-2xl" : "max-w-md"}`}
          >
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-white font-semibold">Traspasar saldo</h3>
              <button onClick={() => setSeleccionado(null)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-sm text-slate-300">
                <p><span className="text-slate-500">Documento:</span> {seleccionado.tipo_doc === "factura" ? "Factura" : "Compra"} {seleccionado.numero}</p>
                <p><span className="text-slate-500">Entidad:</span> {seleccionado.entidad_nombre}</p>
                <p><span className="text-slate-500">Saldo:</span> <span className="text-white font-semibold">{fmtNum(seleccionado.saldo_pendiente, seleccionado.moneda)}</span></p>
                <p><span className="text-slate-500">Cuenta actual:</span> {seleccionado.plan_cuenta_nombre || "Sin cuenta"}</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 font-semibold uppercase">Cuenta destino</label>
                <select value={destinoId} onChange={e => setDestinoId(e.target.value)} style={{ colorScheme: "dark" }}
                  className="mt-1 w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                  <option value="">Seleccionar cuenta...</option>
                  {cuentasDestino.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre} ({c.tipo === "cobrar" ? "cobrar" : "pagar"}{c.es_cuotas ? ", a cuotas" : ""}){c.id === seleccionado.plan_cuenta_id ? " — cuenta actual" : ""}</option>
                  ))}
                </select>
                {mismaCuenta && <p className="text-[11px] text-amber-300/80 mt-1">Misma cuenta de origen: se usa para postergar el vencimiento del saldo.</p>}
              </div>
              {destinoEsCuotas && !cuotasEdit && (
                <div>
                  <label className="text-xs text-slate-400 font-semibold uppercase">Cantidad de cuotas (máx. {maxCuotasDestino})</label>
                  <input type="number" min={1} max={maxCuotasDestino} value={numCuotas}
                    onChange={e => setNumCuotas(e.target.value)}
                    className="mt-1 w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                  <p className="text-[11px] text-slate-500 mt-1">El saldo pendiente se reparte en cuotas iguales, una por mes desde el vencimiento de la 1ª cuota. Las cuotas impagas anteriores quedan canceladas.</p>
                  {planPrevio.length > 0 && (
                    <div className="mt-2 max-h-28 overflow-y-auto rounded-lg border border-white/10 divide-y divide-white/5 text-xs">
                      {planPrevio.map(c => (
                        <div key={c.n} className="flex justify-between px-3 py-1 text-slate-300">
                          <span>Cuota {c.n} · {c.f}</span><span className="text-white">{fmtNum(c.monto, seleccionado.moneda)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!cuotasEdit && (
              <div>
                <label className="text-xs text-slate-400 font-semibold uppercase">{destinoEsCuotas ? "Vencimiento de la 1ª cuota" : "Vencimiento"}</label>
                <DateInput value={fechaVenc} onChange={e => setFechaVenc(e.target.value)} style={{ colorScheme: "dark" }}
                  className="mt-1 w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                <p className="text-[11px] text-slate-500 mt-1">
                  Fecha en que vence el saldo en la cuenta destino. Si el documento está en cuotas, se aplica a la próxima cuota impaga. Vacío = no se modifica.
                </p>
              </div>
              )}
              {cuotasEdit && (() => {
                const total = cuotasEdit.reduce((t, c) => t + (Number(c.monto) || 0), 0);
                const dif = Math.round(total) - Math.round(Number(seleccionado.saldo_pendiente) || 0);
                const setC = (i, k, v) => setCuotasEdit(cs => cs.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
                return (
                  <div>
                    <label className="text-xs text-slate-400 font-semibold uppercase">Cuotas pendientes (monto y vencimiento)</label>
                    <div className="mt-2 space-y-2 bg-white/5 rounded-lg p-3">
                      <div className="grid grid-cols-12 gap-2 text-[11px] text-slate-500 px-1">
                        <div className="col-span-1">N°</div><div className="col-span-5">Vencimiento</div><div className="col-span-5">Monto</div><div className="col-span-1" />
                      </div>
                      {cuotasEdit.map((c, i) => (
                        <div key={c.id || `n${i}`} className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-1 text-sm text-slate-300 px-1">{c.numero || "+"}</div>
                          <div className="col-span-5"><DateInput value={c.fecha_vencimiento || ""} onChange={e => setC(i, "fecha_vencimiento", e.target.value)} style={{ colorScheme: "dark" }} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" /></div>
                          <div className="col-span-5"><input type="number" min={0} step="any" value={c.monto ?? ""} onChange={e => setC(i, "monto", e.target.value)} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" /></div>
                          <button type="button" title="Quitar cuota" className="col-span-1 text-red-400 hover:text-red-300" onClick={() => setCuotasEdit(cs => cs.filter((_, j) => j !== i))}><X className="w-4 h-4" /></button>
                        </div>
                      ))}
                      <button type="button" className="text-xs text-blue-300 hover:text-blue-200 flex items-center gap-1"
                        onClick={() => setCuotasEdit(cs => [...cs, { id: null, numero: null, monto: "", fecha_vencimiento: "" }])}>
                        <Plus className="w-3 h-3" /> Agregar cuota
                      </button>
                      <div className="flex justify-between text-xs border-t border-white/10 pt-2">
                        <span className="text-slate-400">Saldo del documento: <b className="text-slate-200">{fmtNum(seleccionado.saldo_pendiente, seleccionado.moneda)}</b></span>
                        <span className={dif === 0 ? "text-emerald-400" : "text-amber-400"}>
                          Total de cuotas: <b>{fmtNum(total, seleccionado.moneda)}</b>{dif !== 0 ? ` (dif. ${fmtNum(dif, seleccionado.moneda)})` : ""}
                        </span>
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">Las cuotas ya pagadas no se modifican. Las cuotas que quites se cancelan.</p>
                  </div>
                );
              })()}
              <div>
                <label className="text-xs text-slate-400 font-semibold uppercase">Notas (opcional)</label>
                <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2}
                  className="mt-1 w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-white text-sm resize-none"
                  placeholder="Motivo del traspaso..." />
              </div>
            </div>
            <div className="p-5 border-t border-white/10 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSeleccionado(null)} className="border-white/10 text-slate-400">Cancelar</Button>
              <Button onClick={confirmarTraspaso} disabled={!puedeConfirmar} className="bg-arandu-blue hover:bg-arandu-blue/80 gap-2">
                <Save className="w-4 h-4" /> {guardando ? "Traspasando..." : "Confirmar traspaso"}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}


// ── Programación de Caja ───────────────────────────────────────────────────────
// Planificación de liquidez a corto plazo: proyecta saldo inicial + ingresos
// programados (facturas por cobrar) − egresos programados (proveedores,
// nómina y gastos fijos, por su fecha de vencimiento). NO ejecuta pagos ni
// genera asientos — solo arma la orden de pago que después se ejecuta como
// siempre en Ventas / Compras / Empleados.
function ProgramacionCajaPanel({ token, logoFiltro }) {
  const headers = { Authorization: `Bearer ${token}` };
  const hoyDate = () => new Date().toISOString().slice(0, 10);
  const enDias = (n) => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
  };

  const [desde, setDesde] = useState(hoyDate());
  const [hasta, setHasta] = useState(enDias(30));
  const [items, setItems] = useState([]);
  const [loadingLista, setLoadingLista] = useState(false);
  const [seleccion, setSeleccion] = useState({}); // key "tipo:ref_id" -> bool
  const [generandoLote, setGenerandoLote] = useState(false);

  const keyDe = (it) => `${it.tipo}:${it.ref_id}`;

  const fetchLista = useCallback(async () => {
    if (!logoFiltro || !desde || !hasta) return;
    setLoadingLista(true);
    try {
      const qs = new URLSearchParams({ desde, hasta, logo_tipo: logoFiltro });
      const r = await fetch(`${API}/admin/programacion-caja/lista?${qs.toString()}`, { headers });
      if (r.ok) {
        const data = await r.json();
        const nuevosItems = data.items || [];
        setItems(nuevosItems);
        // Por defecto, todo lo no excluido viene tildado para imprimir.
        setSeleccion(prev => {
          const nueva = {};
          nuevosItems.forEach(it => {
            nueva[keyDe(it)] = it.excluido ? false : (prev[keyDe(it)] ?? true);
          });
          return nueva;
        });
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "No se pudo cargar la Programación de Caja");
      }
    } catch {
      toast.error("Error al cargar la Programación de Caja");
    } finally {
      setLoadingLista(false);
    }
  }, [logoFiltro, desde, hasta, token]); // eslint-disable-line

  useEffect(() => { fetchLista(); }, [fetchLista]);

  const toggleSeleccion = (it) => {
    setSeleccion(prev => ({ ...prev, [keyDe(it)]: !prev[keyDe(it)] }));
  };

  const toggleExcluir = async (it) => {
    const nuevoExcluido = !it.excluido;
    try {
      const r = await fetch(`${API}/admin/programacion-caja/excluir`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ tipo: it.tipo, ref_id: it.ref_id, excluido: nuevoExcluido, logo_tipo: logoFiltro }),
      });
      if (!r.ok) throw new Error();
      setItems(prev => prev.map(d => keyDe(d) === keyDe(it) ? { ...d, excluido: nuevoExcluido } : d));
      setSeleccion(prev => ({ ...prev, [keyDe(it)]: !nuevoExcluido }));
      toast.success(nuevoExcluido ? "Excluido de la lista" : "Incluido nuevamente en la lista");
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  const bloqueLabel = { egreso_proveedor: "Proveedor", egreso_nomina: "Nómina", egreso_gasto_fijo: "Gasto fijo" };

  // Una fila por beneficiario (y moneda): el total a pagar de cada proveedor /
  // empleado / gasto fijo, no una fila por documento. Cada grupo conserva sus
  // ítems para seleccionar, excluir y generar el lote.
  const grupos = useMemo(() => {
    const map = new Map();
    items.forEach(it => {
      const moneda = it.moneda_original || "PYG";
      const k = `${it.tipo}|${it.beneficiario_id || it.beneficiario}|${moneda}`;
      let g = map.get(k);
      if (!g) {
        g = { key: k, tipo: it.tipo, beneficiario: it.beneficiario, moneda, canal_pago: it.canal_pago || null, items: [] };
        map.set(k, g);
      }
      if (!g.canal_pago && it.canal_pago) g.canal_pago = it.canal_pago;
      g.items.push(it);
    });
    return Array.from(map.values()).map(g => {
      const venc = g.items.map(i => i.fecha_vencimiento).filter(Boolean).sort();
      const activos = g.items.filter(i => !i.excluido);
      return {
        ...g,
        total: g.items.reduce((s, i) => s + (i.monto_original || 0), 0),
        totalSel: g.items.reduce((s, i) => s + ((!i.excluido && seleccion[keyDe(i)]) ? (i.monto_original || 0) : 0), 0),
        vencDesde: venc[0] || "",
        vencHasta: venc[venc.length - 1] || "",
        excluido: activos.length === 0,
        todosSel: activos.length > 0 && activos.every(i => seleccion[keyDe(i)]),
        algunoSel: activos.some(i => seleccion[keyDe(i)]),
      };
    }).sort((a, b) => (a.vencDesde || "").localeCompare(b.vencDesde || "") || (a.beneficiario || "").localeCompare(b.beneficiario || ""));
  }, [items, seleccion]); // eslint-disable-line

  const [gruposAbiertos, setGruposAbiertos] = useState({});

  const toggleSeleccionGrupo = (g) => {
    const marcar = !g.todosSel;
    setSeleccion(prev => {
      const nueva = { ...prev };
      g.items.forEach(i => { if (!i.excluido) nueva[keyDe(i)] = marcar; });
      return nueva;
    });
  };

  const toggleExcluirGrupo = async (g) => {
    const nuevoExcluido = !g.excluido;
    try {
      await Promise.all(g.items.map(async (it) => {
        if (!!it.excluido === nuevoExcluido) return;
        const r = await fetch(`${API}/admin/programacion-caja/excluir`, {
          method: "PATCH",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ tipo: it.tipo, ref_id: it.ref_id, excluido: nuevoExcluido, logo_tipo: logoFiltro }),
        });
        if (!r.ok) throw new Error();
      }));
      const keys = new Set(g.items.map(keyDe));
      setItems(prev => prev.map(d => keys.has(keyDe(d)) ? { ...d, excluido: nuevoExcluido } : d));
      setSeleccion(prev => {
        const nueva = { ...prev };
        keys.forEach(k => { nueva[k] = !nuevoExcluido; });
        return nueva;
      });
      toast.success(nuevoExcluido ? "Excluido de la lista" : "Incluido nuevamente en la lista");
    } catch {
      toast.error("No se pudo actualizar");
      fetchLista();
    }
  };

  // Totales de lo SELECCIONADO (checkbox tildado y no excluido), separados por moneda.
  const totalesSeleccion = useMemo(() => {
    const totales = {};
    items.forEach(it => {
      if (it.excluido || !seleccion[keyDe(it)]) return;
      const moneda = it.moneda_original || "PYG";
      totales[moneda] = (totales[moneda] || 0) + (it.monto_original || 0);
    });
    return totales;
  }, [items, seleccion]); // eslint-disable-line

  const generarLote = async () => {
    const seleccionados = items
      .filter(it => !it.excluido && seleccion[keyDe(it)])
      .map(it => ({ tipo: it.tipo, ref_id: it.ref_id }));
    if (!seleccionados.length) { toast.error("Seleccioná al menos un gasto para imprimir"); return; }
    setGenerandoLote(true);
    try {
      const r = await fetch(`${API}/admin/programacion-caja/generar-lote`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ items: seleccionados, logo_tipo: logoFiltro, desde, hasta }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || "No se pudo generar el lote"); }
      const data = await r.json();
      imprimirLote(data);
      fetchLista();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGenerandoLote(false);
    }
  };

  const imprimirLote = (data) => {
    // Reporte agrupado por beneficiario: columna GS y columna USD por
    // separado (nunca se mezcla todo convertido a una sola moneda), con el
    // canal de pago cuando está disponible (proveedores).
    const monedas = Array.from(new Set(
      data.resumen_beneficiarios.flatMap(f => Object.keys(f.montos_por_moneda))
    ));
    // GS y USD siempre primero si aparecen, en ese orden; después cualquier otra.
    const orden = ["PYG", "USD", ...monedas.filter(m => m !== "PYG" && m !== "USD")];
    const monedasOrdenadas = orden.filter(m => monedas.includes(m));
    const colLabel = { PYG: "GUARANÍES (PYG)", USD: "DÓLARES (USD)" };

    const filaBeneficiario = (f) => `
      <tr>
        <td>${f.beneficiario}</td>
        ${monedasOrdenadas.map(m => `<td style="text-align:right">${fmtNum(f.montos_por_moneda[m] || 0, m)}</td>`).join("")}
        <td>${f.canal_pago || "—"}</td>
      </tr>`;
    const totalesPorMoneda = monedasOrdenadas.map(m =>
      data.resumen_beneficiarios.reduce((s, f) => s + (f.montos_por_moneda[m] || 0), 0)
    );
    const html = `
      <html><head><title>Programación de Pagos ${data.lote}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; color: #111; padding: 24px; }
        h1 { font-size: 16px; margin-bottom: 4px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
        th, td { border: 1px solid #ccc; padding: 5px 7px; font-size: 11px; }
        th { background: #f0f0f0; text-align: left; }
        tfoot td { font-weight: bold; background: #f7f7f7; }
        .resumen { display: flex; gap: 24px; margin: 10px 0 18px; flex-wrap: wrap; }
        .resumen div { border: 1px solid #ccc; padding: 8px 12px; border-radius: 4px; }
        .firma { margin-top: 50px; display: flex; justify-content: space-between; }
        .firma div { width: 45%; border-top: 1px solid #333; text-align: center; padding-top: 6px; font-size: 11px; }
      </style></head><body>
        <h1>Programación de Pagos a Proveedores — Lote ${data.lote}</h1>
        <p>Generado: ${new Date(data.fecha_generacion).toLocaleString("es-PY")} · Usuario: ${data.usuario || "-"}</p>
        <table>
          <thead><tr>
            <th>Beneficiario</th>
            ${monedasOrdenadas.map(m => `<th style="text-align:right">${colLabel[m] || m}</th>`).join("")}
            <th>Canal de pago</th>
          </tr></thead>
          <tbody>${data.resumen_beneficiarios.map(filaBeneficiario).join("")}</tbody>
          <tfoot><tr>
            <td>TOTALES</td>
            ${totalesPorMoneda.map((t, i) => `<td style="text-align:right">${fmtNum(t, monedasOrdenadas[i])}</td>`).join("")}
            <td>-</td>
          </tr></tfoot>
        </table>
        <h1 style="margin-top:18px">Detalle por documento</h1>
        <table>
          <thead><tr><th>Tipo</th><th>Beneficiario</th><th>Detalle</th><th>Vencimiento</th><th style="text-align:right">Monto</th></tr></thead>
          <tbody>${[...(data.proveedores || []), ...(data.nomina || []), ...(data.gastos_fijos || [])]
            .sort((a, b) => (a.fecha_vencimiento || "").localeCompare(b.fecha_vencimiento || ""))
            .map(it => `<tr><td>${bloqueLabel[it.tipo] || it.tipo}</td><td>${it.beneficiario || ""}</td><td>${it.descripcion || ""}</td><td>${it.fecha_vencimiento || ""}</td><td style="text-align:right">${fmtNum(it.monto_original || 0, it.moneda_original || "PYG")}</td></tr>`).join("")}</tbody>
        </table>
        <div class="resumen">
          ${monedasOrdenadas.map((m, i) => `<div><b>Total ${colLabel[m] || m}</b><br/>${fmtNum(totalesPorMoneda[i], m)}</div>`).join("")}
          <div><b>Cantidad de beneficiarios</b><br/>${data.resumen_beneficiarios.length}</div>
        </div>
        <div class="firma">
          <div>Elaborado por</div>
          <div>Autorizado por (Gerencia Financiera)</div>
        </div>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("El navegador bloqueó la ventana de impresión"); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const exportarCSV = () => {
    const seleccionados = items.filter(it => !it.excluido && seleccion[keyDe(it)]);
    if (!seleccionados.length) { toast.error("No hay gastos seleccionados para exportar"); return; }
    const filas = [["Tipo", "Beneficiario", "Documentos", "Vencimiento", "Moneda", "Total a pagar", "Canal de pago", "Datos bancarios"]];
    // Una fila por beneficiario y moneda, con el total de lo seleccionado.
    const porGrupo = new Map();
    seleccionados.forEach(it => {
      const moneda = it.moneda_original || "PYG";
      const k = `${it.tipo}|${it.beneficiario_id || it.beneficiario}|${moneda}`;
      const g = porGrupo.get(k) || { it, moneda, total: 0, docs: 0, venc: [] };
      g.total += it.monto_original || 0;
      g.docs += 1;
      if (it.fecha_vencimiento) g.venc.push(it.fecha_vencimiento);
      porGrupo.set(k, g);
    });
    porGrupo.forEach(g => {
      g.venc.sort();
      filas.push([
        bloqueLabel[g.it.tipo] || g.it.tipo,
        g.it.beneficiario || "-",
        g.docs,
        g.venc[0] || "-",
        g.moneda,
        g.total,
        g.it.canal_pago || "",
        g.it.datos_bancarios || "",
      ]);
    });
    const csv = filas.map(f => f.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `programacion_pagos_${hoyDate()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
        <p className="text-slate-400 text-sm mb-3">
          Gastos a pagar (proveedores, nómina y gastos fijos) con vencimiento dentro del período
          elegido. Tildá los que vas a pagar ahora — el total seleccionado se actualiza abajo, por
          moneda — y generá la orden de pago/impresión con lo tildado. No ejecuta ningún pago real.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-slate-500 text-xs font-medium">Desde</label>
            <DateInput value={desde} onChange={e => setDesde(e.target.value)}
              className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-slate-500 text-xs font-medium">Hasta</label>
            <DateInput value={hasta} onChange={e => setHasta(e.target.value)}
              className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
          </div>
          <Button onClick={fetchLista} disabled={loadingLista} className="bg-arandu-blue hover:bg-arandu-blue/80 gap-2">
            {loadingLista ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />} Actualizar
          </Button>
        </div>
      </div>

      <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
        {loadingLista ? (
          <p className="text-slate-500 text-sm py-8 text-center">Cargando gastos a pagar...</p>
        ) : items.length === 0 ? (
          <p className="text-slate-500 text-sm py-8 text-center">Sin gastos a pagar en este período.</p>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-slate-400 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-3 w-8"></th>
                  <th className="text-left px-3 py-3">Tipo</th>
                  <th className="text-left px-3 py-3">Beneficiario</th>
                  <th className="text-left px-3 py-3">Detalle</th>
                  <th className="text-left px-3 py-3">Vencimiento</th>
                  <th className="text-right px-3 py-3">Monto</th>
                  <th className="text-left px-3 py-3">Canal de pago</th>
                  <th className="text-center px-3 py-3">Excluir</th>
                </tr>
              </thead>
              <tbody>
                {grupos.map(g => {
                  const multiple = g.items.length > 1;
                  const abierto = !!gruposAbiertos[g.key];
                  const detalle = multiple
                    ? `${g.items.length} ${g.tipo === "egreso_proveedor" ? "compras" : g.tipo === "egreso_nomina" ? "conceptos" : "gastos"}`
                    : (g.items[0].descripcion + (g.items[0].estimado ? " (estimado)" : ""));
                  return (
                    <React.Fragment key={g.key}>
                      <tr className={`border-t border-white/5 ${g.excluido ? "opacity-40" : ""}`}>
                        <td className="px-3 py-2">
                          <input type="checkbox" checked={g.todosSel} disabled={g.excluido}
                            ref={el => { if (el) el.indeterminate = !g.todosSel && g.algunoSel; }}
                            onChange={() => toggleSeleccionGrupo(g)} className="accent-arandu-blue" />
                        </td>
                        <td className="px-3 py-2 text-slate-400">{bloqueLabel[g.tipo] || g.tipo}</td>
                        <td className="px-3 py-2 text-white">{g.beneficiario}</td>
                        <td className="px-3 py-2 text-slate-400">
                          {multiple ? (
                            <button type="button" onClick={() => setGruposAbiertos(p => ({ ...p, [g.key]: !p[g.key] }))}
                              className="text-slate-400 hover:text-white underline decoration-dotted">
                              {abierto ? "▾" : "▸"} {detalle}
                            </button>
                          ) : detalle}
                        </td>
                        <td className="px-3 py-2 text-slate-400">
                          {g.vencDesde}{g.vencHasta && g.vencHasta !== g.vencDesde ? ` → ${g.vencHasta}` : ""}
                        </td>
                        <td className="px-3 py-2 text-right text-white font-semibold">{fmtNum(g.totalSel, g.moneda)}</td>
                        <td className="px-3 py-2 text-slate-400">{g.canal_pago || "—"}</td>
                        <td className="px-3 py-2 text-center">
                          <button onClick={() => toggleExcluirGrupo(g)}
                            className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${g.excluido ? "bg-slate-500/10 border-slate-500/30 text-slate-400" : "bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20"}`}>
                            {g.excluido ? "Incluir" : "Quitar"}
                          </button>
                        </td>
                      </tr>
                      {multiple && abierto && g.items.map(it => (
                        <tr key={keyDe(it)} className={`bg-white/[0.02] text-xs ${it.excluido ? "opacity-40" : ""}`}>
                          <td className="px-3 py-1.5">
                            <input type="checkbox" checked={!!seleccion[keyDe(it)]} disabled={it.excluido}
                              onChange={() => toggleSeleccion(it)} className="accent-arandu-blue" />
                          </td>
                          <td></td>
                          <td></td>
                          <td className="px-3 py-1.5 text-slate-500">{it.descripcion}</td>
                          <td className="px-3 py-1.5 text-slate-500">{it.fecha_vencimiento}{it.vencido ? " · vencido" : ""}</td>
                          <td className="px-3 py-1.5 text-right text-slate-300">{fmtNum(it.monto_original, it.moneda_original || "PYG")}</td>
                          <td colSpan={2}></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-white/[0.04] border-t border-white/10">
              <div className="flex flex-wrap gap-4 text-xs text-slate-400">
                <span>Total seleccionado:</span>
                {Object.keys(totalesSeleccion).length === 0 ? (
                  <span className="text-slate-500">—</span>
                ) : Object.entries(totalesSeleccion).map(([moneda, monto]) => (
                  <span key={moneda} className="text-white font-semibold">{fmtNum(monto, moneda)}</span>
                ))}
              </div>
              <div className="flex gap-2">
                <Button onClick={exportarCSV} variant="outline" className="border-white/10 text-slate-300 gap-2 text-xs h-8">
                  <FileText className="w-3.5 h-3.5" /> Exportar CSV
                </Button>
                <Button onClick={generarLote} disabled={generandoLote} className="bg-emerald-600 hover:bg-emerald-500 gap-2 text-xs h-8">
                  {generandoLote ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />} Imprimir / Exportar Programación de Pagos
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ── Página principal ──────────────────────────────────────────────────────────
export default function FinancieroPage() {
  const { user, token, hasPermission, activeEmpresaPropia } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  // Permissions
  const puedeVer          = user?.role in { admin: 1, gerente: 1, super_admin: 1 } || hasPermission("transferencias.ver");
  const puedeCrear        = user?.role in { admin: 1, gerente: 1, super_admin: 1 } || hasPermission("transferencias.crear");
  const puedeEditar       = user?.role in { admin: 1, gerente: 1, super_admin: 1 } || hasPermission("transferencias.editar");
  const puedeEliminar     = user?.role in { admin: 1, gerente: 1, super_admin: 1 } || hasPermission("transferencias.eliminar");
  const puedeHistorial    = user?.role in { admin: 1, gerente: 1, super_admin: 1 } || hasPermission("transferencias.historial");
  const puedeGestionarTransferencias = user?.role in { admin: 1, gerente: 1, super_admin: 1 } || hasPermission("configuracion_financiero.gestionar_acceso_transferencias");
  const puedeGestionarBancos         = user?.role in { admin: 1, gerente: 1, super_admin: 1 } || hasPermission("configuracion_financiero.gestionar_acceso_bancos");
  const puedeGestionarFechas         = user?.role in { admin: 1, gerente: 1, super_admin: 1 } || hasPermission("configuracion_financiero.gestionar_fechas_movimientos");
  const puedeGestionar               = puedeGestionarTransferencias || puedeGestionarBancos || puedeGestionarFechas;
  const [showFechasMovimientosModal, setShowFechasMovimientosModal] = useState(false);
  const isAdminOrGerente  = ["admin", "gerente", "super_admin"].includes(user?.role);
  const puedeEditarTC     = isAdminOrGerente || hasPermission("transferencias.editar_tc");
  const canVerCuentas     = isAdminOrGerente || hasPermission("bancos.ver");
  const puedeVerCheques  = isAdminOrGerente || hasPermission("cheques.ver");
  const puedeUsarCheque  = puedeVerCheques && (isAdminOrGerente || hasPermission("cheques.emitir"));
  // Garantías es un módulo NO obligatorio: no se agrega el prefijo isAdminOrGerente ||
  // para que hasPermission() aplique correctamente el gating por módulo habilitado.
  const puedeVerGarantias = hasPermission("garantias.ver");
  const puedeTraspasarSaldos = isAdminOrGerente || hasPermission("plan_cuentas.traspasar_saldos");
  const puedeVerProgramacionCaja = isAdminOrGerente || hasPermission("programacion_caja.ver");
  const canCreateCuenta   = isAdminOrGerente || hasPermission("bancos.crear");
  const canEditCuenta     = isAdminOrGerente || hasPermission("bancos.editar");
  const canDeleteCuenta   = isAdminOrGerente || hasPermission("bancos.eliminar");

  const [activeTab, setActiveTab]     = useState("transferencias");
  const [cuentas, setCuentas]         = useState([]);
  const [saldos, setSaldos]           = useState({});
  const [transfers, setTransfers]     = useState([]);
  const [cuentasAcceso, setCuentasAcceso] = useState({});
  const [loading, setLoading]         = useState(true);

  // Filters
  const [filterDesde, setFilterDesde] = useState(hoy);
  const [filterHasta, setFilterHasta] = useState(hoy);
  const [filterCuenta, setFilterCuenta] = useState("");
  const [searchText, setSearchText]   = useState("");

  // Modals
  const [showTransferModal, setShowTransferModal]   = useState(false);
  const [showEditModal, setShowEditModal]           = useState(false);
  const [showAccesoModal, setShowAccesoModal]       = useState(false);
  const [showAccesoBancosModal, setShowAccesoBancosModal] = useState(false);
  const [editingTransfer, setEditingTransfer]       = useState(null);
  const [deletingId, setDeletingId]                 = useState(null);
  const [printTransfer, setPrintTransfer]           = useState(null);
  const [showPrintReporte, setShowPrintReporte]     = useState(false);

  // Cuentas tab state
  const emptyFormCuenta = { nombre: "", banco: "", numero_cuenta: "", moneda: "PYG", saldo_inicial: "", saldo_inicial_fecha: "", es_predeterminada: false, permite_saldo_negativo: false, notas: "" };
  const [showCuentaForm, setShowCuentaForm]         = useState(false);
  const [editingCuentaId, setEditingCuentaId]       = useState(null);
  const [formDataCuenta, setFormDataCuenta]         = useState(emptyFormCuenta);

  const logoFiltro = activeEmpresaPropia?.slug || "";

  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const logoParam = logoFiltro ? `?logo_tipo=${logoFiltro}` : "";

      // Parámetros para transferencias (logo + fechas)
      const tparams = new URLSearchParams();
      if (logoFiltro) tparams.append("logo_tipo", logoFiltro);
      if (filterDesde) tparams.append("desde", filterDesde);
      if (filterHasta) tparams.append("hasta", filterHasta);
      const tqp = tparams.toString() ? `?${tparams.toString()}` : "";

      // Cuentas con saldo + cotizaciones de hoy (para auto-fill TC en transferencias)
      const [cuentasRes, transfersRes, cotHoyRes] = await Promise.all([
        fetch(`${API}/admin/cuentas-bancarias/saldos${logoParam}`, { headers }),
        fetch(`${API}/admin/transferencias${tqp}`, { headers }),
        fetch(`${API}/admin/cotizaciones/hoy${logoParam}`, { headers }),
      ]);

      if (cuentasRes.ok) {
        const data = await cuentasRes.json();
        setCuentas(data);
        const s = {};
        data.forEach(c => { s[c.id] = c.saldo_actual ?? 0; });
        setSaldos(s);
      }

      if (transfersRes.ok) {
        setTransfers(await transfersRes.json());
      }

      if (cotHoyRes.ok) {
        const d = await cotHoyRes.json();
        setCotHoy(d.cotizaciones || {});
      }

      // Acceso a cuentas del usuario (solo para no-admin)
      if (!isAdminOrGerente && user?.id) {
        const accesoRes = await fetch(`${API}/admin/usuarios/${user.id}/cuentas-acceso`, { headers });
        if (accesoRes.ok) {
          const d = await accesoRes.json();
          setCuentasAcceso(d.cuentas_acceso || {});
        }
      }
    } finally {
      setLoading(false);
    }
  }, [logoFiltro, user?.id, isAdminOrGerente, filterDesde, filterHasta]); // eslint-disable-line

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    const pagoId = searchParams.get("pago_id");
    const provId = searchParams.get("proveedor_id");
    if (tab === "pagos_proveedores" || pagoId || provId) setActiveTab("pagos_proveedores");
    else if (tab && ["transferencias","cuentas","cotizaciones","cheques","prestamos","garantias","traspaso_saldos","programacion_caja","configuracion"].includes(tab)) setActiveTab(tab);
  }, [searchParams]);

  // Filtrar cuentas visibles
  const cuentasVisibles = isAdminOrGerente
    ? cuentas
    : cuentas.filter(c => cuentasAcceso[c.id]?.ver === true);

  // Cuentas para el selector de transferencia
  const cuentasParaTransfer = cuentasVisibles;

  const renderCuentasPorMoneda = (lista = cuentasVisibles) => {
    const labels = { PYG: "Cuenta bancaria Guaraníes (PYG)", USD: "Cuenta bancaria Dólares (USD)", BRL: "Cuenta bancaria Reales (BRL)", ARS: "Cuenta bancaria Pesos (ARS)" };
    const activas = (lista || []).filter(c => c.activa !== false);
    const monedasBase = ["PYG", "USD", "BRL", "ARS"];
    const monedasExtra = Array.from(new Set(activas.map(c => c.moneda || "PYG"))).filter(m => !monedasBase.includes(m));
    return [...monedasBase, ...monedasExtra].map(moneda => {
      const grupo = activas.filter(c => (c.moneda || "PYG") === moneda);
      if (!grupo.length) return null;
      return (
        <optgroup key={moneda} label={labels[moneda] || moneda}>
          {grupo.map(c => (
            <option key={c.id} value={c.id}>
              {c.nombre}{c.banco ? ` · ${c.banco}` : ""} ({c.moneda || "PYG"}){c.es_predeterminada ? " ★" : ""}
            </option>
          ))}
        </optgroup>
      );
    });
  };

  function canVerSaldo(cuentaId) {
    if (isAdminOrGerente) return true;
    return cuentasAcceso[cuentaId]?.ver_saldo === true;
  }

  // Filtrar historial
  const transfersFiltradas = transfers.filter(t => {
    if (filterDesde && t.fecha < filterDesde) return false;
    if (filterHasta && t.fecha > filterHasta) return false;
    if (filterCuenta && t.de_cuenta_id !== filterCuenta && t.a_cuenta_id !== filterCuenta) return false;
    if (searchText) {
      const q = searchText.toLowerCase();
      return (t.numero || "").toLowerCase().includes(q)
        || (t.descripcion || "").toLowerCase().includes(q)
        || (t.de_cuenta_nombre || "").toLowerCase().includes(q)
        || (t.a_cuenta_nombre || "").toLowerCase().includes(q)
        || (t.creado_por_nombre || "").toLowerCase().includes(q);
    }
    return true;
  });

  async function handleCreateTransfer(data) {
    const res = await fetch(`${API}/admin/transferencias`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (res.ok) {
      toast.success("Transferencia registrada");
      setShowTransferModal(false);
      await loadData();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al registrar la transferencia");
    }
  }

  async function handleEditTransfer(data) {
    const res = await fetch(`${API}/admin/transferencias/${editingTransfer.id}`, {
      method: "PUT",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (res.ok) {
      toast.success("Transferencia actualizada");
      setShowEditModal(false);
      setEditingTransfer(null);
      await loadData();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al actualizar");
    }
  }

  async function handleDelete(id) {
    if (deletingId !== id) { setDeletingId(id); return; }
    const res = await fetch(`${API}/admin/transferencias/${id}`, {
      method: "DELETE", headers,
    });
    if (res.ok) {
      toast.success("Transferencia eliminada");
      setDeletingId(null);
      await loadData();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al eliminar");
    }
  }

  // ── Cuentas tab handlers ──────────────────────────────────────────────────
  function openNewCuenta() {
    if (!activeEmpresaPropia?.slug) { toast.error("Seleccioná una empresa propia primero"); return; }
    setEditingCuentaId(null);
    setFormDataCuenta(emptyFormCuenta);
    setShowCuentaForm(true);
  }
  function openEditCuenta(c) {
    setEditingCuentaId(c.id);
    setFormDataCuenta({
      nombre: c.nombre || "", banco: c.banco || "", numero_cuenta: c.numero_cuenta || "",
      moneda: c.moneda || "PYG",
      saldo_inicial: c.saldo_inicial != null ? String(c.saldo_inicial) : "",
      saldo_inicial_fecha: c.saldo_inicial_fecha || "",
      es_predeterminada: !!c.es_predeterminada, permite_saldo_negativo: !!c.permite_saldo_negativo,
      notas: c.notas || "",
    });
    setShowCuentaForm(true);
  }
  async function handleSaveCuenta(e) {
    e.preventDefault();
    if (!formDataCuenta.nombre) { toast.error("Nombre requerido"); return; }
    const slug = activeEmpresaPropia?.slug;
    if (!slug) { toast.error("Seleccioná una empresa propia"); return; }
    try {
      const payload = { ...formDataCuenta, saldo_inicial: parseFloat(formDataCuenta.saldo_inicial) || 0, logo_tipo: slug };
      const url = editingCuentaId ? `${API}/admin/cuentas-bancarias/${editingCuentaId}` : `${API}/admin/cuentas-bancarias`;
      const res = await fetch(url, { method: editingCuentaId ? "PUT" : "POST", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || `Error ${res.status}`); }
      toast.success(editingCuentaId ? "Cuenta actualizada" : "Cuenta creada");
      setShowCuentaForm(false);
      await loadData();
    } catch (err) { toast.error(err.message || "Error al guardar"); }
  }
  async function handleDeleteCuenta(c) {
    if (!window.confirm(`¿Eliminar "${c.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      const res = await fetch(`${API}/admin/cuentas-bancarias/${c.id}`, { method: "DELETE", headers });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || `Error ${res.status}`); }
      toast.success("Cuenta eliminada");
      await loadData();
    } catch (err) { toast.error(err.message || "Error al eliminar"); }
  }

  const fmtSaldo = (n, moneda = "PYG") => {
    const raw = Number(n);
    const num = (n === null || n === undefined || isNaN(raw)) ? 0 : raw;
    if (moneda === "USD") return `$ ${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (moneda === "BRL") return `R$ ${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `₲ ${Math.round(num).toLocaleString("es-PY")}`;
  };

  // ── Cotizaciones state ─────────────────────────────────────────────────────
  const tieneBRL = activeEmpresaPropia?.monedas_habilitadas?.includes("BRL");
  const canVerCotiz     = isAdminOrGerente || hasPermission("cotizaciones.ver");
  const canGestCotiz    = isAdminOrGerente || hasPermission("cotizaciones.gestionar");

  // Pagos a proveedores
  const canVerPagos     = isAdminOrGerente || hasPermission("pagos_proveedores.ver");
  const canCrearPago    = isAdminOrGerente || hasPermission("pagos_proveedores.crear");
  const canEditarPago   = isAdminOrGerente || hasPermission("pagos_proveedores.editar");
  const canEliminarPago = isAdminOrGerente || hasPermission("pagos_proveedores.eliminar");
  const canEditTcDoc    = isAdminOrGerente || hasPermission("cotizaciones.editar_en_compras");

  const [cotHoy, setCotHoy]                   = useState({}); // { USD: {...}, BRL: {...} }
  const [cotHistorial, setCotHistorial]       = useState([]);
  const [cotLoading, setCotLoading]           = useState(false);
  const [cotSyncing, setCotSyncing]           = useState(false);
  const [cotFiltroDesde, setCotFiltroDesde]   = useState("");
  const [cotFiltroHasta, setCotFiltroHasta]   = useState("");
  const [cotFiltroMoneda, setCotFiltroMoneda] = useState("");
  const [showManualForm, setShowManualForm]   = useState(false);
  const [manualForm, setManualForm]           = useState({ moneda: "USD", fecha: hoy(), tc_empresa: "", tc_iva: "", notas: "" });
  const [manualSaving, setManualSaving]       = useState(false);
  const [cotTcEdit, setCotTcEdit]             = useState({}); // { USD: "6200" } — TC Empresa en edición
  const [cotTcSaving, setCotTcSaving]         = useState({}); // { USD: true }
  const [cotTcIvaEdit, setCotTcIvaEdit]       = useState({}); // { USD: "6100" } — TC IVA en edición
  const [cotTcIvaSaving, setCotTcIvaSaving]   = useState({}); // { USD: true }

  // ── Pagos a Proveedores state ─────────────────────────────────────────────
  const emptyPagoProvForm = () => ({
    proveedor_id: "", proveedor_nombre: "", cuenta_id: "", cuenta_nombre: "",
    cuenta_moneda: "PYG", tipo_cambio: "", fecha_pago: hoy(),
    recibo_numero: "", notas: "", compras_pagos: [], sin_retencion: false,
  });
  const [listaProveedores, setListaProveedores]           = useState([]);
  const [todosPagosProveedores, setTodosPagosProveedores] = useState([]);
  const [loadingTodosPagos, setLoadingTodosPagos]         = useState(false);
  const [showPagoProvForm, setShowPagoProvForm]           = useState(false);
  const [pagoProvForm, setPagoProvForm]                   = useState(emptyPagoProvForm());
  const [editingPagoProv, setEditingPagoProv]             = useState(null);
  const [comprasProvList, setComprasProvList]             = useState([]);
  const [loadingComprasProv, setLoadingComprasProv]       = useState(false);
  const [cuotasPorCompra, setCuotasPorCompra]             = useState({}); // { compra_id: [cuota,...] }
  const [loadingCuotasCompra, setLoadingCuotasCompra]     = useState({}); // { compra_id: true }
  const [selectedPagoView, setSelectedPagoView]           = useState(null);
  const [showPagoProvDoc, setShowPagoProvDoc]             = useState(null); // pago object (comprobante imprimible)
  const [selectedCompraDesdePago, setSelectedCompraDesdePago] = useState(null);
  const [loadingCompraDesdePago, setLoadingCompraDesdePago] = useState(false);
  const [mostrarEliminadasPagosProv, setMostrarEliminadasPagosProv] = useState(false);
  const [reciboPagoProvForm, setReciboPagoProvForm] = useState("");
  const [savingReciboPagoProv, setSavingReciboPagoProv] = useState(false);
  const [pagoProvMedio, setPagoProvMedio] = useState("transferencia"); // "transferencia" | "cheque"
  const [pagoProvChips, setPagoProvChips]   = useState([]);
  const [pagoProvInput, setPagoProvInput]   = useState("");
  // Filtro de período para el tab (independiente del resto de tabs)
  const [ppFiltroTipo, setPpFiltroTipo] = useState("todos"); // "todos"|"dia"|"mes"|"anio"
  const ppMesDefault = new Date().toISOString().slice(0,7);
  const [ppFiltroMes, setPpFiltroMes]   = useState(ppMesDefault);
  const [ppFiltroAnio, setPpFiltroAnio] = useState(new Date().getFullYear().toString());
  const [ppFiltroDia, setPpFiltroDia]   = useState(hoyFin());
  const ppCanViewDeleted = isAdminOrGerente || hasPermission("pagos_proveedores.eliminar");

  const compraPagoLabel = (c) => {
    if (!c) return "Compra";
    const fac = c.numero_factura ? `Fac. ${c.numero_factura}` : "Sin factura";
    const fecha = c.fecha ? ` · ${c.fecha}` : "";
    return `${fac}${fecha}`;
  };

  const pagoProveedorTieneCompraCredito = (pago) =>
    (pago?.compras_detalle || []).some(c => c.tipo_pago === "credito");

  // Sugerir el tipo de cambio al pagar compras en moneda distinta a la de la cuenta
  // (USD/BRL ↔ PYG): se completa solo con el TC de la fecha de pago (empresa activa).
  const _pvPrimeraCompra = comprasProvList.find(c => c.id === pagoProvForm.compras_pagos?.[0]?.compra_id);
  const _pvMonedaC = _pvPrimeraCompra?.moneda || null;
  const _pvNecesitaTc = !!(pagoProvForm.cuenta_id && _pvMonedaC && _pvMonedaC !== pagoProvForm.cuenta_moneda);
  const _pvMonedaExt = _pvMonedaC === "PYG" ? pagoProvForm.cuenta_moneda : _pvMonedaC;
  useEffect(() => {
    if (!_pvNecesitaTc || pagoProvForm.tipo_cambio || !["USD", "BRL"].includes(_pvMonedaExt)) return;
    let vivo = true;
    const lq = activeEmpresaPropia?.slug ? `&logo_tipo=${encodeURIComponent(activeEmpresaPropia.slug)}` : "";
    const fecha = pagoProvForm.fecha_pago || new Date().toISOString().slice(0, 10);
    fetch(`${API}/admin/cotizaciones/${_pvMonedaExt.toLowerCase()}?fecha=${fecha}${lq}`, { headers })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        const tc = d && (d.tipo_cambio_sugerido || d.venta);
        if (vivo && tc) setPagoProvForm(p => (p.tipo_cambio ? p : { ...p, tipo_cambio: String(tc) }));
      })
      .catch(() => {});
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_pvNecesitaTc, _pvMonedaExt, pagoProvForm.fecha_pago, pagoProvForm.tipo_cambio]);

  const pagoProvFormTieneCompraCredito = () =>
    (pagoProvForm.compras_pagos || []).some(cp => {
      const compra = comprasProvList.find(c => c.id === cp.compra_id);
      return compra?.tipo_pago === "credito";
    });

  const openCompraDesdePago = async (compra) => {
    if (!compra?.id || compra.eliminada || compra.existe === false) return;
    setLoadingCompraDesdePago(true);
    setSelectedCompraDesdePago(compra);
    try {
      const res = await fetch(`${API}/admin/compras/${compra.id}`, { headers });
      if (res.ok) setSelectedCompraDesdePago(await res.json());
      else toast.error("No se pudo abrir la compra");
    } catch {
      toast.error("Error al abrir la compra");
    } finally {
      setLoadingCompraDesdePago(false);
    }
  };

  const fetchTodosPagosProveedores = useCallback(async () => {
    if (!canVerPagos) { setTodosPagosProveedores([]); setListaProveedores([]); return; }
    setLoadingTodosPagos(true);
    try {
      const logo = activeEmpresaPropia?.slug;
      const provParams = new URLSearchParams({ es_proveedor: "true" });
      if (logo) provParams.set("logo_tipo", logo);
      const pagosParams = new URLSearchParams();
      if (logo) pagosParams.set("logo_tipo", logo);
      if (mostrarEliminadasPagosProv) pagosParams.set("incluir_eliminadas", "true");
      const [pagosRes, provRes] = await Promise.all([
        fetch(`${API}/admin/pagos-proveedores?${pagosParams}`, { headers }),
        fetch(`${API}/admin/entidades?${provParams}`, { headers }),
      ]);
      if (pagosRes.ok) {
        const data = await pagosRes.json();
        setTodosPagosProveedores(mostrarEliminadasPagosProv ? data.filter(p => p.eliminada) : data.filter(p => !p.eliminada));
      }
      if (provRes.ok) setListaProveedores(await provRes.json());
    } catch (e) {}
    finally { setLoadingTodosPagos(false); }
  }, [canVerPagos, activeEmpresaPropia?.slug, mostrarEliminadasPagosProv]); // eslint-disable-line

  useEffect(() => {
    if (activeTab === "pagos_proveedores") fetchTodosPagosProveedores();
  }, [activeTab, fetchTodosPagosProveedores]);

  useEffect(() => {
    const pagoId = searchParams.get("pago_id");
    if (!pagoId || todosPagosProveedores.length === 0) return;
    const pago = todosPagosProveedores.find(p => p.id === pagoId);
    if (!pago) return;
    setSelectedPagoView(pago);
    const next = new URLSearchParams(searchParams);
    next.delete("pago_id");
    if (next.get("tab") === "pagos_proveedores") next.delete("tab");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, todosPagosProveedores]);

  useEffect(() => {
    setReciboPagoProvForm("");
  }, [selectedPagoView?.id]);

  // Deep-link desde Compras: "Pagar en Pagos Proveedores" (?proveedor_id=&compra_id=)
  // Abre directamente el formulario de Nuevo pago con el proveedor y la compra preseleccionados.
  const [pendingCompraPreselect, setPendingCompraPreselect] = useState(null);
  useEffect(() => {
    const provId = searchParams.get("proveedor_id");
    if (!provId || listaProveedores.length === 0) return;
    const prov = listaProveedores.find(p => p.id === provId);
    if (!prov) return;
    const compraId = searchParams.get("compra_id");
    setPagoProvForm(f => ({ ...emptyPagoProvForm(), proveedor_id: prov.id, proveedor_nombre: prov.nombre || "" }));
    setEditingPagoProv(null);
    setShowPagoProvForm(true);
    fetchComprasProveedor(prov.id);
    if (compraId) setPendingCompraPreselect(compraId);
    const next = new URLSearchParams(searchParams);
    next.delete("proveedor_id");
    next.delete("compra_id");
    if (next.get("tab") === "pagos_proveedores") next.delete("tab");
    setSearchParams(next, { replace: true });
  }, [searchParams, listaProveedores]); // eslint-disable-line

  useEffect(() => {
    if (!pendingCompraPreselect || comprasProvList.length === 0) return;
    const c = comprasProvList.find(x => x.id === pendingCompraPreselect);
    if (c) {
      setPagoProvForm(p => ({ ...p, compras_pagos: [{ compra_id: c.id, monto_pagado: c.saldo_pendiente || c.monto_total || 0 }] }));
    }
    setPendingCompraPreselect(null);
  }, [comprasProvList, pendingCompraPreselect]);

  const fetchComprasProveedor = async (provId) => {
    if (!provId) { setComprasProvList([]); return; }
    setLoadingComprasProv(true);
    try {
      const logo = activeEmpresaPropia?.slug;
      const res = await fetch(`${API}/admin/compras?logo_tipo=${logo || ""}`, { headers });
      if (res.ok) {
        const all = await res.json();
        setComprasProvList(all.filter(c => c.proveedor_id === provId && ["pendiente", "parcial", "vencido"].includes(c.estado_pago)));
      }
    } catch (e) {}
    finally { setLoadingComprasProv(false); }
  };

  // Trae las cuotas pendientes/vencidas de una compra a crédito en cuotas,
  // para que el usuario elija cuál está cancelando con este pago proveedor.
  const fetchCuotasCompra = async (compraId) => {
    if (!compraId || cuotasPorCompra[compraId]) return;
    setLoadingCuotasCompra(p => ({ ...p, [compraId]: true }));
    try {
      const res = await fetch(`${API}/admin/cuotas?doc_id=${compraId}&tipo_doc=compra`, { headers });
      if (res.ok) {
        const all = await res.json();
        const pendientes = all.filter(c => ["pendiente", "vencido"].includes(c.estado));
        setCuotasPorCompra(p => ({ ...p, [compraId]: pendientes }));
        // Preseleccionar todas las cuotas pendientes por defecto (equivalente a "pago total"),
        // y ajustar el monto a pagar de esa compra a la suma real de esas cuotas.
        if (pendientes.length > 0) {
          const compraRef = comprasProvList.find(c => c.id === compraId);
          const saldoMax = compraRef?.saldo_pendiente || compraRef?.monto_total || 0;
          const sumaCuotas = Math.min(pendientes.reduce((s, c) => s + (c.monto || 0), 0), saldoMax || Infinity);
          setPagoProvForm(p => ({
            ...p,
            compras_pagos: p.compras_pagos.map(cp => cp.compra_id === compraId
              ? { ...cp, cuota_ids: pendientes.map(c => c.id), monto_pagado: sumaCuotas }
              : cp),
          }));
        }
      }
    } catch (e) {}
    finally { setLoadingCuotasCompra(p => ({ ...p, [compraId]: false })); }
  };

  const handlePagoProveedor = async (e) => {
    e.preventDefault();
    const { compras_pagos } = pagoProvForm;
    if (!pagoProvForm.proveedor_id) { toast.error("Seleccioná un proveedor"); return; }
    if (compras_pagos.length === 0) { toast.error("Seleccioná al menos una compra a pagar"); return; }
    if (compras_pagos.some(cp => !cp.monto_pagado || cp.monto_pagado <= 0)) { toast.error("Todos los montos deben ser mayores a 0"); return; }
    if (compras_pagos.some(cp => {
      const compra = comprasProvList.find(c => c.id === cp.compra_id);
      return compra?.es_credito_cuotas && (!cp.cuota_ids || cp.cuota_ids.length === 0);
    })) { toast.error("Seleccioná las cuotas que se están cancelando en las compras a crédito en cuotas"); return; }
    for (const cp of compras_pagos) {
      const compra = comprasProvList.find(c => c.id === cp.compra_id);
      if (!compra?.es_credito_cuotas) continue;
      const saldoC = compra.saldo_pendiente || compra.monto_total || 0;
      const montos = (cp.cuota_ids || []).map(id => Number(cp.cuotas_montos?.[id] ?? (cuotasPorCompra[cp.compra_id] || []).find(x => x.id === id)?.monto) || 0);
      if (montos.some(m => m <= 0)) { toast.error("El monto de cada cuota seleccionada debe ser mayor a 0"); return; }
      if (montos.reduce((a, b) => a + b, 0) > saldoC + 0.01) { toast.error("El total de las cuotas no puede superar el monto total pendiente de la factura"); return; }
    }
    if (!pagoProvForm.cuenta_id) { toast.error("Seleccioná la cuenta bancaria"); return; }
    if (!pagoProvForm.fecha_pago) { toast.error("La fecha de pago es requerida"); return; }
    const firstCompra = comprasProvList.find(c => c.id === compras_pagos[0]?.compra_id);
    const monedaCompra = firstCompra?.moneda || "USD";
    const totalCompra = compras_pagos.reduce((s, cp) => s + (Number(cp.monto_pagado) || 0), 0);
    const needsTc = monedaCompra !== pagoProvForm.cuenta_moneda;
    if (needsTc && !pagoProvForm.tipo_cambio) { toast.error("Ingresá el tipo de cambio"); return; }
    const tcNum = Number(pagoProvForm.tipo_cambio) || 0;
    // El TC siempre representa 1 unidad de moneda extranjera = tcNum guaraníes.
    // Compra en moneda extranjera + cuenta en guaraníes → multiplicar.
    // Compra en guaraníes + cuenta en moneda extranjera → dividir.
    const montoEquiv = needsTc ? (monedaCompra === "PYG" ? totalCompra / tcNum : totalCompra * tcNum) : null;
    const comprasRef = comprasProvList.filter(c => compras_pagos.some(cp => cp.compra_id === c.id));
    const concepto = `Pago por ${comprasRef.length} compra${comprasRef.length > 1 ? "s" : ""}: ${comprasRef.map(compraPagoLabel).join("; ")}`;
    try {
      const payload = {
        proveedor_id: pagoProvForm.proveedor_id,
        proveedor_nombre: pagoProvForm.proveedor_nombre,
        concepto,
        monto: totalCompra,
        moneda: monedaCompra,
        tipo_cambio: needsTc ? tcNum : null,
        // monto_gs solo tiene sentido cuando el destino es una cuenta en guaraníes.
        monto_gs: needsTc && pagoProvForm.cuenta_moneda === "PYG" ? Math.round(montoEquiv) : null,
        cuenta_id: pagoProvForm.cuenta_id,
        cuenta_nombre: pagoProvForm.cuenta_nombre,
        cuenta_moneda: pagoProvForm.cuenta_moneda,
        cuenta_pago: pagoProvForm.cuenta_moneda === "PYG" ? "guaranies" : "dolares",
        fecha_vencimiento: pagoProvForm.fecha_pago,
        fecha_pago: pagoProvForm.fecha_pago,
        recibo_numero: pagoProvFormTieneCompraCredito() ? (pagoProvForm.recibo_numero || null) : null,
        compras_pagos: compras_pagos.map(cp => {
          const ids = cp.cuota_ids || [];
          const item = { compra_id: cp.compra_id, monto_pagado: Number(cp.monto_pagado), cuota_ids: ids };
          if (ids.length > 0) {
            const lista = cuotasPorCompra[cp.compra_id] || [];
            item.cuotas_montos = Object.fromEntries(ids.map(id => [id, Number(cp.cuotas_montos?.[id] ?? lista.find(x => x.id === id)?.monto) || 0]));
          }
          return item;
        }),
        notas: pagoProvForm.notas || null,
        sin_retencion: !!pagoProvForm.sin_retencion && hasPermission("retenciones.omitir_retencion"),
        logo_tipo: activeEmpresaPropia?.slug || "arandujar",
      };
      const url = editingPagoProv ? `${API}/admin/pagos-proveedores/${editingPagoProv.id}` : `${API}/admin/pagos-proveedores`;
      const res = await fetch(url, { method: editingPagoProv ? "PUT" : "POST", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (res.ok) {
        toast.success(editingPagoProv ? "Pago actualizado" : "Pago registrado");
        setShowPagoProvForm(false); setEditingPagoProv(null); setPagoProvForm(emptyPagoProvForm()); setComprasProvList([]);
        fetchTodosPagosProveedores();
      } else { const err = await res.json().catch(() => ({})); toast.error(typeof err.detail === "string" ? err.detail : "Error al registrar pago"); }
    } catch (e) { toast.error("Error de conexión"); }
  };

  const handleDeletePagoProv = async (pagoId) => {
    if (!window.confirm("¿Eliminar este pago? Esta acción no se puede deshacer.")) return;
    try {
      const res = await fetch(`${API}/admin/pagos-proveedores/${pagoId}`, { method: "DELETE", headers });
      if (res.ok) { toast.success("Pago eliminado"); setSelectedPagoView(null); fetchTodosPagosProveedores(); }
      else { const err = await res.json().catch(() => ({})); toast.error(typeof err.detail === "string" ? err.detail : "Error al eliminar", { duration: 7000 }); }
    } catch (e) { toast.error("Error de conexión"); }
  };

  const openEditPagoProv = (pago) => {
    let compras_pagos = [];
    if (pago.compras_pagos?.length > 0) {
      compras_pagos = pago.compras_pagos.map(cp => ({ compra_id: cp.compra_id, monto_pagado: cp.monto_pagado }));
    } else if (pago.compras_ids?.length > 0) {
      const montoUnitario = pago.monto / pago.compras_ids.length;
      compras_pagos = pago.compras_ids.map(id => ({ compra_id: id, monto_pagado: montoUnitario }));
    }
    setPagoProvForm({
      proveedor_id: pago.proveedor_id || "", proveedor_nombre: pago.proveedor_nombre || "",
      cuenta_id: pago.cuenta_id || "", cuenta_nombre: pago.cuenta_nombre || "",
      cuenta_moneda: pago.cuenta_moneda || (pago.cuenta_pago === "dolares" ? "USD" : "PYG"),
      tipo_cambio: pago.tipo_cambio ? String(pago.tipo_cambio) : "",
      fecha_pago: pago.fecha_pago || hoy(), recibo_numero: pago.recibo_numero || "",
      notas: pago.notas || "", compras_pagos, sin_retencion: !!pago.retencion_omitida,
    });
    if (pago.proveedor_id) fetchComprasProveedor(pago.proveedor_id);
    setEditingPagoProv(pago); setSelectedPagoView(null); setPagoProvMedio("transferencia"); setShowPagoProvForm(true);
  };

  const handleMarcarPagado = async (pagoId) => {
    try {
      const res = await fetch(`${API}/admin/pagos-proveedores/${pagoId}/marcar-pagado`, { method: "PATCH", headers });
      if (res.ok) { toast.success("Marcado como pagado"); setSelectedPagoView(null); fetchTodosPagosProveedores(); }
    } catch (e) { toast.error("Error"); }
  };

  const handleAsignarReciboPagoProv = async () => {
    const recibo = reciboPagoProvForm.trim();
    if (!selectedPagoView?.id) return;
    if (!recibo) { toast.error("Ingresá el número de recibo"); return; }
    setSavingReciboPagoProv(true);
    try {
      const res = await fetch(`${API}/admin/pagos-proveedores/${selectedPagoView.id}/recibo`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ recibo_numero: recibo }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success("Recibo asignado");
        setSelectedPagoView(prev => prev ? { ...prev, recibo_numero: data.recibo_numero || recibo } : prev);
        setTodosPagosProveedores(prev => prev.map(p => p.id === selectedPagoView.id ? { ...p, recibo_numero: data.recibo_numero || recibo } : p));
        setReciboPagoProvForm("");
      } else {
        toast.error(data.detail || "No se pudo asignar el recibo");
      }
    } catch {
      toast.error("Error de conexión");
    } finally {
      setSavingReciboPagoProv(false);
    }
  };

  const handlePagoProvConCheque = async (hoja, chequera, tipoCambio) => {
    const { compras_pagos, proveedor_id, proveedor_nombre, notas } = pagoProvForm;
    const totalC = compras_pagos.reduce((s, cp) => s + (Number(cp.monto_pagado) || 0), 0);
    const monedaC = comprasProvList.find(c => c.id === compras_pagos[0]?.compra_id)?.moneda || "PYG";
    const concepto = `Pago a ${proveedor_nombre} — ${compras_pagos.length} compra(s)`;
    const payload = {
      proveedor_id, proveedor_nombre, concepto, monto: totalC,
      moneda: chequera.moneda || monedaC,
      tipo_cambio: tipoCambio || null,
      fecha_pago: hoja.fecha_emision,
      cuenta_id: chequera.cuenta_bancaria_id,
      cuenta_nombre: chequera.banco_nombre,
      cuenta_moneda: chequera.moneda || monedaC,
      cuenta_pago: (chequera.moneda || monedaC) === "PYG" ? "guaranies" : "dolares",
      fecha_vencimiento: hoja.fecha_pago || hoja.fecha_emision,
      cheque_hoja_id: hoja.id,
          cheque_numero: `${hoja.serie}${String(hoja.numero).padStart(6, "0")}`,
          notas: `Cheque ${hoja.serie}${String(hoja.numero).padStart(6, "0")}${notas ? ` — ${notas}` : ""}`,
      compras_pagos,
      logo_tipo: activeEmpresaPropia?.slug || logoFiltro,
    };
    try {
      const res = await fetch(`${API}/admin/pagos-proveedores`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success("Cheque emitido y pago registrado");
        setShowPagoProvForm(false); setEditingPagoProv(null);
        setPagoProvForm(emptyPagoProvForm()); setComprasProvList([]);
        setPagoProvMedio("transferencia");
        fetchTodosPagosProveedores();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === "string" ? err.detail : (Array.isArray(err.detail) ? err.detail.map(d => `${(d.loc || []).slice(-1)[0] || ""}: ${d.msg}`).join(" · ") : "Error al registrar pago"));
        // Relanzar (ya avisado arriba): ChequeEgresoForm necesita enterarse
        // de que el pago no se registró para revertir el cheque que ya
        // había emitido — si no, la hoja queda marcada emitida/diferida sin
        // ningún pago real detrás, y el próximo intento del usuario choca
        // con "la hoja ya está emitida".
        throw new Error("__ya_avisado__");
      }
    } catch (e) {
      if (e?.message !== "__ya_avisado__") toast.error("Error de conexión");
      throw e;
    }
  };

  const loadCotizaciones = useCallback(async () => {
    if (!canVerCotiz) return;
    setCotLoading(true);
    try {
      const logoTc = activeEmpresaPropia?.slug || "";
      const [hoyRes, histRes] = await Promise.all([
        fetch(`${API}/admin/cotizaciones/hoy?${new URLSearchParams(logoTc ? { logo_tipo: logoTc } : {})}`, { headers }),
        fetch(`${API}/admin/cotizaciones?${new URLSearchParams(Object.fromEntries(
          [["desde", cotFiltroDesde], ["hasta", cotFiltroHasta], ["moneda", cotFiltroMoneda], ["logo_tipo", logoTc]].filter(([,v]) => v)
        ))}`, { headers }),
      ]);
      if (hoyRes.ok) { const d = await hoyRes.json(); setCotHoy(d.cotizaciones || {}); }
      if (histRes.ok) setCotHistorial(await histRes.json());
    } catch (e) { console.error(e); }
    finally { setCotLoading(false); }
  }, [canVerCotiz, cotFiltroDesde, cotFiltroHasta, cotFiltroMoneda, activeEmpresaPropia?.slug]); // eslint-disable-line

  useEffect(() => {
    if (activeTab === "cotizaciones") loadCotizaciones();
  }, [activeTab, loadCotizaciones]);

  const handleSync = async () => {
    if (!canGestCotiz) return;
    setCotSyncing(true);
    // Tope de espera: si el servidor/DNIT no responde, no dejamos el botón "Sincronizando…" colgado
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 60000);
    try {
      const res = await fetch(`${API}/admin/cotizaciones/sync`, { method: "POST", headers, signal: ctrl.signal });
      const d = await res.json();
      // El endpoint siempre responde 200 (así lo capturamos acá y no como error
      // de red), pero el resultado real de la sincronización viene en d.ok — antes
      // solo se miraba res.ok, así que un fallo de DNIT (sitio caído, timeout, etc.)
      // se mostraba como "sincronizado con éxito" sin serlo.
      if (res.ok && d.ok) {
        const usd = d.sincronizados?.USD || 0, brl = d.sincronizados?.BRL || 0;
        if (usd === 0 && brl === 0) {
          toast("DNIT no tenía fechas nuevas para traer (puede que el mes actual todavía no esté publicado en su web)", { icon: "ℹ️" });
        } else {
          toast.success(`Cotizaciones sincronizadas con DNIT (${usd} USD, ${brl} BRL nuevas)`);
        }
        loadCotizaciones();
      } else {
        toast.error(d.errores?.DNIT || d.detail || "No se pudo conectar con DNIT para sincronizar");
      }
    } catch (err) {
      toast.error(err?.name === "AbortError"
        ? "DNIT tardó demasiado en responder. Probá de nuevo en unos minutos o cargá el TC manualmente."
        : "Error de red");
    }
    finally { clearTimeout(timer); setCotSyncing(false); }
  };

  const handleManualSave = async (e) => {
    e.preventDefault();
    if (!canGestCotiz) return;
    if (!activeEmpresaPropia?.slug) { toast.error("Seleccioná una empresa propia primero"); return; }
    setManualSaving(true);
    try {
      const body = {
        moneda: manualForm.moneda,
        fecha: manualForm.fecha,
        tc_empresa: parseFloat(manualForm.tc_empresa),
        notas: manualForm.notas || undefined,
        // El TC empresa cargado a mano es propio de ESTA empresa, no global
        // (el TC oficial DNIT sí es global y no se toca acá).
        logo_tipo: activeEmpresaPropia.slug,
      };
      if (manualForm.tc_iva) body.tc_iva = parseFloat(manualForm.tc_iva);
      const res = await fetch(`${API}/admin/cotizaciones/manual`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (res.ok) { toast.success("Cotización guardada"); setShowManualForm(false); loadCotizaciones(); }
      else toast.error(d.detail || "Error al guardar");
    } catch { toast.error("Error de red"); }
    finally { setManualSaving(false); }
  };

  const handleTcEmpresaEdit = async (moneda, fecha) => {
    const venta = parseFloat(cotTcEdit[moneda]);
    if (!venta || venta <= 0) { toast.error("Ingresá un TC válido"); return; }
    if (!activeEmpresaPropia?.slug) { toast.error("Seleccioná una empresa propia primero"); return; }
    setCotTcSaving(s => ({ ...s, [moneda]: true }));
    try {
      const res = await fetch(`${API}/admin/cotizaciones/${moneda.toLowerCase()}/${fecha}`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        // El TC Empresa es propio de esta empresa (logo_tipo); no pisa el de otras.
        body: JSON.stringify({ venta, logo_tipo: activeEmpresaPropia.slug }),
      });
      const d = await res.json();
      if (res.ok) {
        toast.success(`TC Empresa ${moneda} → ₲ ${Number(d.venta).toLocaleString("es-PY")}`);
        setCotTcEdit(s => { const n = { ...s }; delete n[moneda]; return n; });
        loadCotizaciones();
      } else toast.error(d.detail || "Error al guardar");
    } catch { toast.error("Error de red"); }
    finally { setCotTcSaving(s => ({ ...s, [moneda]: false })); }
  };

  const handleTcIvaEdit = async (moneda, fecha) => {
    const venta_mercado = parseFloat(cotTcIvaEdit[moneda]);
    if (!venta_mercado || venta_mercado <= 0) { toast.error("Ingresá un TC IVA válido"); return; }
    setCotTcIvaSaving(s => ({ ...s, [moneda]: true }));
    try {
      const res = await fetch(`${API}/admin/cotizaciones/${moneda.toLowerCase()}/${fecha}`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ venta_mercado }),
      });
      const d = await res.json();
      if (res.ok) {
        toast.success(`TC IVA ${moneda} → ₲ ${Number(d.venta_mercado).toLocaleString("es-PY")}`);
        setCotTcIvaEdit(s => { const n = { ...s }; delete n[moneda]; return n; });
        loadCotizaciones();
      } else toast.error(d.detail || "Error al guardar");
    } catch { toast.error("Error de red"); }
    finally { setCotTcIvaSaving(s => ({ ...s, [moneda]: false })); }
  };

  const fmtTC = (n) => n ? Number(n).toLocaleString("es-PY") : "—";

  // ── PRÉSTAMOS state ───────────────────────────────────────────────────────
  const canVerPrestamos     = isAdminOrGerente || hasPermission("prestamos.ver");
  const canGestPrestamos    = isAdminOrGerente || hasPermission("prestamos.gestionar");
  const canCrearPrestEmpleado = canGestPrestamos || hasPermission("prestamos.crear_empleado");
  const canCrearPrestProveedor = canGestPrestamos || hasPermission("prestamos.crear_proveedor");
  const canCrearPrestBancario = canGestPrestamos || hasPermission("prestamos.crear_bancario");
  const canCrearPrestamos = canCrearPrestEmpleado || canCrearPrestProveedor || canCrearPrestBancario;
  const canPagarPrestamos   = isAdminOrGerente || hasPermission("prestamos.pagar");
  const canCuotaManualPrestamos = canGestPrestamos || hasPermission("prestamos.cuota_manual");
  const canCancelarPrestamos = canGestPrestamos || hasPermission("prestamos.cancelar");
  const canDescPrestEmpleadoInteres = canGestPrestamos || hasPermission("prestamos.descuento_empleado_interes");
  const canDescPrestEmpleadoExtra = canGestPrestamos || hasPermission("prestamos.descuento_empleado_extra");
  const canDescPrestProveedor = canGestPrestamos || hasPermission("prestamos.descuento_proveedor");
  const canDescPrestBancario = canGestPrestamos || hasPermission("prestamos.descuento_bancario");

  const emptyPrestForm = () => ({
    tipo: canCrearPrestBancario ? "bancario" : canCrearPrestEmpleado ? "funcionario" : "proveedor",
    entidad_nombre: "", entidad_id: "",
    referencia: "", alias: "", destino: "",
    monto_total: "", moneda: "PYG", tasa_interes_mensual: 0,
    n_cuotas: 12, gastos_administrativos: "", iva_cuotas_pct: "", monto_cuota_manual: "",
    usar_cuotas_manuales: false, cuotas_manuales: [], cuotas_manuales_vencimientos: [],
    fecha_inicio: hoy(), cuenta_id: "", cuenta_nombre: "", notas: "",
  });

  const [prestamos, setPrestamos]                   = useState([]);
  const [prestLoading, setPrestLoading]             = useState(false);
  const [prestFiltroTipo, setPrestFiltroTipo]       = useState("todos");
  const [prestFiltroEstado, setPrestFiltroEstado]   = useState("activo");
  const [showPrestForm, setShowPrestForm]           = useState(false);
  const [prestForm, setPrestForm]                   = useState(emptyPrestForm());
  const [prestSaving, setPrestSaving]               = useState(false);
  const [prestPreview, setPrestPreview]             = useState([]);
  const [prestPreviewLoading, setPrestPreviewLoading] = useState(false);
  const [expandedPrest, setExpandedPrest]           = useState(null);
  const [pagarCuotaModal, setPagarCuotaModal]       = useState(null); // { prestamo, cuota }
  const [pagarForm, setPagarForm]                   = useState({
    cuenta_pago_id: "",
    fecha_pago: hoy(),
    notas: "",
    descuento_tipo: "monto",
    descuento_monto: "",
    descuento_porcentaje: "",
    descuento_motivo: "",
    descuento_aplicar_total: false,
  });
  const [pagarSaving, setPagarSaving]               = useState(false);
  const [prestEmpleados, setPrestEmpleados]         = useState([]);
  const [prestProveedores, setPrestProveedores]     = useState([]);
  const [prestEntidadesBancarias, setPrestEntidadesBancarias] = useState([]);

  const resetPagarPrestamoForm = () => ({
    cuenta_pago_id: cuentasVisibles[0]?.id || "",
    fecha_pago: hoy(),
    notas: "",
    descuento_tipo: "monto",
    descuento_monto: "",
    descuento_porcentaje: "",
    descuento_motivo: "",
    descuento_aplicar_total: false,
  });

  const cuotasPagoPrestamo = useMemo(() => {
    if (!pagarCuotaModal) return [];
    const cuotas = pagarCuotaModal.prestamo?.cuotas || [];
    if (pagarForm.descuento_aplicar_total) return cuotas.filter(c => c.estado !== "pagada");
    return [pagarCuotaModal.cuota].filter(Boolean);
  }, [pagarCuotaModal, pagarForm.descuento_aplicar_total]);

  const totalPagoPrestamo = useMemo(
    () => cuotasPagoPrestamo.reduce((sum, c) => sum + Number(c.monto_total || 0), 0),
    [cuotasPagoPrestamo]
  );

  const interesExonerablePrestamo = useMemo(() => {
    const fechaPago = pagarForm.fecha_pago || hoy();
    return cuotasPagoPrestamo.reduce((sum, c) => {
      const vence = String(c.fecha_vencimiento || "").slice(0, 10);
      return sum + (vence && fechaPago < vence ? Number(c.monto_interes || 0) : 0);
    }, 0);
  }, [cuotasPagoPrestamo, pagarForm.fecha_pago]);

  const descuentoPrestamoMonto = useMemo(() => {
    if (pagarForm.descuento_tipo === "porcentaje") {
      return totalPagoPrestamo * ((Number(pagarForm.descuento_porcentaje) || 0) / 100);
    }
    return Number(pagarForm.descuento_monto) || 0;
  }, [pagarForm.descuento_tipo, pagarForm.descuento_monto, pagarForm.descuento_porcentaje, totalPagoPrestamo]);

  const netoPagoPrestamo = Math.max(totalPagoPrestamo - descuentoPrestamoMonto, 0);

  const puedeDescuentoPrestamo = useMemo(() => {
    const tipo = pagarCuotaModal?.prestamo?.tipo;
    if (tipo === "funcionario") return canDescPrestEmpleadoInteres || canDescPrestEmpleadoExtra;
    if (tipo === "proveedor") return canDescPrestProveedor;
    if (tipo === "bancario") return canDescPrestBancario;
    return false;
  }, [pagarCuotaModal, canDescPrestEmpleadoInteres, canDescPrestEmpleadoExtra, canDescPrestProveedor, canDescPrestBancario]);

  const fetchEmpleadosPrest = useCallback(async () => {
    const logo = activeEmpresaPropia?.slug;
    if (!logo) return;
    try {
      const r = await fetch(`${API}/admin/entidades?es_empleado=true&activo=true&logo_tipo=${encodeURIComponent(logo)}`, { headers });
      if (r.ok) {
        const empleados = await r.json();
        setPrestEmpleados((empleados || []).filter(e => {
          const estado = String(e.estado_laboral || "").toLowerCase();
          return e.activo !== false && !e.liquidado && !["liquidado", "desvinculado"].includes(estado);
        }));
      }
    } catch {}
  }, [activeEmpresaPropia?.slug]); // eslint-disable-line

  const fetchProveedoresPrest = useCallback(async () => {
    const logo = activeEmpresaPropia?.slug;
    if (!logo) return;
    try {
      const [rProv, rBancos] = await Promise.all([
        fetch(`${API}/admin/entidades?es_proveedor=true&es_entidad_bancaria=false&logo_tipo=${encodeURIComponent(logo)}`, { headers }),
        fetch(`${API}/admin/entidades?es_proveedor=true&es_entidad_bancaria=true&logo_tipo=${encodeURIComponent(logo)}`, { headers }),
      ]);
      if (rProv.ok) setPrestProveedores(await rProv.json());
      if (rBancos.ok) setPrestEntidadesBancarias(await rBancos.json());
    } catch {}
  }, [activeEmpresaPropia?.slug]); // eslint-disable-line

  const fetchPrestamos = useCallback(async () => {
    if (!canVerPrestamos) { setPrestamos([]); return; }
    setPrestLoading(true);
    try {
      const logo = activeEmpresaPropia?.slug;
      const params = new URLSearchParams();
      if (logo) params.set("empresa_id", logo);
      if (prestFiltroTipo !== "todos") params.set("tipo", prestFiltroTipo);
      if (prestFiltroEstado !== "todos") params.set("estado", prestFiltroEstado);
      const r = await fetch(`${API}/admin/prestamos?${params}`, { headers });
      if (r.ok) setPrestamos(await r.json());
    } catch (e) {}
    finally { setPrestLoading(false); }
  }, [canVerPrestamos, activeEmpresaPropia?.slug, prestFiltroTipo, prestFiltroEstado]); // eslint-disable-line

  useEffect(() => {
    if (activeTab === "prestamos") { fetchPrestamos(); fetchEmpleadosPrest(); fetchProveedoresPrest(); }
  }, [activeTab, fetchPrestamos, fetchEmpleadosPrest, fetchProveedoresPrest]);

  const normalizarCuotasManuales = (n = prestForm.n_cuotas, actuales = prestForm.cuotas_manuales) => {
    const cantidad = Math.max(0, Math.min(360, Number(n) || 0));
    return Array.from({ length: cantidad }, (_, idx) => actuales?.[idx] || "");
  };

  const normalizarVencimientosManuales = (n = prestForm.n_cuotas, actuales = prestForm.cuotas_manuales_vencimientos) => {
    const cantidad = Math.max(0, Math.min(360, Number(n) || 0));
    return Array.from({ length: cantidad }, (_, idx) => actuales?.[idx] || "");
  };

  useEffect(() => {
    if (!prestForm.usar_cuotas_manuales) return;
    setPrestForm(s => {
      const cantidad = Math.max(0, Math.min(360, Number(s.n_cuotas) || 0));
      if ((s.cuotas_manuales || []).length === cantidad && (s.cuotas_manuales_vencimientos || []).length === cantidad) return s;
      return {
        ...s,
        cuotas_manuales: Array.from({ length: cantidad }, (_, idx) => s.cuotas_manuales?.[idx] || ""),
        cuotas_manuales_vencimientos: Array.from({ length: cantidad }, (_, idx) => s.cuotas_manuales_vencimientos?.[idx] || ""),
      };
    });
  }, [prestForm.n_cuotas, prestForm.usar_cuotas_manuales]);

  const cuotasManualesActivas = canCuotaManualPrestamos && prestForm.usar_cuotas_manuales;
  const cuotasManualesPayload = cuotasManualesActivas
    ? normalizarCuotasManuales(prestForm.n_cuotas, prestForm.cuotas_manuales).map(v => Number(v || 0))
    : [];
  const cuotasManualesVencimientosPayload = cuotasManualesActivas
    ? normalizarVencimientosManuales(prestForm.n_cuotas, prestForm.cuotas_manuales_vencimientos)
    : [];

  const loadPreview = useCallback(async () => {
    const { monto_total, tasa_interes_mensual, n_cuotas, fecha_inicio, gastos_administrativos, iva_cuotas_pct, monto_cuota_manual } = prestForm;
    if (!monto_total || !n_cuotas || !fecha_inicio) { setPrestPreview([]); return; }
    setPrestPreviewLoading(true);
    try {
      const body = {
        monto: monto_total,
        tasa: tasa_interes_mensual || 0,
        n_cuotas,
        fecha_inicio,
        gastos_administrativos: gastos_administrativos || 0,
        iva_cuotas_pct: iva_cuotas_pct || 0,
      };
      if (cuotasManualesActivas) {
        body.cuotas_manuales = cuotasManualesPayload;
        if (cuotasManualesVencimientosPayload.every(Boolean)) body.cuotas_manuales_vencimientos = cuotasManualesVencimientosPayload;
      }
      else if (canCuotaManualPrestamos && monto_cuota_manual) body.monto_cuota_manual = monto_cuota_manual;
      const r = await fetch(`${API}/admin/prestamos/preview-cuotas`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (r.ok) setPrestPreview(await r.json());
    } catch {}
    finally { setPrestPreviewLoading(false); }
  }, [prestForm.monto_total, prestForm.tasa_interes_mensual, prestForm.n_cuotas, prestForm.fecha_inicio, prestForm.gastos_administrativos, prestForm.iva_cuotas_pct, prestForm.monto_cuota_manual, canCuotaManualPrestamos, cuotasManualesActivas, JSON.stringify(cuotasManualesPayload), JSON.stringify(cuotasManualesVencimientosPayload)]); // eslint-disable-line

  useEffect(() => { if (showPrestForm) loadPreview(); }, [showPrestForm, loadPreview]);

  const handleCrearPrestamo = async () => {
    if (!prestForm.entidad_nombre.trim()) { toast.error("Ingresá el nombre de la entidad"); return; }
    if (prestForm.tipo === "bancario" && !canCrearPrestBancario) { toast.error("Sin permiso para crear préstamos bancarios"); return; }
    if (prestForm.tipo === "bancario" && !prestForm.referencia.trim()) { toast.error("Ingresá la referencia / operación del préstamo bancario"); return; }
    if (prestForm.tipo === "bancario" && !prestForm.entidad_id) { toast.error("Seleccioná una entidad bancaria del catastro"); return; }
    if (prestForm.tipo === "funcionario" && !canCrearPrestEmpleado) { toast.error("Sin permiso para crear préstamos a empleados"); return; }
    if (prestForm.tipo === "proveedor" && !canCrearPrestProveedor) { toast.error("Sin permiso para crear préstamos a proveedores"); return; }
    if (!prestForm.monto_total || Number(prestForm.monto_total) <= 0) { toast.error("El monto debe ser mayor a 0"); return; }
    if (!prestForm.cuenta_id) { toast.error("Seleccioná una cuenta bancaria"); return; }
    if (!prestForm.n_cuotas || Number(prestForm.n_cuotas) < 1) { toast.error("Cuotas inválidas"); return; }
    if (cuotasManualesActivas) {
      const cuotas = cuotasManualesPayload;
      if (cuotas.length !== Number(prestForm.n_cuotas) || cuotas.some(v => !v || v <= 0)) {
        toast.error("Cargá el monto de todas las cuotas manuales");
        return;
      }
      if (cuotasManualesVencimientosPayload.some(v => !v)) {
        toast.error("Cargá la fecha de vencimiento de todas las cuotas manuales");
        return;
      }
    }
    setPrestSaving(true);
    try {
      const slug = activeEmpresaPropia?.slug;
      const body = {
        ...prestForm,
        monto_total: Number(prestForm.monto_total),
        tasa_interes_mensual: Number(prestForm.tasa_interes_mensual) || 0,
        n_cuotas: Number(prestForm.n_cuotas),
        gastos_administrativos: Number(prestForm.gastos_administrativos) || 0,
        iva_cuotas_pct: Number(prestForm.iva_cuotas_pct) || 0,
        monto_cuota_manual: !cuotasManualesActivas && canCuotaManualPrestamos && prestForm.monto_cuota_manual ? Number(prestForm.monto_cuota_manual) : null,
        cuotas_manuales: cuotasManualesActivas ? cuotasManualesPayload : null,
        cuotas_manuales_vencimientos: cuotasManualesActivas ? cuotasManualesVencimientosPayload : null,
        cuenta_nombre: cuentasVisibles.find(c => c.id === prestForm.cuenta_id)?.nombre || "",
      };
      const params = slug ? `?empresa_id=${encodeURIComponent(slug)}` : "";
      const r = await fetch(`${API}/admin/prestamos${params}`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success("Préstamo registrado");
      setShowPrestForm(false);
      setPrestForm(emptyPrestForm());
      setPrestPreview([]);
      fetchPrestamos();
      loadData();
    } catch (e) { toast.error(e.message); }
    finally { setPrestSaving(false); }
  };

  const handlePagarCuota = async () => {
    if (!pagarForm.cuenta_pago_id) { toast.error("Seleccioná una cuenta bancaria"); return; }
    if (!pagarCuotaModal) return;
    if (descuentoPrestamoMonto > totalPagoPrestamo) { toast.error("El descuento no puede superar el total a pagar"); return; }
    if (descuentoPrestamoMonto > 0 && !puedeDescuentoPrestamo) { toast.error("Sin permiso para aplicar descuentos en este préstamo"); return; }
    if (
      pagarCuotaModal.prestamo?.tipo === "funcionario"
      && descuentoPrestamoMonto > interesExonerablePrestamo
      && !canDescPrestEmpleadoExtra
    ) {
      toast.error("El descuento supera el interés exonerable. Requiere permiso de descuento extra.");
      return;
    }
    setPagarSaving(true);
    try {
      const slug = activeEmpresaPropia?.slug;
      const params = slug ? `?empresa_id=${encodeURIComponent(slug)}` : "";
      const r = await fetch(`${API}/admin/prestamos/${pagarCuotaModal.prestamo.id}/pagar-cuota${params}`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          cuota_numero: pagarCuotaModal.cuota.numero,
          cuenta_pago_id: pagarForm.cuenta_pago_id,
          cuenta_pago_nombre: cuentasVisibles.find(c => c.id === pagarForm.cuenta_pago_id)?.nombre || "",
          fecha_pago: pagarForm.fecha_pago,
          notas: pagarForm.notas,
          descuento_tipo: pagarForm.descuento_tipo,
          descuento_monto: Number(pagarForm.descuento_monto) || 0,
          descuento_porcentaje: Number(pagarForm.descuento_porcentaje) || 0,
          descuento_motivo: pagarForm.descuento_motivo,
          descuento_aplicar_total: !!pagarForm.descuento_aplicar_total,
        }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      const result = await r.json();
      toast.success(result.mensaje || "Cuota registrada");
      setPagarCuotaModal(null);
      fetchPrestamos();
      loadData();
    } catch (e) { toast.error(e.message); }
    finally { setPagarSaving(false); }
  };

  const handleCancelarPrestamo = async (id) => {
    if (!window.confirm("¿Cancelar este préstamo?")) return;
    try {
      const slug = activeEmpresaPropia?.slug;
      const params = slug ? `?empresa_id=${encodeURIComponent(slug)}` : "";
      const r = await fetch(`${API}/admin/prestamos/${id}/cancelar${params}`, { method: "PATCH", headers });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success("Préstamo cancelado");
      fetchPrestamos();
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  const prestEstadoBadge = (p) => {
    if (p.estado === "finalizado") return <span className="text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 text-[10px] px-2 py-0.5 rounded-full">Finalizado</span>;
    if (p.estado === "cancelado")  return <span className="text-slate-400 bg-slate-400/10 border border-slate-400/20 text-[10px] px-2 py-0.5 rounded-full">Cancelado</span>;
    const tieneVencida = p.cuotas?.some(c => c.estado === "vencida" || (c.estado === "pendiente" && c.fecha_vencimiento < hoy()));
    if (tieneVencida) return <span className="text-red-400 bg-red-400/10 border border-red-400/20 text-[10px] px-2 py-0.5 rounded-full font-semibold">Vencido</span>;
    return <span className="text-blue-400 bg-blue-400/10 border border-blue-400/20 text-[10px] px-2 py-0.5 rounded-full">Activo</span>;
  };

  const cuotaEstadoBadge = (c) => {
    const esVencida = c.estado === "pendiente" && c.fecha_vencimiento < hoy();
    if (c.estado === "pagada") return <span className="text-emerald-400 text-[10px] bg-emerald-400/10 px-1.5 py-0.5 rounded">Pagada{c.desde_sueldo ? " (sueldo)" : ""}</span>;
    if (esVencida || c.estado === "vencida") return <span className="text-red-400 text-[10px] bg-red-400/10 px-1.5 py-0.5 rounded font-semibold">Vencida</span>;
    return <span className="text-slate-400 text-[10px] bg-white/5 px-1.5 py-0.5 rounded">Pendiente</span>;
  };

  const TABS = [
    { key: "transferencias", label: "Transferencias", icon: <ArrowLeftRight className="w-4 h-4" /> },
    ...(canVerCuentas ? [{ key: "cuentas", label: "Bancos / Cuentas", icon: <Building2 className="w-4 h-4" /> }] : []),
    ...(canVerCotiz ? [{ key: "cotizaciones", label: "Cotizaciones", icon: <DollarSign className="w-4 h-4" /> }] : []),
    ...(canVerPagos ? [{ key: "pagos_proveedores", label: "Pagos proveedores", icon: <CheckCircle className="w-4 h-4" /> }] : []),
    ...(puedeVerCheques ? [{ key: "cheques", label: "Cheques", icon: <CreditCard className="w-4 h-4" /> }] : []),
    ...(canVerPrestamos ? [{ key: "prestamos", label: "Préstamos", icon: <Banknote className="w-4 h-4" /> }] : []),
    ...(puedeVerGarantias ? [{ key: "garantias", label: "Garantías", icon: <Shield className="w-4 h-4" /> }] : []),
    ...(puedeTraspasarSaldos ? [{ key: "traspaso_saldos", label: "Traspaso de Saldos", icon: <ArrowRight className="w-4 h-4" /> }] : []),
    ...(puedeVerProgramacionCaja ? [{ key: "programacion_caja", label: "Programación de Caja", icon: <Calendar className="w-4 h-4" /> }] : []),
    ...(puedeGestionar ? [{ key: "configuracion", label: "Configuración", icon: <Settings className="w-4 h-4" /> }] : []),
  ];

  const hayModalAbierto = !!(showTransferModal || showEditModal || showAccesoModal || showAccesoBancosModal || showPrintReporte || showCuentaForm || showManualForm || showPagoProvForm);
  useAutoRefresh(loadData, 30_000, !hayModalAbierto);
  useSSE({
    logoTipo: activeEmpresaPropia?.slug,
    token,
    enabled: !!activeEmpresaPropia?.slug && !!token,
    onEvent: useCallback((event) => {
      if (event.tipo !== "modulo_actualizado") return;
      const modulo = event.data?.modulo;
      if (["financiero", "transferencias", "bancos"].includes(modulo)) loadData();
      if (["pagos_proveedores", "compras"].includes(modulo) && activeTab === "pagos_proveedores") fetchTodosPagosProveedores();
      if (modulo === "prestamos" && activeTab === "prestamos") fetchPrestamos();
    }, [activeTab, loadData, fetchTodosPagosProveedores, fetchPrestamos]),
  });

  return (
    <div className="min-h-screen bg-arandu-dark">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-arandu-dark/80 backdrop-blur-lg border-b border-white/5">
        <div className="px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/sistema" className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-heading text-xl font-bold text-white flex items-center gap-2">
                <Repeat2 className="w-5 h-5 text-arandu-blue" />
                Financiero
              </h1>
              <p className="text-slate-500 text-xs">Módulo financiero centralizado</p>
            </div>
          </div>
          <button
            onClick={loadData}
            className="text-slate-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-lg"
            title="Actualizar"
          >
            <RefreshCcw className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex px-4 md:px-8 border-t border-white/5">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                activeTab === t.key
                  ? "border-arandu-blue text-arandu-blue"
                  : "border-transparent text-slate-400 hover:text-white"
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </header>

      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        <AnimatePresence mode="wait">
          {/* ── TAB: TRANSFERENCIAS ─────────────────────────────────── */}
          {activeTab === "transferencias" && (
            <motion.div
              key="transferencias"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="space-y-6"
            >
              {/* Cuentas bancarias */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-white font-semibold text-sm flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    Cuentas disponibles
                  </h2>
                  {puedeCrear && cuentasParaTransfer.length >= 2 && (
                    <Button
                      onClick={() => setShowTransferModal(true)}
                      className="h-8 text-xs bg-arandu-blue hover:bg-arandu-blue-dark text-white px-3"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Nueva transferencia
                    </Button>
                  )}
                </div>

                {loading ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="border border-white/8 rounded-xl p-4 h-24 bg-white/2 animate-pulse" />
                    ))}
                  </div>
                ) : cuentasVisibles.length === 0 ? (
                  <div className="border border-white/8 rounded-xl p-8 text-center">
                    <CreditCard className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">
                      {isAdminOrGerente
                        ? "No hay cuentas bancarias configuradas"
                        : "No tenés acceso a ninguna cuenta. Pedile al administrador que configure tu acceso."}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {cuentasVisibles.map(c => (
                      <CuentaCard
                        key={c.id}
                        cuenta={c}
                        saldo_actual={saldos[c.id]}
                        canVerSaldo={canVerSaldo(c.id)}
                        onClick={() => setFilterCuenta(prev => prev === c.id ? "" : c.id)}
                      />
                    ))}
                  </div>
                )}

              </div>

              {/* Historial */}
              {puedeHistorial && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-white font-semibold text-sm flex items-center gap-2">
                      <History className="w-4 h-4 text-slate-400" />
                      Historial de transferencias
                    </h2>
                    {transfersFiltradas.length > 0 && (
                      <button
                        onClick={() => setShowPrintReporte(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 hover:text-white border border-white/10 hover:border-white/30 rounded-lg transition-all"
                        title="Imprimir reporte histórico"
                      >
                        <Printer className="w-3.5 h-3.5" />
                        Imprimir reporte
                      </button>
                    )}
                  </div>

                  {/* Filtros */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <button
                      onClick={() => { setFilterDesde(hoy()); setFilterHasta(hoy()); }}
                      className={`h-8 px-3 rounded-md text-xs font-body font-medium border transition-all ${filterDesde === hoy() && filterHasta === hoy() ? "bg-emerald-600 border-emerald-600 text-white" : "border-white/10 text-slate-400 hover:text-white bg-white/5"}`}
                    >
                      Hoy
                    </button>
                    <button
                      onClick={() => { setFilterDesde(""); setFilterHasta(""); }}
                      className={`h-8 px-3 rounded-md text-xs font-body font-medium border transition-all ${!filterDesde && !filterHasta ? "bg-emerald-600 border-emerald-600 text-white" : "border-white/10 text-slate-400 hover:text-white bg-white/5"}`}
                    >
                      Todos
                    </button>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                      <Input
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        placeholder="Buscar..."
                        className="pl-8 h-8 text-xs bg-arandu-dark border-white/10 text-white w-40"
                      />
                    </div>
                    <DateInput
                      value={filterDesde}
                      onChange={e => setFilterDesde(e.target.value)}
                      className="h-8 text-xs bg-arandu-dark border-white/10 text-white w-36"
                      title="Desde"
                    />
                    <DateInput
                      value={filterHasta}
                      onChange={e => setFilterHasta(e.target.value)}
                      className="h-8 text-xs bg-arandu-dark border-white/10 text-white w-36"
                      title="Hasta"
                    />
                    <select
                      value={filterCuenta}
                      onChange={e => setFilterCuenta(e.target.value)}
                      className="h-8 text-xs bg-arandu-dark border border-white/10 rounded-md px-2 text-white"
                    >
                      <option value="">Todas las cuentas</option>
                      {cuentasVisibles.map(c => (
                        <option key={c.id} value={c.id}>{c.nombre}</option>
                      ))}
                    </select>
                    {(filterCuenta || searchText) && (
                      <button
                        onClick={() => { setFilterCuenta(""); setSearchText(""); }}
                        className="h-8 px-2 text-slate-400 hover:text-white border border-white/10 rounded-md text-xs flex items-center gap-1"
                      >
                        <X className="w-3 h-3" /> Limpiar
                      </button>
                    )}
                  </div>

                  {/* Tabla */}
                  {loading ? (
                    <div className="border border-white/8 rounded-xl overflow-hidden">
                      {[1,2,3].map(i => (
                        <div key={i} className="h-12 border-b border-white/5 bg-white/2 animate-pulse" />
                      ))}
                    </div>
                  ) : transfersFiltradas.length === 0 ? (
                    <div className="border border-white/8 rounded-xl p-8 text-center">
                      <ArrowLeftRight className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                      <p className="text-slate-500 text-sm">Sin transferencias para mostrar</p>
                    </div>
                  ) : (
                    <div className="border border-white/8 rounded-xl overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-white/8 bg-arandu-dark/60">
                              <th className="text-left px-3 py-2.5 text-slate-400 font-medium">Nro.</th>
                              <th className="text-left px-3 py-2.5 text-slate-400 font-medium">Fecha</th>
                              <th className="text-left px-3 py-2.5 text-slate-400 font-medium">Origen</th>
                              <th className="text-left px-3 py-2.5 text-slate-400 font-medium"></th>
                              <th className="text-left px-3 py-2.5 text-slate-400 font-medium">Destino</th>
                              <th className="text-right px-3 py-2.5 text-slate-400 font-medium">Monto</th>
                              <th className="text-left px-3 py-2.5 text-slate-400 font-medium">Descripción</th>
                              <th className="text-left px-3 py-2.5 text-slate-400 font-medium">Por</th>
                              <th className="px-3 py-2.5" />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/5">
                            {transfersFiltradas.map(t => (
                              <tr key={t.id} className="hover:bg-white/3 transition-colors">
                                <td className="px-3 py-2.5">
                                  <span className="text-arandu-blue font-mono">{t.numero}</span>
                                </td>
                                <td className="px-3 py-2.5 text-slate-300">{t.fecha}</td>
                                <td className="px-3 py-2.5">
                                  <span className="text-white font-medium truncate max-w-[100px] block">{t.de_cuenta_nombre}</span>
                                  <span className="text-slate-500 text-[10px]">{t.de_moneda}</span>
                                </td>
                                <td className="px-1 py-2.5">
                                  <ArrowRight className="w-3 h-3 text-slate-600" />
                                </td>
                                <td className="px-3 py-2.5">
                                  <span className="text-white font-medium truncate max-w-[100px] block">{t.a_cuenta_nombre}</span>
                                  <span className="text-slate-500 text-[10px]">{t.a_moneda}</span>
                                </td>
                                <td className="px-3 py-2.5 text-right">
                                  <span className="text-white font-semibold">{fmtNum(t.monto, t.de_moneda)}</span>
                                  {t.tipo_cambio && (
                                    <div className="text-emerald-400 text-[10px]">{fmtNum(t.monto_destino, t.a_moneda)}</div>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 text-slate-400 max-w-[120px] truncate">{t.descripcion || "—"}</td>
                                <td className="px-3 py-2.5 text-slate-500 text-[10px]">{t.creado_por_nombre}</td>
                                <td className="px-3 py-2.5">
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => setPrintTransfer(t)}
                                      className="p-1 text-slate-500 hover:text-emerald-400 transition-colors"
                                      title="Imprimir comprobante"
                                    >
                                      <Printer className="w-3.5 h-3.5" />
                                    </button>
                                    {puedeEditar && (
                                      <button
                                        onClick={() => { setEditingTransfer(t); setShowEditModal(true); }}
                                        className="p-1 text-slate-500 hover:text-arandu-blue transition-colors"
                                        title="Editar"
                                      >
                                        <Edit2 className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    {puedeEliminar && (
                                      <button
                                        onClick={() => handleDelete(t.id)}
                                        className={`p-1 transition-colors ${
                                          deletingId === t.id
                                            ? "text-red-400"
                                            : "text-slate-500 hover:text-red-400"
                                        }`}
                                        title={deletingId === t.id ? "Clic para confirmar" : "Eliminar"}
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    <AuditoriaRegistroBtn registro={t} />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="px-3 py-2 border-t border-white/5 bg-arandu-dark/40">
                        <p className="text-slate-500 text-[10px]">
                          {transfersFiltradas.length} transferencia{transfersFiltradas.length !== 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* ── TAB: CUENTAS ────────────────────────────────────────── */}
          {activeTab === "cuentas" && (
            <motion.div
              key="cuentas"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="space-y-4"
            >
              {/* Totales */}
              <div className={`grid grid-cols-1 gap-4 ${(cuentas.some(c => c.moneda === "BRL") || activeEmpresaPropia?.monedas_habilitadas?.includes("BRL")) ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
                {[["PYG", "emerald", "₲"], ["USD", "blue", "$"], ...(cuentas.some(c => c.moneda === "BRL") || activeEmpresaPropia?.monedas_habilitadas?.includes("BRL") ? [["BRL", "green", "R$"]] : [])].map(([mon, col, sym]) => {
                  const total = cuentas.filter(c => c.moneda === mon).reduce((s, c) => s + (c.saldo_actual || 0), 0);
                  return (
                    <div key={mon} className={`bg-${col}-500/10 border border-${col}-500/20 rounded-xl p-4`}>
                      <p className={`text-${col}-300 text-xs uppercase tracking-wider`}>Saldo total {mon}</p>
                      <p className="text-white text-2xl font-bold mt-1">{fmtSaldo(total, mon)}</p>
                      <p className="text-slate-400 text-xs mt-1">{cuentas.filter(c => c.moneda === mon).length} cuenta(s)</p>
                    </div>
                  );
                })}
              </div>

              {/* Header con botón nuevo */}
              <div className="flex items-center justify-between">
                <h2 className="text-white font-semibold text-sm flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" /> Cuentas bancarias
                </h2>
                {canCreateCuenta && (
                  <button onClick={openNewCuenta} className="flex items-center gap-1.5 bg-arandu-blue hover:bg-arandu-blue-dark text-white px-3 py-1.5 rounded-lg text-xs font-medium">
                    <Plus className="w-3.5 h-3.5" /> Nueva cuenta
                  </button>
                )}
              </div>

              {loading ? (
                <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-white/3 border border-white/8 rounded-xl animate-pulse" />)}</div>
              ) : cuentas.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-white/10 rounded-xl">
                  <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-600" />
                  <p className="text-slate-400 text-sm">Sin cuentas bancarias</p>
                  {canCreateCuenta && <button onClick={openNewCuenta} className="text-arandu-blue text-sm mt-2 hover:underline">Crear la primera cuenta</button>}
                </div>
              ) : (
                <div className="space-y-2">
                  {cuentas.map(c => (
                    <div key={c.id} className="group flex items-center gap-4 p-4 bg-white/3 hover:bg-white/5 border border-white/10 rounded-xl transition-all">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${c.moneda === "USD" ? "bg-blue-500/15 text-blue-400" : c.moneda === "BRL" ? "bg-green-700/15 text-green-400" : "bg-emerald-500/15 text-emerald-400"}`}>
                        <Building2 className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium text-sm">{c.nombre}</p>
                          {c.es_predeterminada && (
                            <span className="text-[10px] bg-amber-500/15 text-amber-300 border border-amber-500/30 px-1.5 py-0.5 rounded-full">★ Predeterminada</span>
                          )}
                        </div>
                        <p className="text-slate-400 text-xs">{c.banco || "—"}{c.numero_cuenta ? ` · N° ${c.numero_cuenta}` : ""}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${(c.saldo_actual ?? 0) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{fmtSaldo(c.saldo_actual, c.moneda)}</p>
                        <p className="text-slate-500 text-[10px]">saldo actual</p>
                      </div>
                      {(canEditCuenta || canDeleteCuenta) && (
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {canEditCuenta && <button onClick={() => openEditCuenta(c)} className="text-slate-400 hover:text-arandu-blue p-1"><Edit2 className="w-4 h-4" /></button>}
                          {canDeleteCuenta && <button onClick={() => handleDeleteCuenta(c)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Procesadoras de cobro (Bancard, Dinelco, Pix…) con su % por medio */}
              <ProcesadorasCobroPanel API={API} token={token} logoTipo={activeEmpresaPropia?.slug}
                cuentas={cuentas} puedeGestionar={isAdminOrGerente || hasPermission("bancos.gestionar_procesadoras")} />
            </motion.div>
          )}

          {/* ── TAB: COTIZACIONES ───────────────────────────────────── */}
          {activeTab === "cotizaciones" && (
            <motion.div
              key="cotizaciones"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="space-y-5"
            >
              {/* Header acción */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-white font-semibold">Tipos de Cambio</h2>
                  <p className="text-slate-500 text-xs mt-0.5">Tasas de DNIT — fuente de referencia para todas las operaciones en moneda extranjera</p>
                </div>
                {canGestCotiz && (
                  <div className="flex gap-2">
                    <button
                      onClick={handleSync}
                      disabled={cotSyncing}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 text-blue-300 rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                    >
                      <RefreshCw className={`w-4 h-4 ${cotSyncing ? "animate-spin" : ""}`} />
                      {cotSyncing ? "Sincronizando..." : "Sincronizar DNIT"}
                    </button>
                    <button
                      onClick={() => { setShowManualForm(v => !v); setManualForm({ moneda: "USD", fecha: hoy(), tc_empresa: "", tc_iva: "", notas: "" }); }}
                      className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 rounded-lg text-sm font-medium transition-all"
                    >
                      <PenLine className="w-4 h-4" /> Carga manual
                    </button>
                  </div>
                )}
              </div>

              {/* Formulario manual — para cargar fechas sin sync */}
              {showManualForm && canGestCotiz && (
                <form onSubmit={handleManualSave} className="border border-amber-500/20 bg-amber-500/5 rounded-xl p-4 space-y-3">
                  <p className="text-amber-300 text-xs font-semibold flex items-center gap-1.5"><PenLine className="w-3.5 h-3.5" /> Carga manual — para fechas sin sincronización</p>
                  <p className="text-slate-500 text-[11px]">
                    El TC Empresa que cargués acá es propio de <span className="text-slate-300 font-medium">{activeEmpresaPropia?.nombre || activeEmpresaPropia?.slug || "esta empresa"}</span> — no se comparte con las demás empresas.
                    El TC IVA/Referencia (si lo cargás) sí es global, igual que el sync de DNIT.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Moneda</label>
                      <select value={manualForm.moneda} onChange={e => setManualForm(f => ({ ...f, moneda: e.target.value }))}
                        style={{ colorScheme: "dark" }}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue">
                        <option value="USD">USD</option>
                        {tieneBRL && <option value="BRL">BRL</option>}
                      </select>
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Fecha</label>
                      <DateInput value={manualForm.fecha} onChange={e => setManualForm(f => ({ ...f, fecha: e.target.value }))}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue" required />
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">TC Empresa (₲) <span className="text-red-400">*</span></label>
                      <input type="number" step="any" value={manualForm.tc_empresa} onChange={e => setManualForm(f => ({ ...f, tc_empresa: e.target.value }))}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                        placeholder={manualForm.moneda === "BRL" ? "1500" : "6200"} required />
                      <p className="text-slate-600 text-[10px] mt-0.5">Va a los documentos de {activeEmpresaPropia?.slug || "esta empresa"}</p>
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">TC IVA / Referencia (₲)</label>
                      <input type="number" step="any" value={manualForm.tc_iva} onChange={e => setManualForm(f => ({ ...f, tc_iva: e.target.value }))}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                        placeholder={manualForm.tc_empresa || (manualForm.moneda === "BRL" ? "1500" : "6100")} />
                      <p className="text-slate-600 text-[10px] mt-0.5">Opcional — si no se pone, igual al TC Empresa</p>
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Notas</label>
                      <input value={manualForm.notas} onChange={e => setManualForm(f => ({ ...f, notas: e.target.value }))}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue" placeholder="opcional" />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button type="button" onClick={() => setShowManualForm(false)} className="px-4 py-2 bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg text-sm">Cancelar</button>
                    <button type="submit" disabled={manualSaving} className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                      {manualSaving ? "Guardando..." : "Guardar"}
                    </button>
                  </div>
                </form>
              )}

              {/* Cards de hoy */}
              <div className={`grid gap-4 ${tieneBRL ? "md:grid-cols-2" : "md:grid-cols-1 max-w-lg"}`}>
                {[{ mon: "USD", sym: "$", color: "blue", label: "Dólar Estadounidense" }, ...(tieneBRL ? [{ mon: "BRL", sym: "R$", color: "green", label: "Real Brasileño" }] : [])].map(({ mon, sym, color, label }) => {
                  const c = cotHoy[mon];
                  const faltaHoy = !c;
                  const esFallback = c?.es_fallback === true;  // fin de semana/feriado
                  const editandoEmpresa = cotTcEdit[mon] !== undefined;
                  const editandoIva    = cotTcIvaEdit[mon] !== undefined;
                  const hoyFecha = new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10);
                  const tcIva     = c?.venta_mercado || c?.venta; // fallback para datos viejos
                  const tcEmpresa = c?.venta;
                  const difieren  = tcIva && tcEmpresa && Math.abs(tcEmpresa - tcIva) > 0.01;
                  return (
                    <div key={mon} className={`border rounded-xl overflow-hidden ${faltaHoy ? "border-red-500/30" : esFallback ? `border-${color}-500/10 opacity-80` : `border-${color}-500/20`}`}>

                      {/* Header */}
                      <div className={`flex items-center justify-between px-4 py-3 ${faltaHoy ? "bg-red-500/5" : `bg-${color}-500/5`}`}>
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${color}-500/15 text-${color}-400 font-bold text-sm`}>{sym}</div>
                          <div>
                            <p className="text-white font-semibold text-sm">{mon}</p>
                            <p className="text-slate-500 text-[11px]">{label}</p>
                          </div>
                        </div>
                        {faltaHoy
                          ? <span className="flex items-center gap-1 text-xs bg-red-500/15 text-red-400 border border-red-500/20 px-2 py-0.5 rounded-full"><AlertTriangle className="w-3 h-3" /> Sin datos</span>
                          : esFallback
                            ? <span className="flex items-center gap-1 text-[11px] bg-slate-500/15 text-slate-400 border border-slate-500/20 px-2 py-0.5 rounded-full" title="Mercado cerrado hoy (fin de semana o feriado)">📅 {c?.fecha}</span>
                            : c?.tc_empresa_editado
                              ? <span className="text-[11px] bg-amber-500/15 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full">TC Empresa ajustado</span>
                              : <span className="text-[11px] text-slate-600">{c?.fuente_mercado || c?.fuente || "DNIT"}</span>
                        }
                      </div>

                      {faltaHoy ? (
                        <p className="text-slate-400 text-xs px-4 py-4">{canGestCotiz ? "No hay ninguna cotización registrada. Usá Sincronizar." : "Sin datos. Contactá al administrador."}</p>
                      ) : (
                        <div className="divide-y divide-white/5">

                          {/* ── Fila 1: TC para IVA (editable, referencia DNIT) ── */}
                          <div className="flex items-center justify-between px-4 py-3 gap-3">
                            <div className="shrink-0">
                              <p className="text-slate-400 text-xs font-medium">TC para IVA</p>
                              <p className="text-slate-600 text-[10px] mt-0.5">DNIT · fiscal</p>
                              {c?.compra && (
                                <p className="text-slate-600 text-[10px] mt-0.5">Compra: ₲ {fmtTC(c.compra)}</p>
                              )}
                            </div>
                            {editandoIva ? (
                              <div className="flex items-center gap-1.5 ml-auto">
                                <input
                                  type="number" step="any" autoFocus
                                  value={cotTcIvaEdit[mon]}
                                  onChange={e => setCotTcIvaEdit(s => ({ ...s, [mon]: e.target.value }))}
                                  onKeyDown={e => { if (e.key === "Enter") handleTcIvaEdit(mon, hoyFecha); if (e.key === "Escape") setCotTcIvaEdit(s => { const n={...s}; delete n[mon]; return n; }); }}
                                  className={`w-28 bg-arandu-dark border border-slate-500/50 rounded-lg px-2 py-1 text-white font-mono text-sm focus:outline-none focus:border-slate-400`}
                                />
                                <button onClick={() => handleTcIvaEdit(mon, hoyFecha)} disabled={cotTcIvaSaving[mon]}
                                  className="px-2.5 py-1 bg-slate-600/80 hover:bg-slate-500 text-white rounded-lg text-xs font-medium disabled:opacity-50">
                                  {cotTcIvaSaving[mon] ? "..." : "OK"}
                                </button>
                                <button onClick={() => setCotTcIvaEdit(s => { const n={...s}; delete n[mon]; return n; })}
                                  className="px-2 py-1 bg-white/10 hover:bg-white/15 text-slate-300 rounded-lg text-xs">✕</button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 ml-auto">
                                <p className="text-slate-200 font-mono text-xl font-semibold">₲ {fmtTC(tcIva)}</p>
                                {canGestCotiz && (
                                  <button
                                    onClick={() => setCotTcIvaEdit(s => ({ ...s, [mon]: String(tcIva || "") }))}
                                    title="Ajustar TC IVA para hoy"
                                    className="p-1.5 hover:bg-white/10 rounded-lg text-slate-600 hover:text-slate-300 transition-colors"
                                  >
                                    <PenLine className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>

                          {/* ── Fila 2: TC Empresa (editable, el más importante) ── */}
                          <div className={`flex items-center justify-between px-4 py-3 gap-3 ${difieren ? `bg-${color}-500/5` : ""}`}>
                            <div className="shrink-0">
                              <p className={`text-${color}-300 text-xs font-medium`}>TC Empresa</p>
                              <p className="text-slate-600 text-[10px] mt-0.5">Se usa en documentos</p>
                            </div>
                            {editandoEmpresa ? (
                              <div className="flex items-center gap-1.5 ml-auto">
                                <input
                                  type="number" step="any" autoFocus
                                  value={cotTcEdit[mon]}
                                  onChange={e => setCotTcEdit(s => ({ ...s, [mon]: e.target.value }))}
                                  onKeyDown={e => { if (e.key === "Enter") handleTcEmpresaEdit(mon, hoyFecha); if (e.key === "Escape") setCotTcEdit(s => { const n={...s}; delete n[mon]; return n; }); }}
                                  className={`w-28 bg-arandu-dark border border-${color}-500/50 rounded-lg px-2 py-1 text-white font-mono text-sm focus:outline-none focus:border-${color}-400`}
                                />
                                <button onClick={() => handleTcEmpresaEdit(mon, hoyFecha)} disabled={cotTcSaving[mon]}
                                  className="px-2.5 py-1 bg-emerald-600/80 hover:bg-emerald-500 text-white rounded-lg text-xs font-medium disabled:opacity-50">
                                  {cotTcSaving[mon] ? "..." : "OK"}
                                </button>
                                <button onClick={() => setCotTcEdit(s => { const n={...s}; delete n[mon]; return n; })}
                                  className="px-2 py-1 bg-white/10 hover:bg-white/15 text-slate-300 rounded-lg text-xs">✕</button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 ml-auto">
                                <div className="text-right">
                                  <p className={`text-${color}-300 font-mono text-2xl font-bold`}>₲ {fmtTC(tcEmpresa)}</p>
                                  {difieren && (
                                    <p className="text-slate-500 text-[10px] mt-0.5">
                                      Sugerido: ₲ {fmtTC(tcIva)}
                                      <span className={`ml-1 ${tcEmpresa > tcIva ? "text-emerald-600" : "text-red-600"}`}>
                                        ({tcEmpresa > tcIva ? "+" : ""}{(((tcEmpresa - tcIva) / tcIva) * 100).toFixed(2)}%)
                                      </span>
                                    </p>
                                  )}
                                </div>
                                {canGestCotiz && (
                                  <button
                                    onClick={() => setCotTcEdit(s => ({ ...s, [mon]: String(tcEmpresa || "") }))}
                                    title="Ajustar TC Empresa para hoy"
                                    className="p-1.5 hover:bg-white/10 rounded-lg text-slate-500 hover:text-white transition-colors"
                                  >
                                    <PenLine className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>

                          {c?.notas && (
                            <div className="px-4 py-2">
                              <p className="text-slate-500 text-[11px] italic">{c.notas}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Filtros historial */}
              <div className="border border-white/10 rounded-xl p-4 bg-white/3 space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-white font-semibold text-sm flex-1">Historial</p>
                  <div className="flex flex-wrap gap-2">
                    <DateInput value={cotFiltroDesde} onChange={e => setCotFiltroDesde(e.target.value)}
                      className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-arandu-blue" />
                    <DateInput value={cotFiltroHasta} onChange={e => setCotFiltroHasta(e.target.value)}
                      className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-arandu-blue" />
                    <select value={cotFiltroMoneda} onChange={e => setCotFiltroMoneda(e.target.value)}
                      className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-arandu-blue">
                      <option value="">Todas</option>
                      <option value="USD">USD</option>
                      {tieneBRL && <option value="BRL">BRL</option>}
                    </select>
                    <button onClick={loadCotizaciones} className="px-3 py-1.5 bg-arandu-blue/20 hover:bg-arandu-blue/30 text-blue-300 border border-arandu-blue/30 rounded-lg text-xs">
                      <Search className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {cotLoading ? (
                  <p className="text-slate-500 text-sm text-center py-4">Cargando...</p>
                ) : cotHistorial.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-4">Sin datos. Usá Sincronizar para cargar el historial.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left text-slate-500 font-medium py-2 pr-4">Fecha</th>
                          <th className="text-left text-slate-500 font-medium py-2 pr-4">Moneda</th>
                          <th className="text-left text-slate-500 font-medium py-2 pr-4">Ámbito</th>
                          <th className="text-right text-slate-500 font-medium py-2 pr-4">TC IVA</th>
                          <th className="text-right text-slate-500 font-medium py-2 pr-4">TC Empresa</th>
                          <th className="text-right text-slate-500 font-medium py-2 pr-4">Diferencia</th>
                          <th className="text-left text-slate-500 font-medium py-2">Fuente</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cotHistorial.map((c, i) => {
                          const esGlobal = !c.logo_tipo;
                          // TC IVA es siempre referencia global (fiscal/DNIT). En filas propias de
                          // una empresa (logo_tipo) no aplica: esa fila solo trae su TC Empresa.
                          const tcIvaHist = esGlobal ? (c.venta_mercado || c.venta) : null;
                          const diff = esGlobal && c.venta_mercado && c.venta ? c.venta - c.venta_mercado : null;
                          return (
                            <tr key={i} className="border-b border-white/5 hover:bg-white/3">
                              <td className="py-2 pr-4 text-slate-300 font-mono">{c.fecha}</td>
                              <td className="py-2 pr-4">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.moneda === "USD" ? "bg-blue-500/15 text-blue-300" : "bg-green-500/15 text-green-300"}`}>{c.moneda}</span>
                              </td>
                              <td className="py-2 pr-4">
                                {esGlobal
                                  ? <span className="px-2 py-0.5 rounded-full text-xs bg-slate-500/15 text-slate-400">Global (DNIT/IVA)</span>
                                  : <span className="px-2 py-0.5 rounded-full text-xs bg-purple-500/15 text-purple-300">{c.logo_tipo}</span>
                                }
                              </td>
                              <td className="py-2 pr-4 text-right text-slate-500 font-mono">
                                {tcIvaHist ? `₲ ${fmtTC(tcIvaHist)}` : "—"}
                                {esGlobal && !c.venta_mercado && c.venta && <span className="ml-1 text-slate-600 text-[10px]" title="Dato anterior a la separación de tasas">≈</span>}
                              </td>
                              <td className="py-2 pr-4 text-right font-mono font-semibold text-white">
                                ₲ {fmtTC(c.venta)}
                                {c.tc_empresa_editado && <span className="ml-1 text-amber-400">✎</span>}
                              </td>
                              <td className="py-2 pr-4 text-right font-mono">
                                {diff !== null && diff !== 0
                                  ? <span className={diff > 0 ? "text-emerald-400" : "text-red-400"}>{diff > 0 ? "+" : ""}₲ {fmtTC(diff)}</span>
                                  : <span className="text-slate-600">—</span>
                                }
                              </td>
                              <td className="py-2 text-slate-500">{c.fuente === "manual" ? <span className="text-amber-400">Manual</span> : c.fuente || "DNIT"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* ── TAB: PAGOS A PROVEEDORES ─────────────────────────── */}
          {activeTab === "pagos_proveedores" && (
            <motion.div key="pagos_proveedores" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
              {/* Filtro de período */}
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {[["todos","Todo"], ["dia","Día"], ["mes","Mes"], ["anio","Año"]].map(([v,l]) => (
                  <button key={v} onClick={() => setPpFiltroTipo(v)} className={`px-3 py-1.5 rounded-lg text-sm border transition-all ${ppFiltroTipo===v ? "bg-violet-600 border-violet-500 text-white" : "border-white/10 text-slate-400 hover:text-white"}`}>{l}</button>
                ))}
                {ppFiltroTipo === "dia" && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg px-1 py-0.5">
                      <button onClick={() => setPpFiltroDia(prevDiaFin(ppFiltroDia))} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-white text-xs font-medium min-w-[110px] text-center px-1 capitalize">{diaLabelFin(ppFiltroDia)}</span>
                      <button onClick={() => setPpFiltroDia(nextDiaFin(ppFiltroDia))} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {ppFiltroDia !== hoyFin() && (
                      <button onClick={() => setPpFiltroDia(hoyFin())} className="text-xs text-violet-400 hover:text-violet-300 transition-colors">Hoy</button>
                    )}
                  </div>
                )}
                {ppFiltroTipo === "mes" && (
                  <input type="month" value={ppFiltroMes} onChange={e => setPpFiltroMes(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-violet-500" />
                )}
                {ppFiltroTipo === "anio" && (
                  <input type="number" min="2020" max="2040" value={ppFiltroAnio} onChange={e => setPpFiltroAnio(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-violet-500 w-24" />
                )}
              </div>

              {/* Toolbar chips */}
              <div className="mb-5">
                <ChipSearch
                  chips={pagoProvChips} setChips={setPagoProvChips}
                  inputVal={pagoProvInput} setInputVal={setPagoProvInput}
                  placeholder="Buscar por proveedor, concepto, monto, fecha, pagado, pendiente, vencido… (Enter para agregar filtro)"
                  accentColor="violet"
                  actionButton={(
                    <div className="flex gap-2">
                      {ppCanViewDeleted && (
                        <button
                          onClick={() => setMostrarEliminadasPagosProv(v => !v)}
                          className={`px-3 py-2 rounded-lg text-xs border flex items-center gap-1.5 ${mostrarEliminadasPagosProv ? "bg-red-500/20 border-red-500/40 text-red-300" : "border-white/10 text-slate-400 hover:text-white"}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> {mostrarEliminadasPagosProv ? "Ocultar eliminadas" : "Ver eliminadas"}
                        </button>
                      )}
                      {!mostrarEliminadasPagosProv && canCrearPago && (
                        <button
                          onClick={() => { setPagoProvForm(emptyPagoProvForm()); setEditingPagoProv(null); setComprasProvList([]); setPagoProvMedio("transferencia"); setShowPagoProvForm(true); }}
                          className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-all whitespace-nowrap"
                        >
                          <Plus className="w-4 h-4" /> Nuevo pago
                        </button>
                      )}
                    </div>
                  )}
                />
              </div>

              {/* Lista con filtro de período */}
              {(() => {
                const pagosPorPeriodo = todosPagosProveedores.filter(p => {
                  if (ppFiltroTipo === "todos") return true;
                  const fechaRef = String(p.fecha_pago || p.fecha_vencimiento || "");
                  if (!fechaRef) return false;
                  if (ppFiltroTipo === "dia")  return fechaRef.startsWith(ppFiltroDia);
                  if (ppFiltroTipo === "mes")  return fechaRef.startsWith(ppFiltroMes);
                  if (ppFiltroTipo === "anio") return fechaRef.startsWith(ppFiltroAnio);
                  return true;
                });
                return (
                  <>
                    {loadingTodosPagos ? (
                      <div className="flex items-center justify-center py-16 text-violet-400">
                        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando pagos...
                      </div>
                    ) : pagosPorPeriodo.length === 0 ? (
                      <div className="text-center py-16 bg-arandu-dark-light border border-white/5 rounded-xl">
                        <Wallet className="w-14 h-14 text-slate-700 mx-auto mb-3" />
                        <p className="text-slate-400 mb-2">
                          {todosPagosProveedores.length === 0
                            ? (mostrarEliminadasPagosProv ? "No hay pagos a proveedores eliminados" : "No hay pagos a proveedores registrados")
                            : `No hay pagos en este período (${ppFiltroTipo === "dia" ? diaLabelFin(ppFiltroDia) : ppFiltroTipo === "mes" ? mesLabel(ppFiltroMes) : ppFiltroTipo === "anio" ? ppFiltroAnio : "todo el tiempo"})`}
                        </p>
                        {!mostrarEliminadasPagosProv && canCrearPago && todosPagosProveedores.length === 0 && (
                          <button onClick={() => setShowPagoProvForm(true)} className="text-sm text-violet-400 hover:underline">
                            Registrar primer pago
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {pagosPorPeriodo
                          .filter(p => {
                            const comprasTexto = (p.compras_detalle || []).map(c => [c.numero_factura, c.fecha, c.concepto, c.monto_pagado, c.monto_total].filter(Boolean).join(" ")).join(" ");
                            const texto = [p.proveedor_nombre, p.concepto, comprasTexto, String(p.monto||""), p.fecha_pago, p.fecha_vencimiento, p.cuenta_nombre, p.moneda, p.estado].filter(Boolean).join(" ");
                            return matchChips(pagoProvChips, pagoProvInput, texto);
                          })
                          .map((pago, i) => {
                            const comprasDetalle = pago.compras_detalle || [];
                            const estadoBadge = {
                              pagado:   "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
                              pendiente:"bg-blue-500/15 text-blue-400 border-blue-500/30",
                              vencido:  "bg-red-500/15 text-red-400 border-red-500/30",
                            }[pago.estado] || "bg-slate-500/15 text-slate-400 border-slate-500/30";
                            return (
                              <motion.div
                                key={pago.id || i}
                                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                                onClick={() => setSelectedPagoView(pago)}
                                className={`bg-arandu-dark-light border rounded-xl p-4 hover:bg-white/[0.03] cursor-pointer transition-all ${pago.eliminada ? "border-red-500/20 bg-red-900/10" : "border-white/5 hover:border-violet-500/40"}`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className="w-10 h-10 bg-violet-500/15 rounded-lg flex items-center justify-center flex-shrink-0">
                                      <Banknote className="w-5 h-5 text-violet-400" />
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-white font-medium truncate">{pago.proveedor_nombre}</p>
                                      {pago.eliminada && <span className="inline-flex mt-1 text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/30">Eliminado</span>}
                                      <p className="text-slate-400 text-sm truncate">{pago.concepto}</p>
                                      {comprasDetalle.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                          {comprasDetalle.slice(0, 3).map(c => (
                                            <span key={`${pago.id}-${c.id}`} className={`text-[10px] px-2 py-0.5 rounded-full border ${c.eliminada ? "bg-red-500/10 text-red-300 border-red-500/20" : "bg-blue-500/10 text-blue-300 border-blue-500/20"}`}>
                                              {compraPagoLabel(c)} · {fmtNum(c.monto_pagado || c.monto_total || 0, c.moneda || pago.moneda)}
                                            </span>
                                          ))}
                                          {comprasDetalle.length > 3 && (
                                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/10">
                                              +{comprasDetalle.length - 3} más
                                            </span>
                                          )}
                                        </div>
                                      )}
                                      <p className="text-slate-600 text-xs mt-0.5">
                                        Vence: {pago.fecha_vencimiento}
                                        {pago.fecha_pago && <span className="ml-2 text-emerald-600">· Pagado: {pago.fecha_pago}</span>}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3 flex-shrink-0">
                                    <div className="text-right">
                                      <p className="text-white font-bold">{fmtNum(pago.monto, pago.moneda)}</p>
                                      <span className={`text-xs px-2 py-0.5 rounded-full border ${estadoBadge}`}>{pago.estado}</span>
                                    </div>
                                    <div className="flex flex-col gap-1 items-center" onClick={e => e.stopPropagation()}>
                                      {!mostrarEliminadasPagosProv && canEliminarPago && (
                                        <button onClick={() => handleDeletePagoProv(pago.id)} className="text-slate-500 hover:text-red-400 transition-colors" title="Eliminar pago">
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      )}
                                      <AuditoriaRegistroBtn registro={pago} />
                                      {!mostrarEliminadasPagosProv && (pago.estado === "pendiente" || pago.estado === "vencido") && (
                                        <button onClick={() => handleMarcarPagado(pago.id)} className="text-slate-500 hover:text-emerald-400 transition-colors" title="Marcar como pagado">
                                          <Check className="w-4 h-4" />
                                        </button>
                                      )}
                                      {!mostrarEliminadasPagosProv && pago.estado === "pagado" && (
                                        <button onClick={() => setShowPagoProvDoc(pago)} className="text-slate-500 hover:text-violet-400 transition-colors" title="Imprimir comprobante">
                                          <Printer className="w-4 h-4" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            );
                          })}
                      </div>
                    )}
                  </>
                );
              })()}
            </motion.div>
          )}

          {/* ── TAB: PRÉSTAMOS ──────────────────────────────────────── */}
          {activeTab === "prestamos" && (
            <motion.div key="prestamos" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="space-y-4">

              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex gap-1 bg-white/5 rounded-lg p-1">
                  {[["todos","Todos"],["bancario","Bancarios"],["funcionario","Empleados"],["proveedor","Proveedores"]].map(([v,l]) => (
                    <button key={v} onClick={() => setPrestFiltroTipo(v)}
                      className={`px-3 py-1 rounded text-xs font-medium transition-all ${prestFiltroTipo === v ? "bg-arandu-blue text-white" : "text-slate-400 hover:text-white"}`}>{l}</button>
                  ))}
                </div>
                <div className="flex gap-1 bg-white/5 rounded-lg p-1">
                  {[["activo","Activos"],["finalizado","Finalizados"],["cancelado","Cancelados"],["todos","Todos"]].map(([v,l]) => (
                    <button key={v} onClick={() => setPrestFiltroEstado(v)}
                      className={`px-3 py-1 rounded text-xs font-medium transition-all ${prestFiltroEstado === v ? "bg-arandu-blue text-white" : "text-slate-400 hover:text-white"}`}>{l}</button>
                  ))}
                </div>
                <button onClick={fetchPrestamos} className="p-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors" title="Actualizar"><RefreshCcw className="w-3.5 h-3.5" /></button>
                <div className="ml-auto">
                  {canCrearPrestamos && (
                    <button onClick={() => { setPrestForm(emptyPrestForm()); setPrestPreview([]); setShowPrestForm(true); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-arandu-blue hover:bg-arandu-blue/80 text-white rounded-lg text-sm font-medium transition-colors">
                      <Plus className="w-4 h-4" /> Nuevo préstamo
                    </button>
                  )}
                </div>
              </div>

              {/* Lista */}
              {prestLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-arandu-blue animate-spin" /></div>
              ) : prestamos.length === 0 ? (
                <div className="text-center py-16 text-slate-500">
                  <Banknote className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Sin préstamos registrados</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {prestamos.map(p => {
                    const expanded = expandedPrest === p.id;
                    const cuotasPagadas = p.cuotas?.filter(c => c.estado === "pagada").length || 0;
                    const totalCuotas = p.n_cuotas || 0;
                    const montoPendiente = p.cuotas?.filter(c => c.estado !== "pagada").reduce((s, c) => s + (c.monto_total || 0), 0) || 0;
                    const simboloMonedaPrest = p.moneda === "PYG" ? "₲" : p.moneda === "USD" ? "$" : "R$";
                    return (
                      <div key={p.id} className="border border-white/10 rounded-xl bg-white/3 overflow-hidden">
                        {/* Header */}
                        <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/3 transition-colors"
                          onClick={() => setExpandedPrest(expanded ? null : p.id)}>
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${p.tipo === "bancario" ? "bg-blue-500/15" : p.tipo === "proveedor" ? "bg-amber-500/15" : "bg-violet-500/15"}`}>
                            {p.tipo === "bancario" ? <Building2 className="w-4 h-4 text-blue-400" /> : p.tipo === "proveedor" ? <Receipt className="w-4 h-4 text-amber-400" /> : <Users className="w-4 h-4 text-violet-400" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-white font-semibold text-sm">{p.alias || p.entidad_nombre}</span>
                              {prestEstadoBadge(p)}
                              <span className="text-slate-500 text-[10px]">{p.tipo === "bancario" ? "Bancario recibido" : p.tipo === "proveedor" ? "Proveedor otorgado" : "Empleado otorgado"}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                              {p.alias && p.alias !== p.entidad_nombre && <span className="text-slate-500 text-xs">{p.entidad_nombre}</span>}
                              {p.referencia && <span className="text-blue-300 text-xs font-mono">Ref: {p.referencia}</span>}
                              {p.destino && <span className="text-slate-500 text-xs">{p.destino}</span>}
                              <span className="text-slate-300 text-xs font-mono">
                                {simboloMonedaPrest} {Number(p.monto_total).toLocaleString("es-PY")}
                              </span>
                              <span className="text-slate-500 text-xs">{cuotasPagadas}/{totalCuotas} cuotas</span>
                              <span className="text-slate-500 text-xs">{p.tasa_interes_mensual > 0 ? `${p.tasa_interes_mensual}% mensual` : "Sin interés"}</span>
                              {p.gastos_administrativos > 0 && <span className="text-slate-500 text-xs">Admin: {simboloMonedaPrest} {Number(p.gastos_administrativos).toLocaleString("es-PY")}</span>}
                              {p.iva_cuotas_pct > 0 && <span className="text-slate-500 text-xs">IVA: {p.iva_cuotas_pct}%</span>}
                              <span className="text-slate-500 text-xs">desde {p.fecha_inicio}</span>
                              {p.estado === "activo" && montoPendiente > 0 && (
                                <span className="text-amber-400 text-xs font-mono">Pendiente: {simboloMonedaPrest} {Math.round(montoPendiente).toLocaleString("es-PY")}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <AuditoriaRegistroBtn registro={p} />
                            {canCancelarPrestamos && p.estado === "activo" && (
                              <button onClick={(e) => { e.stopPropagation(); handleCancelarPrestamo(p.id); }}
                                className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors text-xs"
                                title="Cancelar préstamo"><X className="w-3.5 h-3.5" /></button>
                            )}
                            {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                          </div>
                        </div>

                        {/* Tabla de cuotas */}
                        {expanded && (
                          <div className="border-t border-white/5 px-4 pb-3 pt-2">
                            <p className="text-slate-500 text-[10px] font-medium uppercase tracking-wider mb-2">Cuotas</p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-slate-500 border-b border-white/5">
                                    <th className="text-left pb-1.5 pr-3">#</th>
                                    <th className="text-left pb-1.5 pr-3">Vencimiento</th>
                                    <th className="text-right pb-1.5 pr-3">Capital</th>
                                    <th className="text-right pb-1.5 pr-3">Interés</th>
                                    <th className="text-right pb-1.5 pr-3">Admin</th>
                                    <th className="text-right pb-1.5 pr-3">IVA</th>
                                    <th className="text-right pb-1.5 pr-3">Total</th>
                                    <th className="text-center pb-1.5 pr-3">Estado</th>
                                    <th className="text-left pb-1.5">Fecha pago</th>
                                    {canPagarPrestamos && <th className="pb-1.5"></th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {(p.cuotas || []).map(c => {
                                    const esVencida = c.estado === "pendiente" && c.fecha_vencimiento < hoy();
                                    return (
                                      <tr key={c.numero} className={`border-b border-white/3 last:border-0 ${c.estado === "pagada" ? "opacity-50" : ""}`}>
                                        <td className="py-1.5 pr-3 text-slate-400">{c.numero}</td>
                                        <td className={`py-1.5 pr-3 ${esVencida ? "text-red-400 font-semibold" : "text-slate-300"}`}>{c.fecha_vencimiento}</td>
                                        <td className="py-1.5 pr-3 text-right text-slate-300 font-mono">{Number(c.monto_capital).toLocaleString("es-PY")}</td>
                                        <td className="py-1.5 pr-3 text-right text-slate-400 font-mono">{Number(c.monto_interes).toLocaleString("es-PY")}</td>
                                        <td className="py-1.5 pr-3 text-right text-slate-400 font-mono">{Number(c.monto_gasto_administrativo || 0).toLocaleString("es-PY")}</td>
                                        <td className="py-1.5 pr-3 text-right text-slate-400 font-mono">{Number(c.monto_iva || 0).toLocaleString("es-PY")}</td>
                                        <td className="py-1.5 pr-3 text-right text-white font-mono font-semibold">
                                          {Number(c.monto_pagado_neto ?? c.monto_total).toLocaleString("es-PY")}
                                          {Number(c.descuento_pronto_pago || 0) > 0 && (
                                            <div className="text-[9px] text-amber-300">desc. {Number(c.descuento_pronto_pago).toLocaleString("es-PY")}</div>
                                          )}
                                        </td>
                                        <td className="py-1.5 pr-3 text-center">{cuotaEstadoBadge(c)}</td>
                                        <td className="py-1.5 text-slate-500">{c.fecha_pago || "—"}</td>
                                        {canPagarPrestamos && (
                                          <td className="py-1.5 pl-2">
                                            {c.estado !== "pagada" && p.estado === "activo" && (
                                              <button onClick={() => { setPagarCuotaModal({ prestamo: p, cuota: c }); setPagarForm(resetPagarPrestamoForm()); }}
                                                className="px-2 py-0.5 bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 rounded text-[10px] font-medium transition-colors">
                                                Pagar
                                              </button>
                                            )}
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Modal: Crear préstamo */}
              {showPrestForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                  <div className="bg-arandu-dark border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between p-5 border-b border-white/10">
                      <h3 className="text-white font-bold text-base flex items-center gap-2"><Banknote className="w-5 h-5 text-arandu-blue" /> Nuevo préstamo</h3>
                      <button onClick={() => { setShowPrestForm(false); setPrestPreview([]); }} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
                    </div>
                    <div className="p-5 space-y-4">
                      {/* Tipo */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {[
                          ["bancario","Bancario","El banco/financiera le presta dinero a la empresa", canCrearPrestBancario],
                          ["funcionario","A empleado","La empresa le presta dinero a un empleado", canCrearPrestEmpleado],
                          ["proveedor","A proveedor","La empresa le presta dinero a un proveedor", canCrearPrestProveedor],
                        ].filter(([, , , ok]) => ok).map(([v,l,d]) => (
                          <button key={v} onClick={() => setPrestForm(s => ({ ...s, tipo: v, entidad_nombre: "", entidad_id: "" }))}
                            className={`p-3 rounded-xl border text-left transition-all ${prestForm.tipo === v ? "border-arandu-blue bg-arandu-blue/10" : "border-white/10 hover:border-white/20"}`}>
                            <p className={`text-sm font-semibold ${prestForm.tipo === v ? "text-arandu-blue" : "text-white"}`}>{l}</p>
                            <p className="text-slate-500 text-xs mt-0.5">{d}</p>
                          </button>
                        ))}
                      </div>

                      {/* Entidad */}
                      {prestForm.tipo === "funcionario" ? (
                        <div>
                          <label className="text-slate-400 text-xs mb-1 block">Empleado *</label>
                          <EntidadSearchSelect
                            entidades={prestEmpleados}
                            value={prestForm.entidad_id ? { id: prestForm.entidad_id, nombre: prestForm.entidad_nombre } : null}
                            onChange={sel => setPrestForm(s => ({ ...s, entidad_id: sel?.id || "", entidad_nombre: sel?.nombre || "" }))}
                            placeholder="Buscar empleado por nombre..."
                          />
                        </div>
                      ) : (
                        <div>
                          <label className="text-slate-400 text-xs mb-1 block">{prestForm.tipo === "bancario" ? "Banco / entidad financiera *" : "Proveedor *"}</label>
                          <EntidadSearchSelect
                            entidades={prestForm.tipo === "bancario" ? prestEntidadesBancarias : prestProveedores}
                            value={prestForm.entidad_id ? { id: prestForm.entidad_id, nombre: prestForm.entidad_nombre } : null}
                            onChange={sel => setPrestForm(s => ({ ...s, entidad_id: sel?.id || "", entidad_nombre: sel?.nombre || "" }))}
                            placeholder={prestForm.tipo === "bancario" ? "Buscar banco o financiera..." : "Buscar proveedor..."}
                          />
                          {prestForm.tipo === "bancario" && prestEntidadesBancarias.length === 0 && (
                            <p className="text-cyan-300/80 text-xs mt-1">
                              Creá primero el banco/financiera en Catastro como proveedor y marcá Entidad bancaria.
                            </p>
                          )}
                        </div>
                      )}

                      {/* Identificación */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="text-slate-400 text-xs mb-1 block">
                            Referencia / operación {prestForm.tipo === "bancario" ? "*" : ""}
                          </label>
                          <input
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                            value={prestForm.referencia}
                            onChange={e => setPrestForm(s => ({ ...s, referencia: e.target.value }))}
                            placeholder={prestForm.tipo === "bancario" ? "Contrato, pagaré u operación" : "Opcional"}
                          />
                        </div>
                        <div>
                          <label className="text-slate-400 text-xs mb-1 block">Alias visible</label>
                          <input
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                            value={prestForm.alias}
                            onChange={e => setPrestForm(s => ({ ...s, alias: e.target.value }))}
                            placeholder="Ej: Ueno capital operativo"
                          />
                        </div>
                        <div>
                          <label className="text-slate-400 text-xs mb-1 block">Destino</label>
                          <input
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                            value={prestForm.destino}
                            onChange={e => setPrestForm(s => ({ ...s, destino: e.target.value }))}
                            placeholder="Ej: capital, vehículo"
                          />
                        </div>
                      </div>

                      {/* Monto + Moneda */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                          <label className="text-slate-400 text-xs mb-1 block">Monto total *</label>
                          <MoneyInput moneda={prestForm.moneda} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                            value={prestForm.monto_total} onChange={value => setPrestForm(s => ({ ...s, monto_total: value }))}
                            placeholder="0" />
                        </div>
                        <div>
                          <label className="text-slate-400 text-xs mb-1 block">Moneda</label>
                          <select className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                            value={prestForm.moneda} onChange={e => setPrestForm(s => ({ ...s, moneda: e.target.value }))}>
                            <option value="PYG">₲ PYG</option>
                            <option value="USD">$ USD</option>
                            <option value="BRL">R$ BRL</option>
                          </select>
                        </div>
                      </div>

                      {/* Tasa + Cuotas + Fecha */}
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="text-slate-400 text-xs mb-1 block">Tasa mensual (%)</label>
                          <input type="number" min={0} step={0.01} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                            value={prestForm.tasa_interes_mensual} onChange={e => setPrestForm(s => ({ ...s, tasa_interes_mensual: e.target.value }))}
                            placeholder="0" />
                          <p className="text-slate-600 text-[10px] mt-0.5">0 = sin interés</p>
                        </div>
                        <div>
                          <label className="text-slate-400 text-xs mb-1 block">Cantidad de cuotas *</label>
                          <input type="number" min={1} max={360} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                            value={prestForm.n_cuotas} onChange={e => setPrestForm(s => ({ ...s, n_cuotas: e.target.value }))} />
                        </div>
                        <div>
                          <label className="text-slate-400 text-xs mb-1 block">Fecha de inicio *</label>
                          <DateInput className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                            value={prestForm.fecha_inicio} onChange={e => setPrestForm(s => ({ ...s, fecha_inicio: e.target.value }))} />
                        </div>
                      </div>

                      {/* Gastos + IVA + cuota manual */}
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="text-slate-400 text-xs mb-1 block">Gastos administrativos</label>
                          <MoneyInput moneda={prestForm.moneda} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                            value={prestForm.gastos_administrativos} onChange={value => setPrestForm(s => ({ ...s, gastos_administrativos: value }))}
                            placeholder="0" />
                        </div>
                        <div>
                          <label className="text-slate-400 text-xs mb-1 block">IVA de cuotas (%)</label>
                          <input type="number" min={0} step={0.01} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                            value={prestForm.iva_cuotas_pct} onChange={e => setPrestForm(s => ({ ...s, iva_cuotas_pct: e.target.value }))}
                            placeholder="0" />
                        </div>
                        <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
                          <p className="text-slate-400 text-xs mb-1">Cuotas manuales</p>
                          {canCuotaManualPrestamos ? (
                            <button
                              type="button"
                              onClick={() => setPrestForm(s => {
                                const activar = !s.usar_cuotas_manuales;
                                const cantidad = Math.max(0, Math.min(360, Number(s.n_cuotas) || 0));
                                return {
                                  ...s,
                                  usar_cuotas_manuales: activar,
                                  monto_cuota_manual: "",
                                  cuotas_manuales: activar ? Array.from({ length: cantidad }, (_, idx) => s.cuotas_manuales?.[idx] || "") : [],
                                };
                              })}
                              className={`w-full rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${prestForm.usar_cuotas_manuales ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" : "bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10"}`}
                            >
                              {prestForm.usar_cuotas_manuales ? "Plan manual activo" : "Activar plan manual"}
                            </button>
                          ) : (
                            <p className="text-slate-500 text-xs">Requiere permiso</p>
                          )}
                        </div>
                      </div>

                      {canCuotaManualPrestamos && prestForm.usar_cuotas_manuales && (
                        <div className="border border-emerald-500/20 bg-emerald-500/5 rounded-xl p-3 space-y-3">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="text-emerald-300 text-xs font-semibold">Plan manual de cuotas</p>
                              <p className="text-slate-500 text-[11px]">Cargá el monto exacto de cada cuota.</p>
                            </div>
                            <span className="text-emerald-200 text-xs font-mono">
                              Total: {fmtNum(cuotasManualesPayload.reduce((s, n) => s + (Number(n) || 0), 0), prestForm.moneda)}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
                            {normalizarCuotasManuales(prestForm.n_cuotas, prestForm.cuotas_manuales).map((monto, idx) => (
                              <div key={idx} className="grid grid-cols-2 gap-1.5">
                                <label className="block">
                                  <span className="text-slate-500 text-[10px] mb-1 block">Cuota {idx + 1}</span>
                                  <MoneyInput
                                    moneda={prestForm.moneda}
                                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-emerald-400"
                                    value={monto}
                                    onChange={value => setPrestForm(s => {
                                      const cuotas = normalizarCuotasManuales(s.n_cuotas, s.cuotas_manuales);
                                      cuotas[idx] = value;
                                      return { ...s, cuotas_manuales: cuotas };
                                    })}
                                    placeholder="0"
                                  />
                                </label>
                                <label className="block">
                                  <span className="text-slate-500 text-[10px] mb-1 block">Vencimiento</span>
                                  <DateInput
                                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-2 py-2 text-white text-xs focus:outline-none focus:border-emerald-400"
                                    value={normalizarVencimientosManuales(prestForm.n_cuotas, prestForm.cuotas_manuales_vencimientos)[idx] || ""}
                                    onChange={e => setPrestForm(s => {
                                      const vtos = normalizarVencimientosManuales(s.n_cuotas, s.cuotas_manuales_vencimientos);
                                      vtos[idx] = e.target.value;
                                      return { ...s, cuotas_manuales_vencimientos: vtos };
                                    })}
                                  />
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Cuenta bancaria */}
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">
                          {prestForm.tipo === "bancario" ? "Cuenta bancaria que recibe el desembolso *" : "Cuenta bancaria que entrega el dinero *"}
                        </label>
                        <select className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                          value={prestForm.cuenta_id} onChange={e => setPrestForm(s => ({ ...s, cuenta_id: e.target.value }))}>
                          <option value="">— Seleccionar cuenta —</option>
                          {renderCuentasPorMoneda(cuentasVisibles)}
                        </select>
                      </div>

                      {/* Notas */}
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Notas</label>
                        <input className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                          value={prestForm.notas} onChange={e => setPrestForm(s => ({ ...s, notas: e.target.value }))}
                          placeholder="Opcional" />
                      </div>

                      {/* Preview cuotas */}
                      {prestForm.monto_total && prestForm.n_cuotas && (
                        <div className="border border-white/10 rounded-xl p-3">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-slate-400 text-xs font-medium">Vista previa de cuotas</p>
                            {prestPreviewLoading && <Loader2 className="w-3.5 h-3.5 text-arandu-blue animate-spin" />}
                          </div>
                          {prestPreview.length > 0 && (
                            <>
                              <div className="flex gap-x-4 gap-y-1 mb-2 text-[10px] text-slate-500 flex-wrap">
                                <span>Cuota: <strong className="text-white">{fmtNum(Math.round(prestPreview[0]?.monto_total || 0), prestForm.moneda)}</strong></span>
                                <span>Total intereses: <strong className="text-amber-400">{fmtNum(Math.round(prestPreview.reduce((s,c) => s + (c.monto_interes || 0), 0)), prestForm.moneda)}</strong></span>
                                <span>Admin: <strong className="text-slate-300">{fmtNum(Math.round(prestPreview.reduce((s,c) => s + (c.monto_gasto_administrativo || 0), 0)), prestForm.moneda)}</strong></span>
                                <span>IVA: <strong className="text-cyan-300">{fmtNum(Math.round(prestPreview.reduce((s,c) => s + (c.monto_iva || 0), 0)), prestForm.moneda)}</strong></span>
                                <span>Total a cobrar: <strong className="text-emerald-300">{fmtNum(Math.round(prestPreview.reduce((s,c) => s + (c.monto_total || 0), 0)), prestForm.moneda)}</strong></span>
                              </div>
                              <div className="max-h-36 overflow-y-auto space-y-0.5">
                                {prestPreview.slice(0, 6).map(c => (
                                  <div key={c.numero} className="flex items-center gap-2 text-[10px] py-0.5">
                                    <span className="text-slate-500 w-4">{c.numero}</span>
                                    <span className="text-slate-400 w-20">{c.fecha_vencimiento}</span>
                                    <span className="text-slate-300 font-mono w-24 text-right">{fmtNum(Number(c.monto_total), prestForm.moneda)}</span>
                                    {c.monto_interes > 0 && <span className="text-slate-600 font-mono">(int: {Number(c.monto_interes).toLocaleString("es-PY")})</span>}
                                    {c.monto_iva > 0 && <span className="text-slate-600 font-mono">(iva: {Number(c.monto_iva).toLocaleString("es-PY")})</span>}
                                  </div>
                                ))}
                                {prestPreview.length > 6 && <p className="text-slate-600 text-[10px] pl-4">… y {prestPreview.length - 6} más</p>}
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      <div className="flex justify-end gap-3 pt-1">
                        <button onClick={() => { setShowPrestForm(false); setPrestPreview([]); }}
                          className="px-4 py-2 text-sm text-slate-400 hover:text-white border border-white/10 rounded-lg transition-colors">Cancelar</button>
                        <button onClick={handleCrearPrestamo} disabled={prestSaving}
                          className="flex items-center gap-2 px-4 py-2 bg-arandu-blue hover:bg-arandu-blue/80 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50">
                          {prestSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Registrar préstamo</>}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal: Pagar cuota */}
              {pagarCuotaModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                  <div className="bg-arandu-dark border border-white/10 rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
                    <div className="flex items-center justify-between p-5 border-b border-white/10">
                      <h3 className="text-white font-bold text-sm flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                        Pagar cuota {pagarCuotaModal.cuota.numero} / {pagarCuotaModal.prestamo.n_cuotas}
                      </h3>
                      <button onClick={() => setPagarCuotaModal(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
                    </div>
                    <div className="p-5 space-y-4">
                      <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-slate-300">
                        <input
                          type="checkbox"
                          checked={pagarForm.descuento_aplicar_total}
                          onChange={e => setPagarForm(s => ({ ...s, descuento_aplicar_total: e.target.checked }))}
                          className="mt-1 accent-emerald-500"
                        />
                        <span>
                          <span className="block text-white font-semibold">Aplicar al saldo pendiente del préstamo</span>
                          <span className="text-xs text-slate-500">Si está activo, este pago liquida todas las cuotas pendientes.</span>
                        </span>
                      </label>

                      <div className="bg-white/3 rounded-xl p-3 space-y-3 text-xs">
                        <div className="grid grid-cols-5 gap-3 text-center">
                          <div><p className="text-slate-500">Cuotas</p><p className="text-white font-mono">{cuotasPagoPrestamo.length}</p></div>
                          <div><p className="text-slate-500">Capital</p><p className="text-white font-mono">₲ {Math.round(cuotasPagoPrestamo.reduce((s,c)=>s+Number(c.monto_capital||0),0)).toLocaleString("es-PY")}</p></div>
                          <div><p className="text-slate-500">Interés</p><p className="text-amber-400 font-mono">₲ {Math.round(cuotasPagoPrestamo.reduce((s,c)=>s+Number(c.monto_interes||0),0)).toLocaleString("es-PY")}</p></div>
                          <div><p className="text-slate-500">IVA/Admin</p><p className="text-cyan-300 font-mono">₲ {Math.round(cuotasPagoPrestamo.reduce((s,c)=>s+Number(c.monto_iva||0)+Number(c.monto_gasto_administrativo||0),0)).toLocaleString("es-PY")}</p></div>
                          <div><p className="text-slate-500">Total</p><p className="text-white font-bold font-mono">₲ {Math.round(totalPagoPrestamo).toLocaleString("es-PY")}</p></div>
                        </div>
                        {pagarCuotaModal.prestamo?.tipo === "funcionario" && (
                          <div className="flex items-center justify-between border-t border-white/10 pt-3">
                            <span className="text-slate-400">Interés exonerable por fecha de pago</span>
                            <button
                              type="button"
                              disabled={!canDescPrestEmpleadoInteres || interesExonerablePrestamo <= 0}
                              onClick={() => setPagarForm(s => ({ ...s, descuento_tipo: "monto", descuento_monto: String(Math.round(interesExonerablePrestamo)), descuento_porcentaje: "" }))}
                              className="text-amber-300 hover:text-amber-200 disabled:opacity-40 disabled:hover:text-amber-300 font-mono"
                            >
                              ₲ {Math.round(interesExonerablePrestamo).toLocaleString("es-PY")}
                            </button>
                          </div>
                        )}
                      </div>

                      {puedeDescuentoPrestamo && (
                        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 space-y-3">
                          <div className="flex items-center justify-between gap-3">
                            <label className="text-amber-200 text-xs font-semibold">Descuento por pronto pago</label>
                            <select
                              value={pagarForm.descuento_tipo}
                              onChange={e => setPagarForm(s => ({ ...s, descuento_tipo: e.target.value }))}
                              className="bg-black/20 border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
                            >
                              <option value="monto">Monto</option>
                              <option value="porcentaje">Porcentaje</option>
                            </select>
                          </div>
                          {pagarForm.descuento_tipo === "porcentaje" ? (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-400"
                              value={pagarForm.descuento_porcentaje}
                              onChange={e => setPagarForm(s => ({ ...s, descuento_porcentaje: e.target.value }))}
                              placeholder="Ej: 5"
                            />
                          ) : (
                            <MoneyInput
                              moneda={pagarCuotaModal?.prestamo?.moneda}
                              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-400"
                              value={pagarForm.descuento_monto}
                              onChange={value => setPagarForm(s => ({ ...s, descuento_monto: value }))}
                              placeholder="₲ 0"
                            />
                          )}
                          <input
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-400"
                            value={pagarForm.descuento_motivo}
                            onChange={e => setPagarForm(s => ({ ...s, descuento_motivo: e.target.value }))}
                            placeholder="Motivo / acuerdo / referencia"
                          />
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div className="rounded-lg bg-black/15 p-2">
                              <p className="text-slate-500">Descuento</p>
                              <p className="text-amber-300 font-mono font-bold">₲ {Math.round(descuentoPrestamoMonto).toLocaleString("es-PY")}</p>
                            </div>
                            <div className="rounded-lg bg-emerald-500/10 p-2">
                              <p className="text-slate-500">Neto a pagar</p>
                              <p className="text-emerald-300 font-mono font-bold">₲ {Math.round(netoPagoPrestamo).toLocaleString("es-PY")}</p>
                            </div>
                          </div>
                          {pagarCuotaModal.prestamo?.tipo === "funcionario" && descuentoPrestamoMonto > interesExonerablePrestamo && (
                            <p className="text-[11px] text-orange-300">
                              El descuento supera el interés exonerable. Se requiere permiso de descuento extra.
                            </p>
                          )}
                        </div>
                      )}

                      {!puedeDescuentoPrestamo && (
                        <div className="rounded-xl border border-white/10 bg-white/3 p-3 text-xs text-slate-500">
                          Sin permisos de descuento para este tipo de préstamo.
                        </div>
                      )}
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Cuenta bancaria *</label>
                        <select className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                          value={pagarForm.cuenta_pago_id} onChange={e => setPagarForm(s => ({ ...s, cuenta_pago_id: e.target.value }))}>
                          <option value="">— Seleccionar —</option>
                          {renderCuentasPorMoneda(cuentasVisibles)}
                        </select>
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Fecha de pago *</label>
                        <DateInput className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                          value={pagarForm.fecha_pago} onChange={e => setPagarForm(s => ({ ...s, fecha_pago: e.target.value }))} />
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Notas</label>
                        <input className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue"
                          value={pagarForm.notas} onChange={e => setPagarForm(s => ({ ...s, notas: e.target.value }))} placeholder="Opcional" />
                      </div>
                      <div className="flex justify-end gap-3 pt-1">
                        <button onClick={() => setPagarCuotaModal(null)}
                          className="px-4 py-2 text-sm text-slate-400 hover:text-white border border-white/10 rounded-lg transition-colors">Cancelar</button>
                        <button onClick={handlePagarCuota} disabled={pagarSaving}
                          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50">
                          {pagarSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Confirmar pago</>}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </motion.div>
          )}

          {/* ── TAB: CHEQUES ────────────────────────────────────────── */}
          {activeTab === "cheques" && (
            <motion.div
              key="cheques"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              <ChequesPage embedded />
            </motion.div>
          )}

          {/* ── TAB: GARANTÍAS ──────────────────────────────────────── */}
          {activeTab === "garantias" && (
            <motion.div
              key="garantias"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              <GarantiasPage embedded />
            </motion.div>
          )}

          {/* ── TAB: TRASPASO DE SALDOS ─────────────────────────────── */}
          {activeTab === "traspaso_saldos" && (
            <motion.div
              key="traspaso_saldos"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              <TraspasoSaldosPanel token={token} logoFiltro={logoFiltro} />
            </motion.div>
          )}

          {/* ── TAB: PROGRAMACIÓN DE CAJA ────────────────────────────── */}
          {activeTab === "programacion_caja" && (
            <motion.div
              key="programacion_caja"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              <ProgramacionCajaPanel token={token} logoFiltro={logoFiltro} />
            </motion.div>
          )}

          {/* ── TAB: CONFIGURACIÓN ──────────────────────────────────── */}
          {activeTab === "configuracion" && (
            <motion.div
              key="configuracion"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="space-y-4"
            >
              <p className="text-slate-400 text-sm">Configuración del módulo Financiero.</p>

              {/* Card: Acceso en Transferencias */}
              {puedeGestionarTransferencias && (
                <div className="border border-white/10 rounded-xl p-5 bg-white/3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-violet-500/15 flex items-center justify-center flex-shrink-0">
                        <Shield className="w-4 h-4 text-violet-400" />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">Acceso a cuentas en Transferencias</p>
                        <p className="text-slate-400 text-xs mt-0.5 max-w-sm">
                          Definí qué cuentas puede ver cada usuario en el módulo Financiero y si pueden ver el saldo real o solo el semáforo. También configurás los umbrales verde/amarillo/rojo.
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => setShowAccesoModal(true)}
                      className="h-8 text-xs bg-violet-600/80 hover:bg-violet-600 text-white px-3 flex-shrink-0 ml-4"
                    >
                      <Settings className="w-3.5 h-3.5 mr-1" /> Gestionar
                    </Button>
                  </div>
                </div>
              )}

              {/* Card: Acceso en Bancos (sistema y reportes) */}
              {puedeGestionarBancos && (
                <div className="border border-white/10 rounded-xl p-5 bg-white/3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-cyan-500/15 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-4 h-4 text-cyan-400" />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">Acceso a cuentas en Bancos</p>
                        <p className="text-slate-400 text-xs mt-0.5 max-w-sm">
                          Configurá qué cuentas bancarias puede ver cada usuario en el sistema y en reportes. Podés usar la misma lista para ambos o listas separadas.
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => setShowAccesoBancosModal(true)}
                      className="h-8 text-xs bg-cyan-600/80 hover:bg-cyan-600 text-white px-3 flex-shrink-0 ml-4"
                    >
                      <Settings className="w-3.5 h-3.5 mr-1" /> Gestionar
                    </Button>
                  </div>
                </div>
              )}

              {/* Card: Restricción de fechas en movimientos */}
              {puedeGestionarFechas && (
                <div className="border border-white/10 rounded-xl p-5 bg-white/3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-4 h-4 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">Restricción de fechas en movimientos</p>
                        <p className="text-slate-400 text-xs mt-0.5 max-w-sm">
                          Bloqueá el registro de facturas, compras, pagos, cheques y otros movimientos con fecha pasada y/o futura, en general o por usuario.
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => setShowFechasMovimientosModal(true)}
                      className="h-8 text-xs bg-amber-600/80 hover:bg-amber-600 text-white px-3 flex-shrink-0 ml-4"
                    >
                      <Settings className="w-3.5 h-3.5 mr-1" /> Gestionar
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showTransferModal && (
          <TransferenciaModal
            cuentas={cuentasParaTransfer}
            saldos={saldos}
            onClose={() => setShowTransferModal(false)}
            onSave={handleCreateTransfer}
            canVerSaldoFn={canVerSaldo}
            cotHoy={cotHoy}
            canEditarTC={puedeEditarTC}
          />
        )}
        {showEditModal && editingTransfer && (
          <EditarTransferenciaModal
            doc={editingTransfer}
            onClose={() => { setShowEditModal(false); setEditingTransfer(null); }}
            onSave={handleEditTransfer}
          />
        )}
        {showAccesoModal && (
          <AccesoModal
            cuentas={cuentasVisibles.length ? cuentasVisibles : cuentas}
            onClose={() => { setShowAccesoModal(false); loadData(); }}
            token={token}
            logoFiltro={logoFiltro}
          />
        )}
        {showAccesoBancosModal && (
          <AccesoBancosModal
            cuentas={cuentas}
            onClose={() => setShowAccesoBancosModal(false)}
            token={token}
            logoFiltro={logoFiltro}
          />
        )}
        {showFechasMovimientosModal && (
          <FechasMovimientosModal
            onClose={() => setShowFechasMovimientosModal(false)}
            token={token}
            logoFiltro={logoFiltro}
          />
        )}
      </AnimatePresence>

      {/* Cuentas tab — form modal */}
      {showCuentaForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form onSubmit={handleSaveCuenta} onClick={e => e.stopPropagation()} className="bg-arandu-dark border border-white/10 rounded-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h2 className="font-heading text-white text-lg">{editingCuentaId ? "Editar cuenta" : "Nueva cuenta bancaria"}</h2>
              <button type="button" onClick={() => setShowCuentaForm(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-slate-400 text-xs block mb-1">Nombre *</label>
                <input required value={formDataCuenta.nombre} onChange={e => setFormDataCuenta(f => ({ ...f, nombre: e.target.value }))} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue" placeholder="Ej: Cuenta principal PYG" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Banco</label>
                  <input value={formDataCuenta.banco} onChange={e => setFormDataCuenta(f => ({ ...f, banco: e.target.value }))} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue" placeholder="Itaú, Continental, …" />
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">N° cuenta</label>
                  <input value={formDataCuenta.numero_cuenta} onChange={e => setFormDataCuenta(f => ({ ...f, numero_cuenta: e.target.value }))} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue" placeholder="123-456-789" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Moneda *</label>
                  <select value={formDataCuenta.moneda} onChange={e => setFormDataCuenta(f => ({ ...f, moneda: e.target.value }))} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue">
                    <option value="PYG">₲ PYG</option>
                    <option value="USD">$ USD</option>
                    {(activeEmpresaPropia?.monedas_habilitadas?.includes("BRL") || formDataCuenta.moneda === "BRL") && <option value="BRL">R$ BRL</option>}
                  </select>
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Saldo inicial</label>
                  <input type="number" step="any" value={formDataCuenta.saldo_inicial} onChange={e => setFormDataCuenta(f => ({ ...f, saldo_inicial: e.target.value }))} disabled={!!editingCuentaId} className={`w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue ${editingCuentaId ? "opacity-50 cursor-not-allowed" : ""}`} placeholder="0" />
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Desde fecha</label>
                  <DateInput value={formDataCuenta.saldo_inicial_fecha || ""} onChange={e => setFormDataCuenta(f => ({ ...f, saldo_inicial_fecha: e.target.value }))} disabled={!!editingCuentaId} className={`w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue ${editingCuentaId ? "opacity-50 cursor-not-allowed" : ""}`} />
                </div>
              </div>
              {(() => {
                const esPredOrig = editingCuentaId
                  ? !!cuentas.find(c => c.id === editingCuentaId)?.es_predeterminada
                  : false;
                return (
                  <div className="pt-1">
                    <label className={`flex items-center gap-2 ${esPredOrig ? "cursor-not-allowed" : "cursor-pointer"}`}>
                      <input
                        type="checkbox"
                        checked={formDataCuenta.es_predeterminada}
                        onChange={e => { if (!esPredOrig) setFormDataCuenta(f => ({ ...f, es_predeterminada: e.target.checked })); }}
                        disabled={esPredOrig}
                        className="accent-amber-500 disabled:opacity-50"
                      />
                      <span className={`text-sm ${esPredOrig ? "text-slate-400" : "text-slate-300"}`}>★ Predeterminada para esta moneda</span>
                    </label>
                    {esPredOrig && (
                      <p className="text-amber-600/80 text-[11px] mt-0.5 ml-5">
                        No se puede desmarcar — para cambiarla, marcá otra cuenta como predeterminada
                      </p>
                    )}
                  </div>
                );
              })()}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formDataCuenta.permite_saldo_negativo} onChange={e => setFormDataCuenta(f => ({ ...f, permite_saldo_negativo: e.target.checked }))} className="accent-amber-500" />
                <span className="text-slate-300 text-sm">Permite saldo negativo</span>
              </label>
              <div>
                <label className="text-slate-400 text-xs block mb-1">Notas</label>
                <textarea value={formDataCuenta.notas} onChange={e => setFormDataCuenta(f => ({ ...f, notas: e.target.value }))} rows="2" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-arandu-blue" />
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-white/10">
              <button type="button" onClick={() => setShowCuentaForm(false)} className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg text-sm">Cancelar</button>
              <button type="submit" className="flex-1 px-4 py-2 bg-arandu-blue hover:bg-arandu-blue-dark text-white rounded-lg text-sm flex items-center justify-center gap-1">
                <Check className="w-4 h-4" /> {editingCuentaId ? "Guardar" : "Crear cuenta"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ═══ MODAL: Nuevo / Editar pago a proveedor ════════════════════════ */}
      <AnimatePresence>
        {showPagoProvForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[60] overflow-y-auto p-4"
          >
            <div className="flex min-h-full items-center justify-center">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-md p-6 my-6"
            >
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading text-lg font-bold text-white">
                  {editingPagoProv ? "Editar pago a proveedor" : "Nuevo pago a proveedor"}
                </h3>
                <button onClick={() => { setShowPagoProvForm(false); setEditingPagoProv(null); setPagoProvForm(emptyPagoProvForm()); setComprasProvList([]); }} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handlePagoProveedor} className="space-y-4">
                {/* Proveedor */}
                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Proveedor *</label>
                  <EntidadSearchSelect
                    entidades={listaProveedores}
                    value={pagoProvForm.proveedor_id ? {
                      id: pagoProvForm.proveedor_id,
                      nombre: pagoProvForm.proveedor_nombre,
                    } : null}
                    onChange={(prov) => {
                      setPagoProvForm(p => ({
                        ...p,
                        proveedor_id: prov?.id || "",
                        proveedor_nombre: prov?.nombre || "",
                        compras_pagos: [],
                      }));
                      fetchComprasProveedor(prov?.id);
                    }}
                    placeholder="Buscar proveedor por nombre o RUC..."
                  />
                </div>
                {/* Compras pendientes */}
                {pagoProvForm.proveedor_id && (
                  <div>
                    <label className="text-slate-400 text-sm mb-2 flex items-center gap-2">
                      Compras pendientes {loadingComprasProv && <Loader2 className="w-3 h-3 animate-spin" />}
                    </label>
                    {comprasProvList.length === 0 && !loadingComprasProv ? (
                      <div className="bg-arandu-dark border border-white/10 rounded-lg px-4 py-3 text-slate-500 text-sm">Sin compras pendientes para este proveedor</div>
                    ) : (
                      <div className="bg-arandu-dark border border-white/10 rounded-lg overflow-hidden divide-y divide-white/5">
                        {comprasProvList.map(c => {
                          const cpEntry = pagoProvForm.compras_pagos.find(cp => cp.compra_id === c.id);
                          const checked = !!cpEntry;
                          const saldoMax = c.saldo_pendiente || c.monto_total || 0;
                          return (
                            <div key={c.id} className={`px-3 py-2.5 transition-all ${checked ? "bg-violet-500/5" : ""}`}>
                              <label className="flex items-center gap-3 cursor-pointer">
                                <input type="checkbox" checked={checked} onChange={() => {
                                  if (checked) setPagoProvForm(p => ({ ...p, compras_pagos: p.compras_pagos.filter(cp => cp.compra_id !== c.id) }));
                                  else {
                                    setPagoProvForm(p => ({ ...p, compras_pagos: [...p.compras_pagos, { compra_id: c.id, monto_pagado: saldoMax, cuota_ids: [] }] }));
                                    if (c.es_credito_cuotas) fetchCuotasCompra(c.id);
                                  }
                                }} className="w-4 h-4 accent-violet-500 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-white text-xs font-medium">{c.fecha} · {c.numero_factura ? `Fac. ${c.numero_factura}` : "Sin factura"}</p>
                                  <p className="text-slate-500 text-xs">{c.notas || c.items?.[0]?.descripcion || "Sin descripción"}</p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <p className="text-slate-400 text-xs">Saldo: <span className="text-violet-300 font-bold">{fmtNum(saldoMax, c.moneda)}</span></p>
                                  <p className="text-slate-600 text-xs capitalize">{c.estado_pago}</p>
                                </div>
                              </label>
                              {checked && !c.es_credito_cuotas && (
                                <div className="mt-2 ml-7 flex items-center gap-2">
                                  <span className="text-slate-500 text-xs whitespace-nowrap">Pagar:</span>
                                  <input type="number" min="0.01" max={saldoMax} step="any" value={cpEntry.monto_pagado}
                                    onFocus={e => e.target.select()}
                                    onChange={e => setPagoProvForm(p => ({ ...p, compras_pagos: p.compras_pagos.map(cp => cp.compra_id === c.id ? { ...cp, monto_pagado: Number(e.target.value)||0 } : cp) }))}
                                    onBlur={e => { const val = Math.min(Number(e.target.value)||0, saldoMax); setPagoProvForm(p => ({ ...p, compras_pagos: p.compras_pagos.map(cp => cp.compra_id === c.id ? { ...cp, monto_pagado: val } : cp) })); }}
                                    className="flex-1 bg-arandu-dark-light border border-violet-500/30 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-violet-500/60"
                                  />
                                  <span className="text-slate-600 text-xs">{c.moneda}</span>
                                  {cpEntry.monto_pagado < saldoMax
                                    ? <span className="text-amber-400 text-xs whitespace-nowrap">queda {fmtNum(saldoMax-(Number(cpEntry.monto_pagado)||0), c.moneda)}</span>
                                    : <span className="text-emerald-400 text-xs">✓ total</span>}
                                </div>
                              )}
                              {checked && c.es_credito_cuotas && (
                                <div className="mt-2 ml-7">
                                  <p className="text-slate-500 text-xs mb-1.5 flex items-center gap-2">
                                    Compra a crédito en cuotas — elegí cuáles estás cancelando
                                    {loadingCuotasCompra[c.id] && <Loader2 className="w-3 h-3 animate-spin" />}
                                  </p>
                                  {!loadingCuotasCompra[c.id] && (cuotasPorCompra[c.id] || []).length === 0 && (
                                    <div className="bg-arandu-dark-light border border-white/10 rounded-lg px-3 py-2 text-slate-500 text-xs">
                                      Sin cuotas pendientes registradas para esta compra
                                    </div>
                                  )}
                                  {(cuotasPorCompra[c.id] || []).length > 0 && (
                                    <div className="bg-arandu-dark-light border border-violet-500/20 rounded-lg divide-y divide-white/5 overflow-hidden">
                                      {cuotasPorCompra[c.id].map(cu => {
                                        const cuotaChecked = (cpEntry.cuota_ids || []).includes(cu.id);
                                        const montoCu = cpEntry.cuotas_montos?.[cu.id] ?? cu.monto ?? 0;
                                        const recalc = (cp, ids, montos) => {
                                          const total = (cuotasPorCompra[c.id] || [])
                                            .filter(x => ids.includes(x.id))
                                            .reduce((sum, x) => sum + (Number(montos[x.id] ?? x.monto) || 0), 0);
                                          return { ...cp, cuota_ids: ids, cuotas_montos: montos, monto_pagado: total };
                                        };
                                        return (
                                          <div key={cu.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                                            <input type="checkbox" checked={cuotaChecked} onChange={() => {
                                              setPagoProvForm(p => ({
                                                ...p,
                                                compras_pagos: p.compras_pagos.map(cp => {
                                                  if (cp.compra_id !== c.id) return cp;
                                                  const nuevosIds = cuotaChecked
                                                    ? (cp.cuota_ids || []).filter(id => id !== cu.id)
                                                    : [...(cp.cuota_ids || []), cu.id];
                                                  return recalc(cp, nuevosIds, cp.cuotas_montos || {});
                                                }),
                                              }));
                                            }} className="w-3.5 h-3.5 accent-violet-500 flex-shrink-0 cursor-pointer" />
                                            <span className="text-slate-300 flex-1">Cuota {cu.numero} · vence {cu.fecha_vencimiento}
                                              {cuotaChecked && Math.abs((Number(montoCu) || 0) - (cu.monto || 0)) > 0.009 && (
                                                <span className="text-amber-400 ml-1">(orig. {fmtNum(cu.monto, cu.moneda || c.moneda)})</span>
                                              )}
                                            </span>
                                            {cuotaChecked ? (
                                              <input type="number" min="0.01" step="any" value={montoCu}
                                                onFocus={e => e.target.select()}
                                                onChange={e => {
                                                  const v = e.target.value === "" ? "" : Number(e.target.value);
                                                  setPagoProvForm(p => ({
                                                    ...p,
                                                    compras_pagos: p.compras_pagos.map(cp => cp.compra_id !== c.id ? cp
                                                      : recalc(cp, cp.cuota_ids || [], { ...(cp.cuotas_montos || {}), [cu.id]: v })),
                                                  }));
                                                }}
                                                className="w-32 text-right bg-arandu-dark border border-violet-500/40 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-violet-400"
                                              />
                                            ) : (
                                              <span className={`font-medium ${cu.estado === "vencido" ? "text-red-400" : "text-slate-300"}`}>{fmtNum(cu.monto, cu.moneda || c.moneda)}</span>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  <div className="mt-1.5 flex items-center justify-between text-xs">
                                    <span className="text-slate-500">{(cpEntry.cuota_ids || []).length} cuota{(cpEntry.cuota_ids || []).length !== 1 ? "s" : ""} seleccionada{(cpEntry.cuota_ids || []).length !== 1 ? "s" : ""}</span>
                                    <span className={`font-bold ${(Number(cpEntry.monto_pagado) || 0) > saldoMax + 0.01 ? "text-red-400" : "text-violet-300"}`}>{fmtNum(cpEntry.monto_pagado, c.moneda)}</span>
                                  </div>
                                  {(Number(cpEntry.monto_pagado) || 0) > saldoMax + 0.01 && (
                                    <p className="text-red-400 text-xs mt-1">✕ Supera el monto total pendiente de la factura ({fmtNum(saldoMax, c.moneda)})</p>
                                  )}
                                  <p className="text-slate-600 text-[10px] mt-1">Podés editar el monto de cada cuota según el acuerdo con el proveedor. El total no puede superar el saldo de la factura.</p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {pagoProvForm.compras_pagos.length > 0 && (() => {
                      const monedaC = comprasProvList.find(c => c.id === pagoProvForm.compras_pagos[0]?.compra_id)?.moneda || "USD";
                      const totalC = pagoProvForm.compras_pagos.reduce((s, cp) => s + (Number(cp.monto_pagado)||0), 0);
                      const totalSaldo = comprasProvList.filter(c => pagoProvForm.compras_pagos.some(cp => cp.compra_id === c.id)).reduce((s, c) => s + (c.saldo_pendiente||c.monto_total||0), 0);
                      const esParcial = totalC < totalSaldo;
                      return (
                        <div className={`mt-2 rounded-lg px-4 py-2.5 flex justify-between items-center border ${esParcial ? "bg-amber-500/10 border-amber-500/30" : "bg-violet-500/10 border-violet-500/30"}`}>
                          <div>
                            <span className={`text-sm font-medium ${esParcial ? "text-amber-300" : "text-violet-300"}`}>{pagoProvForm.compras_pagos.length} compra{pagoProvForm.compras_pagos.length>1?"s":""} · {esParcial ? "Pago parcial" : "Pago total"}</span>
                            {esParcial && <p className="text-xs text-amber-500 mt-0.5">Quedará saldo pendiente en algunas compras</p>}
                          </div>
                          <span className={`font-heading font-bold text-lg ${esParcial ? "text-amber-200" : "text-violet-200"}`}>{fmtNum(totalC, monedaC)}</span>
                        </div>
                      );
                    })()}
                  </div>
                )}
                {/* Tabs medio de pago */}
                {pagoProvForm.compras_pagos.length > 0 && puedeUsarCheque && !editingPagoProv && (
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setPagoProvMedio("transferencia")}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${pagoProvMedio === "transferencia" ? "bg-violet-600 text-white" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}>
                      Transf. / Efectivo
                    </button>
                    <button type="button" onClick={() => setPagoProvMedio("cheque")}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${pagoProvMedio === "cheque" ? "bg-emerald-600 text-white" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}>
                      Cheque
                    </button>
                  </div>
                )}
                {/* Cuenta bancaria */}
                {pagoProvMedio !== "cheque" && pagoProvForm.compras_pagos.length > 0 && (
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Cuenta bancaria *</label>
                    <select value={pagoProvForm.cuenta_id} onChange={e => { const c = cuentas.find(c => c.id === e.target.value); setPagoProvForm(p => ({ ...p, cuenta_id: e.target.value, cuenta_nombre: c?.nombre||"", cuenta_moneda: c?.moneda||"PYG", tipo_cambio: "" })); }} className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50" required>
                      <option value="">Seleccionar cuenta...</option>
                      {renderCuentasPorMoneda(cuentasVisibles.length ? cuentasVisibles : cuentas)}
                    </select>
                  </div>
                )}
                {/* Tipo de cambio si moneda compra ≠ cuenta */}
                {pagoProvMedio !== "cheque" && pagoProvForm.cuenta_id && pagoProvForm.compras_pagos.length > 0 && (() => {
                  const firstCompra = comprasProvList.find(c => c.id === pagoProvForm.compras_pagos[0]?.compra_id);
                  const monedaC = firstCompra?.moneda || "USD";
                  const totalC = pagoProvForm.compras_pagos.reduce((s, cp) => s + (Number(cp.monto_pagado)||0), 0);
                  const needsTc = monedaC !== pagoProvForm.cuenta_moneda;
                  if (!needsTc) return null;
                  // TC = 1 unidad de moneda extranjera = X guaraníes.
                  // Compra en PYG + cuenta extranjera → dividir; compra extranjera + cuenta PYG → multiplicar.
                  const monedaExtranjera = monedaC === "PYG" ? pagoProvForm.cuenta_moneda : monedaC;
                  const equiv = pagoProvForm.tipo_cambio
                    ? (monedaC === "PYG" ? totalC / Number(pagoProvForm.tipo_cambio) : totalC * Number(pagoProvForm.tipo_cambio))
                    : null;
                  return (
                    <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 space-y-3">
                      <p className="text-amber-300 text-xs">Las compras son en <strong>{monedaC}</strong> pero la cuenta es en <strong>{pagoProvForm.cuenta_moneda}</strong> — ingresá el tipo de cambio.</p>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Tipo de cambio (₲ por {monedaExtranjera}) *</label>
                        <input type="number" min="0" step="any" value={pagoProvForm.tipo_cambio} onChange={e => setPagoProvForm(p => ({ ...p, tipo_cambio: e.target.value }))} onFocus={e => e.target.select()} className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500/50" placeholder="7500" required />
                      </div>
                      {equiv && (
                        <div className="bg-arandu-dark rounded-lg px-3 py-2 flex justify-between text-sm">
                          <span className="text-slate-400">Monto a debitar:</span>
                          <span className="text-white font-bold">{fmtNum(equiv, pagoProvForm.cuenta_moneda)}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
                {/* Fecha, recibo, notas */}
                {pagoProvMedio !== "cheque" && pagoProvForm.compras_pagos.length > 0 && (
                  <>
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Fecha de pago * <span className="text-slate-600 text-xs">(afecta al balance)</span></label>
                      <DateInput value={pagoProvForm.fecha_pago} onChange={e => setPagoProvForm(p => ({ ...p, fecha_pago: e.target.value }))} className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50" required />
                    </div>
                    <RetencionPreview token={token}
                      moneda={_pvPrimeraCompra?.moneda || "PYG"}
                      pagos={pagoProvForm.compras_pagos.map(cp => ({ compra_id: cp.compra_id, monto: cp.monto_pagado }))}
                      fechaPago={pagoProvForm.fecha_pago}
                      excluirPagoProveedorId={editingPagoProv?.id}
                      omitir={!!pagoProvForm.sin_retencion}
                      onOmitir={hasPermission("retenciones.omitir_retencion") ? (v => setPagoProvForm(p => ({ ...p, sin_retencion: v }))) : null} />
                    {pagoProvFormTieneCompraCredito() && (
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">N° recibo del proveedor</label>
                      <input value={pagoProvForm.recibo_numero} onChange={e => setPagoProvForm(p => ({ ...p, recibo_numero: e.target.value }))} className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50 placeholder-slate-600" placeholder="Automático si se deja vacío" />
                      <p className="text-slate-500 text-xs mt-1">Solo se registra recibo para pagos de compras a crédito.</p>
                    </div>
                    )}
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Notas</label>
                      <input value={pagoProvForm.notas} onChange={e => setPagoProvForm(p => ({ ...p, notas: e.target.value }))} className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50 placeholder-slate-600" placeholder="Información adicional (opcional)" />
                    </div>
                  </>
                )}
                {pagoProvMedio !== "cheque" && (
                  <div className="flex gap-3 pt-1">
                    <button type="button" onClick={() => { setShowPagoProvForm(false); setEditingPagoProv(null); setPagoProvForm(emptyPagoProvForm()); setComprasProvList([]); setPagoProvMedio("transferencia"); }} className="flex-1 py-2.5 rounded-lg border border-white/10 text-slate-400 text-sm hover:text-white transition-all">Cancelar</button>
                    <button type="submit" disabled={pagoProvForm.compras_pagos.length === 0} className="flex-1 py-2.5 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-all flex items-center justify-center gap-2">
                      <Check className="w-4 h-4" /> {editingPagoProv ? "Actualizar pago" : "Registrar pago"}
                    </button>
                  </div>
                )}
              </form>
              {pagoProvMedio === "cheque" && pagoProvForm.compras_pagos.length > 0 && (
                <ChequeEgresoForm
                  token={token}
                  logoTipo={activeEmpresaPropia?.slug || logoFiltro}
                  beneficiario={pagoProvForm.proveedor_nombre}
                  monto={pagoProvForm.compras_pagos.reduce((s, cp) => s + (Number(cp.monto_pagado) || 0), 0)}
                  egresoMoneda={comprasProvList.find(c => c.id === pagoProvForm.compras_pagos[0]?.compra_id)?.moneda || "PYG"}
                  onSuccess={handlePagoProvConCheque}
                  onCancel={() => { setShowPagoProvForm(false); setPagoProvForm(emptyPagoProvForm()); setComprasProvList([]); setPagoProvMedio("transferencia"); }}
                />
              )}
            </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ MODAL: Ver detalle de un pago a proveedor ════════════════════════ */}
      <AnimatePresence>
        {selectedPagoView && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-5">
                <span className="text-xs bg-violet-500/20 text-violet-300 border border-violet-500/30 rounded-full px-3 py-1 font-medium">Pago a proveedor</span>
                <button onClick={() => setSelectedPagoView(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-violet-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-violet-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-slate-400 text-xs">Proveedor</p>
                  <p className="text-white font-semibold truncate">{selectedPagoView.proveedor_nombre}</p>
                </div>
                {selectedPagoView.estado && (
                  <span className={`text-xs px-2.5 py-1 rounded-full border ${{pagado:"bg-emerald-500/15 text-emerald-400 border-emerald-500/30",pendiente:"bg-blue-500/15 text-blue-400 border-blue-500/30",vencido:"bg-red-500/15 text-red-400 border-red-500/30"}[selectedPagoView.estado]||""}`}>
                    {selectedPagoView.estado}
                  </span>
                )}
              </div>
              <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 mb-4">
                <p className="text-slate-500 text-xs mb-0.5">Concepto</p>
                <p className="text-white text-sm font-medium">{selectedPagoView.concepto || "Sin concepto"}</p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-5 text-center mb-4">
                <p className="text-slate-400 text-xs mb-1">Monto</p>
                <p className="font-heading text-3xl font-bold text-emerald-400">{fmtNum(selectedPagoView.monto, selectedPagoView.moneda)}</p>
                {selectedPagoView.moneda !== "PYG" && selectedPagoView.tipo_cambio && (
                  <p className="text-slate-500 text-xs mt-1">TC: ₲ {Number(selectedPagoView.tipo_cambio).toLocaleString("es-PY")} / {selectedPagoView.moneda}</p>
                )}
                {selectedPagoView.moneda !== "PYG" && selectedPagoView.monto_gs && (
                  <p className="text-slate-400 text-sm mt-1">{fmtNum(selectedPagoView.monto_gs, "PYG")} equivalente</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-arandu-dark/60 rounded-xl p-3">
                  <p className="text-slate-500 text-xs mb-0.5">Fecha vencimiento</p>
                  <p className="text-white text-sm font-medium">{selectedPagoView.fecha_vencimiento || "—"}</p>
                </div>
                <div className="bg-arandu-dark/60 rounded-xl p-3">
                  <p className="text-slate-500 text-xs mb-0.5">Fecha de pago</p>
                  <p className={`text-sm font-medium ${selectedPagoView.fecha_pago ? "text-emerald-400" : "text-slate-500"}`}>{selectedPagoView.fecha_pago || "Pendiente"}</p>
                </div>
                <div className="bg-arandu-dark/60 rounded-xl p-3">
                  <p className="text-slate-500 text-xs mb-0.5">Moneda</p>
                  <p className="text-white text-sm font-medium">{selectedPagoView.moneda === "USD" ? "Dólar (USD)" : selectedPagoView.moneda === "BRL" ? "Real (R$)" : "Guaraní (₲)"}</p>
                </div>
                <div className="bg-arandu-dark/60 rounded-xl p-3">
                  <p className="text-slate-500 text-xs mb-0.5">Cuenta tipo</p>
                  <p className="text-white text-sm font-medium capitalize">{selectedPagoView.cuenta_pago || "guaranies"}</p>
                </div>
                {selectedPagoView.cuenta_nombre && (
                  <div className="bg-arandu-dark/60 rounded-xl p-3 col-span-2">
                    <p className="text-slate-500 text-xs mb-0.5 flex items-center gap-1"><Banknote className="w-3 h-3" /> Pagado desde</p>
                    <p className="text-white text-sm font-medium">{selectedPagoView.cuenta_nombre}</p>
                  </div>
                )}
                {selectedPagoView.cheque_numero && (
                  <div className="bg-violet-500/5 border border-violet-500/20 rounded-xl p-3 col-span-2">
                    <p className="text-slate-500 text-xs mb-0.5">Cheque utilizado</p>
                    <p className="text-violet-300 text-sm font-medium">N° {selectedPagoView.cheque_numero}{selectedPagoView.cheque_estado ? ` · ${selectedPagoView.cheque_estado}` : ""}</p>
                    <p className="text-slate-500 text-[11px] mt-1">Para eliminar este pago primero liberá el cheque en Financiero → Cheques.</p>
                  </div>
                )}
                {selectedPagoView.recibo_numero && (
                  <div className="bg-arandu-dark/60 rounded-xl p-3 col-span-2">
                    <p className="text-slate-500 text-xs mb-0.5 flex items-center gap-1"><Receipt className="w-3 h-3" /> Recibo proveedor</p>
                    <p className="text-white text-sm font-medium">{selectedPagoView.recibo_numero}</p>
                  </div>
                )}
                {!selectedPagoView.recibo_numero && !selectedPagoView.eliminada && canEditarPago && pagoProveedorTieneCompraCredito(selectedPagoView) && (
                  <div className="bg-arandu-dark/60 rounded-xl p-3 col-span-2">
                    <p className="text-slate-500 text-xs mb-2 flex items-center gap-1"><Receipt className="w-3 h-3" /> Recibo proveedor</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        value={reciboPagoProvForm}
                        onChange={e => setReciboPagoProvForm(e.target.value)}
                        placeholder="Número de recibo"
                        className="flex-1 bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-500/60"
                      />
                      <button
                        type="button"
                        disabled={savingReciboPagoProv}
                        onClick={handleAsignarReciboPagoProv}
                        className="px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-sm font-medium flex items-center justify-center gap-2"
                      >
                        {savingReciboPagoProv ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Guardar recibo
                      </button>
                    </div>
                  </div>
                )}
                {!selectedPagoView.recibo_numero && !selectedPagoView.eliminada && !pagoProveedorTieneCompraCredito(selectedPagoView) && (
                  <div className="bg-arandu-dark/60 rounded-xl p-3 col-span-2">
                    <p className="text-slate-500 text-xs mb-0.5 flex items-center gap-1"><Receipt className="w-3 h-3" /> Recibo proveedor</p>
                    <p className="text-slate-400 text-sm">No aplica para pagos de compras contado.</p>
                  </div>
                )}
              </div>
              {(selectedPagoView.compras_detalle || []).length > 0 && (
                <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 mb-4">
                  <p className="text-slate-500 text-xs mb-2 flex items-center gap-1">
                    <Receipt className="w-3 h-3" /> Compras vinculadas ({selectedPagoView.compras_detalle.length})
                  </p>
                  <div className="space-y-2">
                    {selectedPagoView.compras_detalle.map(c => {
                      const content = (
                        <>
                          <div className="min-w-0">
                            <p className="text-white text-sm font-medium truncate">{compraPagoLabel(c)}</p>
                            <p className="text-slate-500 text-xs truncate">
                              {c.concepto || "Sin descripción"}
                              {c.eliminada ? " · Eliminada" : ""}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-emerald-400 text-sm font-semibold">{fmtNum(c.monto_pagado || c.monto_total || 0, c.moneda || selectedPagoView.moneda)}</p>
                            {Number(c.monto_anticipo_total || 0) > 0 && (
                              <p className="text-cyan-300 text-[10px]">
                                Anticipo {fmtNum(c.monto_anticipo_total, c.moneda || selectedPagoView.moneda)}
                              </p>
                            )}
                            <p className="text-slate-500 text-[10px] capitalize">{c.estado_pago || "—"}</p>
                          </div>
                        </>
                      );
                      return c.existe && !c.eliminada ? (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => openCompraDesdePago(c)}
                          className="w-full text-left flex items-center justify-between gap-3 bg-white/[0.03] hover:bg-blue-500/10 border border-white/5 hover:border-blue-500/30 rounded-lg px-3 py-2 transition-colors"
                        >
                          {content}
                        </button>
                      ) : (
                        <div key={c.id} className="flex items-center justify-between gap-3 bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2">
                          {content}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {selectedPagoView.notas && (
                <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 mb-4">
                  <p className="text-slate-500 text-xs mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> Notas</p>
                  <p className="text-slate-300 text-sm">{selectedPagoView.notas}</p>
                </div>
              )}
              {!selectedPagoView.eliminada && (selectedPagoView.estado === "pendiente" || selectedPagoView.estado === "vencido") && (
                <button onClick={() => { handleMarcarPagado(selectedPagoView.id); setSelectedPagoView(null); }} className="w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-all flex items-center justify-center gap-2 mb-2">
                  <Check className="w-4 h-4" /> Marcar como pagado
                </button>
              )}
              {!selectedPagoView.eliminada && selectedPagoView.estado === "pagado" && (
                <button onClick={() => setShowPagoProvDoc(selectedPagoView)} className="w-full py-2.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium transition-all flex items-center justify-center gap-2 mb-2">
                  <Printer className="w-4 h-4" /> Imprimir comprobante
                </button>
              )}
              {!selectedPagoView.eliminada && canEliminarPago && (
                <button onClick={() => handleDeletePagoProv(selectedPagoView.id)} className="w-full py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20 transition-all flex items-center justify-center gap-2 mb-2">
                  <Trash2 className="w-4 h-4" /> Eliminar
                </button>
              )}
              <button onClick={() => setSelectedPagoView(null)} className="w-full py-2.5 rounded-lg bg-arandu-dark border border-white/10 text-slate-400 text-sm hover:text-white transition-all">Cerrar</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ MODAL: Compra vinculada desde pago proveedor ════════════════════ */}
      <AnimatePresence>
        {selectedCompraDesdePago && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/85 z-[90] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Receipt className="w-5 h-5 text-orange-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-semibold truncate">{selectedCompraDesdePago.proveedor_nombre || "Compra vinculada"}</p>
                    <p className="text-slate-500 text-xs">
                      {selectedCompraDesdePago.numero_factura ? `Fac. ${selectedCompraDesdePago.numero_factura}` : "Sin factura"} · {selectedCompraDesdePago.fecha || "—"}
                    </p>
                  </div>
                  {loadingCompraDesdePago && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
                </div>
                <button onClick={() => setSelectedCompraDesdePago(null)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4 text-center">
                  <p className="text-slate-400 text-xs mb-1">Monto compra</p>
                  <p className="font-heading text-2xl font-bold text-orange-400">{fmtNum(selectedCompraDesdePago.monto_total, selectedCompraDesdePago.moneda)}</p>
                </div>
                <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-4">
                  <p className="text-slate-500 text-xs mb-1">Estado</p>
                  <p className="text-white text-sm font-medium capitalize">{selectedCompraDesdePago.estado_pago || "—"}</p>
                  <p className="text-slate-500 text-xs mt-1 capitalize">Tipo: {selectedCompraDesdePago.tipo_pago || "—"}</p>
                </div>
                {selectedCompraDesdePago.nro_timbrado && (
                  <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 sm:col-span-2">
                    <p className="text-slate-500 text-xs">Timbrado</p>
                    <p className="text-white text-sm">{selectedCompraDesdePago.nro_timbrado}{selectedCompraDesdePago.fecha_vigencia_timbrado ? ` · Vigencia ${selectedCompraDesdePago.fecha_vigencia_timbrado}` : ""}</p>
                  </div>
                )}
              </div>

              {(selectedCompraDesdePago.items || []).length > 0 && (
                <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 mb-4">
                  <p className="text-slate-500 text-xs mb-2 flex items-center gap-1"><FileText className="w-3 h-3" /> Ítems</p>
                  <div className="space-y-1.5">
                    {(selectedCompraDesdePago.items || []).map((it, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-3 text-sm border-b border-white/5 last:border-0 pb-1.5 last:pb-0">
                        <span className="text-slate-300 truncate">{it.descripcion || "—"} <span className="text-slate-600">x{it.cantidad || 1}</span></span>
                        <span className="text-white font-medium whitespace-nowrap">{fmtNum(it.subtotal || ((it.cantidad || 1) * (it.precio_unitario || 0)), selectedCompraDesdePago.moneda)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(selectedCompraDesdePago.pagos || []).length > 0 && (
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-3 mb-4">
                  <p className="text-emerald-300 text-xs mb-2 flex items-center gap-1"><Banknote className="w-3 h-3" /> Pagos registrados</p>
                  <div className="space-y-1.5">
                    {(selectedCompraDesdePago.pagos || []).map((p, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-slate-400 truncate">{p.fecha_pago || p.fecha || "—"} · {p.cuenta_nombre || "Sin cuenta"}{p.recibo_numero ? ` · Recibo ${p.recibo_numero}` : ""}</span>
                        <span className="text-emerald-300 font-medium whitespace-nowrap">{fmtNum(p.monto_pagado, selectedCompraDesdePago.moneda)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedCompraDesdePago.notas && (
                <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 mb-4">
                  <p className="text-slate-500 text-xs mb-1">Notas</p>
                  <p className="text-slate-300 text-sm">{selectedCompraDesdePago.notas}</p>
                </div>
              )}

              <button onClick={() => setSelectedCompraDesdePago(null)} className="w-full py-2.5 rounded-lg bg-arandu-dark border border-white/10 text-slate-400 text-sm hover:text-white transition-all">
                Cerrar y volver al pago
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Print modals — fuera de AnimatePresence para no tener conflictos de z-index */}
      {printTransfer && (
        <PrintTransferenciaModal
          transfer={printTransfer}
          empresa={activeEmpresaPropia}
          onClose={() => setPrintTransfer(null)}
        />
      )}
      {showPrintReporte && (
        <PrintReporteModal
          transfers={transfersFiltradas}
          empresa={activeEmpresaPropia}
          filtros={{
            desde: filterDesde,
            hasta: filterHasta,
            cuenta: filterCuenta ? cuentasVisibles.find(c => c.id === filterCuenta)?.nombre : null,
          }}
          onClose={() => setShowPrintReporte(false)}
        />
      )}
      {showPagoProvDoc && (
        <PagoProveedorDocModal
          pago={showPagoProvDoc}
          activeEmpresaPropia={activeEmpresaPropia}
          fmtNum={fmtNum}
          onClose={() => setShowPagoProvDoc(null)}
        />
      )}
    </div>
  );
}

// ═══ Comprobante de Pago a Proveedor (imprimible) ══════════════════════════
function PagoProveedorDocModal({ pago: p, activeEmpresaPropia = null, onClose, fmtNum }) {
  React.useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const empresaEmisora = activeEmpresaPropia || {};
  const accentColor = p.logo_tipo === "jar" ? "#dc2626" : p.logo_tipo === "arandu" ? "#2563eb" : (empresaEmisora?.color || "#1d4ed8");
  const emisorNombre = empresaEmisora?.razon_social || empresaEmisora?.nombre || (p.logo_tipo === "jar" ? "JAR Informática" : p.logo_tipo === "arandu" ? "Arandu Informática" : "AranduJAR Informática");
  const emisorRuc = empresaEmisora?.ruc || "";
  const emisorDireccion = empresaEmisora?.direccion || "";
  const emisorTelefono = empresaEmisora?.telefono || "";
  const emisorEmail = empresaEmisora?.email || "";
  const emisorLogoUrl = empresaEmisora?.logo_url || null;
  const fmt = (n, moneda) => fmtNum(n, moneda || p.moneda);

  // Si el pago se hizo en moneda extranjera, mostramos el tipo de cambio
  // usado y el total equivalente en guaraníes (preferimos el monto_gs ya
  // calculado y guardado al registrar el pago; si no vino, lo calculamos
  // con el tipo_cambio como respaldo).
  const esMonedaExtranjera = (p.moneda || "PYG") !== "PYG";
  const montoGsEquivalente = p.monto_gs != null
    ? p.monto_gs
    : (p.tipo_cambio ? Math.round(Number(p.monto || 0) * Number(p.tipo_cambio)) : null);
  const mostrarTcYGs = esMonedaExtranjera && (p.tipo_cambio || montoGsEquivalente);

  const detalles = p.compras_detalle && p.compras_detalle.length > 0
    ? p.compras_detalle
    : [{ numero_factura: p.concepto, monto_pagado: p.monto, moneda: p.moneda }];
  const proveedorRuc = detalles.find(d => d.proveedor_ruc)?.proveedor_ruc || "";

  const numeroComprobante = p.recibo_numero || p.id?.slice(0, 8) || "-";

  const handlePrint = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const docLogo = svgDocumentHeaderLogoHtml(p.logo_tipo, emisorLogoUrl);
    const detalleRows = detalles.map(d => `
      <tr>
        <td style="padding:5px 8px;border-bottom:1px solid #e5e7eb;font-family:monospace;font-weight:700">${d.numero_factura || "-"}</td>
        <td style="padding:5px 8px;border-bottom:1px solid #e5e7eb;text-align:right">${fmt(d.monto_pagado, d.moneda)}</td>
      </tr>
    `).join("");
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Comprobante de pago ${numeroComprobante}</title>
      <style>
        @page{margin:14mm}
        body{font-family:Arial,sans-serif;color:#111;padding:0;max-width:720px;margin:0 auto}
        .receipt{border:2px solid ${accentColor};border-radius:8px;padding:20px}
        table{page-break-inside:auto}
        tr{page-break-inside:avoid;page-break-after:auto}
      </style></head><body><div class="receipt">
      <div style="display:flex;justify-content:center;margin-bottom:16px">${docLogo}</div>
      <div style="text-align:center;margin-bottom:20px;border-bottom:2px solid ${accentColor};padding-bottom:16px">
        <h1 style="font-size:26px;font-weight:900;color:${accentColor};margin:0">COMPROBANTE DE PAGO A PROVEEDOR</h1>
        <p style="font-size:20px;font-weight:700;color:#1f2937;margin:4px 0">N° ${numeroComprobante}</p>
        ${emisorRuc ? `<p style="font-size:12px;font-weight:700;color:#374151;margin:2px 0">RUC: ${emisorRuc}</p>` : ""}
      </div>
      <table style="width:100%;border-collapse:collapse;margin-bottom:16px;font-size:14px">
        <tr><td style="padding:8px 4px;color:#6b7280;width:40%">Empresa:</td><td style="padding:8px 4px;font-weight:600">${emisorNombre}${emisorRuc ? ` · RUC: ${emisorRuc}` : ""}</td></tr>
        <tr style="background:#f9fafb"><td style="padding:8px 4px;color:#6b7280">Pagado a:</td><td style="padding:8px 4px;font-weight:600">${p.proveedor_nombre || "-"}</td></tr>
        ${proveedorRuc ? `<tr><td style="padding:8px 4px;color:#6b7280">RUC proveedor:</td><td style="padding:8px 4px">${proveedorRuc}</td></tr>` : ""}
        <tr><td style="padding:8px 4px;color:#6b7280">Fecha de pago:</td><td style="padding:8px 4px;font-weight:600">${p.fecha_pago || "-"}</td></tr>
        ${p.cuenta_nombre ? `<tr style="background:#f9fafb"><td style="padding:8px 4px;color:#6b7280">Cuenta bancaria:</td><td style="padding:8px 4px;font-weight:600">🏦 ${p.cuenta_nombre}</td></tr>` : ""}
      </table>
      <div style="margin:14px 0;page-break-inside:avoid">
        <div style="font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:.08em;margin-bottom:6px">Facturas pagadas (${detalles.length})</div>
        <table style="width:100%;border-collapse:collapse;font-size:13px;background:#f9fafb;border-radius:8px;overflow:hidden">
          <thead><tr><th style="padding:7px 8px;text-align:left;color:#6b7280;border-bottom:1px solid #d1d5db">Factura / Concepto</th><th style="padding:7px 8px;text-align:right;color:#6b7280;border-bottom:1px solid #d1d5db">Monto</th></tr></thead>
          <tbody>${detalleRows}</tbody>
        </table>
      </div>
      <div style="background:${accentColor}10;border:2px solid ${accentColor};border-radius:8px;padding:16px;text-align:center;margin:20px 0">
        <p style="color:#6b7280;font-size:12px;margin:0 0 4px">MONTO PAGADO</p>
        <p style="font-size:32px;font-weight:900;color:${accentColor};margin:0">${fmt(p.monto)}</p>
        ${Number(p.monto_retencion) > 0 ? `<p style="font-size:12px;color:#374151;margin:4px 0 0">Importe aplicado a la deuda: ${fmt(p.monto_bruto)} · Retención de IVA: ${fmt(p.monto_retencion)} · Neto pagado: ${fmt(p.monto)}</p>` : ""}
        ${mostrarTcYGs ? `
          ${p.tipo_cambio ? `<p style="font-size:11px;color:#6b7280;margin:8px 0 0">Tipo de cambio: ₲ ${Number(p.tipo_cambio).toLocaleString("es-PY")} / ${p.moneda}</p>` : ""}
          ${montoGsEquivalente != null ? `<p style="font-size:15px;font-weight:700;color:#374151;margin:2px 0 0">Total en Gs.: ${fmtNum(montoGsEquivalente, "PYG")}</p>` : ""}
        ` : ""}
      </div>
      ${p.notas ? `<p style="font-size:12px;color:#6b7280;border-top:1px solid #e5e7eb;padding-top:12px">Notas: ${p.notas}</p>` : ""}
      <p style="font-size:11px;color:#9ca3af;text-align:center;margin-top:20px">${emisorDireccion}${emisorTelefono ? ` · Tel: ${emisorTelefono}` : ""}${emisorEmail ? ` · ${emisorEmail}` : ""}</p>
    </div></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] shadow-2xl relative flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
          <span className="text-gray-600 font-medium text-sm">Comprobante de pago a proveedor</span>
          <div className="flex gap-2">
            <button onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-all">
              <Printer className="w-3.5 h-3.5" /> Imprimir
            </button>
            <button onClick={onClose}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs rounded-lg transition-all">
              Cerrar
            </button>
          </div>
        </div>

        {/* Header del comprobante */}
        <div className="p-6 text-center border-b-4 shrink-0" style={{ borderColor: accentColor }}>
          <div className="mb-3 flex justify-center">
            {(() => {
              const logoUrl = emisorLogoUrl || empresaEmisora?.logo_url;
              if (logoUrl) return <img src={logoUrl} alt="Logo" className="max-h-16 max-w-[160px] object-contain" />;
              if (p.logo_tipo === "arandu") return <LogoMarcaArandu />;
              if (p.logo_tipo === "jar") return <LogoMarcaJar />;
              return <LogoMarcaAranduJar />;
            })()}
          </div>
          <h2 className="text-xl font-black text-gray-800 tracking-wider">COMPROBANTE DE PAGO A PROVEEDOR</h2>
          <p className="text-4xl font-black mt-1" style={{ color: accentColor }}>N° {numeroComprobante}</p>
          {emisorRuc && <p className="text-gray-600 text-sm font-semibold mt-1">RUC: {emisorRuc}</p>}
        </div>

        {/* Datos del comprobante */}
        <div className="p-6 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Pagado a</p>
              <p className="text-gray-800 font-semibold">{p.proveedor_nombre || "-"}</p>
              {proveedorRuc && <p className="text-gray-500 text-xs mt-0.5">RUC: {proveedorRuc}</p>}
            </div>
            <div className={`bg-gray-50 rounded-lg p-3 ${detalles.length > 1 ? "col-span-2" : ""}`}>
              <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">
                {detalles.length > 1 ? `Facturas pagadas (${detalles.length})` : "Factura / Concepto"}
              </p>
              {detalles.length > 1 ? (
                <ul className="space-y-1.5 mt-1 max-h-64 overflow-y-auto pr-1">
                  {detalles.map((d, i) => (
                    <li key={i} className="flex justify-between gap-2 text-sm">
                      <span className="text-gray-800 font-mono font-semibold">{d.numero_factura || "-"}</span>
                      <span className="text-gray-600 shrink-0">{fmt(d.monto_pagado, d.moneda)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-800 font-mono font-semibold">{detalles[0]?.numero_factura || "-"}</p>
              )}
              <p className="text-gray-400 text-xs mt-1.5">{p.fecha_pago || "-"}</p>
            </div>
          </div>

          {/* Monto destacado */}
          <div className="rounded-xl p-5 text-center" style={{ background: `${accentColor}15`, border: `2px solid ${accentColor}40` }}>
            <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Monto pagado</p>
            <p className="text-4xl font-black" style={{ color: accentColor }}>{fmt(p.monto)}</p>
            {mostrarTcYGs && (
              <div className="mt-2 pt-2 border-t border-dashed" style={{ borderColor: `${accentColor}40` }}>
                {p.tipo_cambio && (
                  <p className="text-gray-500 text-xs">Tipo de cambio: ₲ {Number(p.tipo_cambio).toLocaleString("es-PY")} / {p.moneda}</p>
                )}
                {montoGsEquivalente != null && (
                  <p className="text-gray-700 text-base font-bold mt-0.5">Total en Gs.: {fmtNum(montoGsEquivalente, "PYG")}</p>
                )}
              </div>
            )}
          </div>

          {p.cuenta_nombre && (
            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm">
              <span className="text-blue-600 text-xs uppercase tracking-wide font-semibold">Cuenta bancaria</span>
              <span className="text-blue-800 font-medium flex items-center gap-1">🏦 {p.cuenta_nombre}</span>
            </div>
          )}
          {p.notas && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-r text-sm text-gray-600">
              <strong className="text-yellow-800">Notas:</strong> {p.notas}
            </div>
          )}
        </div>
        <div className="px-6 pb-4 text-center">
          <p className="text-gray-400 text-xs">{emisorDireccion}{emisorTelefono ? ` · Tel: ${emisorTelefono}` : ""}{emisorEmail ? ` · ${emisorEmail}` : ""}</p>
        </div>
      </div>
    </div>
  );
}
